<template>
  <div class="checkpoint-edit-form-v2-root">
    <CheckpointPastVersionCard
      v-if="viewingPastVersion"
      :checkpoint="viewingPastVersion.checkpoint"
      :version-number="viewingPastVersion.versionNumber"
      :responsibility-id="responsibilityId"
      :responsibility-name="responsibilityName"
      :responsibility-code="responsibilityCode"
      :responsibility-purpose="responsibilityPurpose"
      data-testid="checkpoint-edit-form-v2-past-version"
      @breadcrumb-click="handlePastVersionBreadcrumbClick"
    />
    <section v-else class="checkpoint-edit-form-v2">
      <CheckpointHeader
        v-if="!pastVersionView"
        v2
        :readiness_level="readinessLevel"
        :status="checkpointStatus"
        :checkpointData="checkpoint"
        :checkpointId="checkpointId"
        :criteria="checkpoint?.criteria || ''"
        :checkpointPrefix="prefixDisplay"
        :assignees="presenceAvatars"
        :levelEditable="!criteriaReadonly"
        @breadcrumb-click="$emit('back-to-list')"
        @open-chatter="openChatter"
        @open-actions="onHeaderMenuAction"
        @update:level="handleHeaderLevelUpdate"
      />

      <MicrolearningEditBar
        v-if="!pastVersionView"
        class="checkpoint-edit-form-v2__edit-bar"
        :checkpointState="editBarCheckpointState"
        :action="editBarAction"
        :selectedTab="editBarSelectedTab"
        :saveDisabled="isArticle && !articleCanSave"
        :versionNumber="currentVersionNumber"
        :publishMessage="currentPublishMessage"
        :publishMessageStatus="currentPublishMessageStatus"
        :historyTooltipEnabled="historyTooltipEnabled"
        :showInspectionsTab="showInspectionsTab"
        :inspectionsCount="inspectionsCount"
        :hasPendingUploadTask="hasPendingUploadTask"
        @tab-click="handleEditBarTabClick"
        @history-click="handleVersionHistoryClick"
        @discard="handleEditBarDiscard"
        @secondary-action="$emit('lock-to-edit')"
        @exit-edit="handleExitEdit"
        @primary-action="handleEditBarPrimaryAction"
        @save="handleEditBarArticleSave"
      />

      <div
        v-if="showVersionHistoryPanel"
        class="checkpoint-edit-form-v2__version-history"
        data-testid="checkpoint-edit-form-v2-version-history"
      >
        <CheckpointVersionHistoryPanel
          :versions="versionHistoryRows"
          :loading="versionHistoryLoading"
          :error="versionHistoryError"
          :selectedVersionId="selectedVersionId"
          @close="closeVersionHistoryPanel"
          @select="handleVersionHistorySelect"
        />
      </div>

      <LockCheckpointBanner
        v-if="!pastVersionView && showLockBanner"
        class="checkpoint-edit-form-v2__lock-banner"
        :locked="lockBannerVariant"
        :editorName="lockEditorName"
        :videoProcessing="showVideoProcessingLockBanner"
        @cancel-and-discard="handleVideoProcessingCancelAndDiscard"
        @takeover="$emit('takeover-edit-lock')"
      />

      <!-- Criteria: shared across all microlearning types. Editable only when locked by self. -->
      <CriteriaViewEditInPlace
        :key="`criteria-${checkpointId}-${criteriaFormEpoch}`"
        class="checkpoint-edit-form-v2__criteria"
        :criteria="checkpoint?.criteria || ''"
        :criteriaPrefix="prefixDisplay"
        :controlledByCheckpoint="true"
        :readonly="criteriaReadonly"
        @update:criteria="$emit('update-criteria', $event)"
        @update:criteriaPrefix="$emit('update-prefix', $event)"
      />

      <!-- Learning-type banner — single consistent position above the content tab strip on
           every microlearning type (hidden for task_an_employee / unknown, which have no
           design variant). -->
      <CheckpointTypeBanner
        v-if="checkpointTypeBannerType && !showInspectionsPanel"
        class="checkpoint-edit-form-v2__type-banner"
        :type="checkpointTypeBannerType"
      />

      <!-- MDP-592: Inspections tab body — GET inspections-by-version (ER-11594). -->
      <template v-if="showInspectionsPanel">
        <InspectionsTableByVersion
          class="checkpoint-edit-form-v2__inspections"
          data-testid="checkpoint-edit-form-v2-inspections"
          :version-groups="displayInspectionVersionGroups"
          :reset-group="displayInspectionResetGroup"
          :filters="inspectionChipFilters"
          :employee-search="inspectionEmployeeSearch"
          :loading="inspectionsLoading"
          :account-id="getAccountId()"
          :chatter-type-value="inspectionChatterTitle"
          :can-bulk-reset="canBulkResetInspections"
          :reset-all-busy="inspectionBulkResetBusy"
          @filterChange="handleInspectionsChipChange"
          @searchChange="handleInspectionsSearchChange"
          @resetAll="handleInspectionsResetAll"
          @reset="handleInspectionsRowReset"
          @chatterOpen="handleInspectionsChatterOpen"
          @openFilters="inspectionFilterModalOpen = true"
        />
        <InspectionsTableByVersionFilterModal
          :value="inspectionFilterModalOpen"
          :initial-filters="inspectionFilterPayload"
          @input="inspectionFilterModalOpen = $event"
          @apply="handleInspectionsFilterApply"
        />
      </template>

      <!-- Bottom content varies by proposed_microlearning_type; shared chrome stays above. -->
      <template v-else-if="isInspectorOnly">
        <NoLearningNeededBanner
          class="checkpoint-edit-form-v2__banner"
          :mode="noLearningNeededBannerMode"
          @add-microlearning="handleAddMicrolearningClick"
        />

        <!-- Short instructions to employee (Optional) — always present on Inspector Only (Shape 4). -->
        <CheckpointPrepNoteField
          class="checkpoint-edit-form-v2__prep-note"
          :note="employeePrepNote"
          :prose="isEmployeePrepNoteProseSurface"
          :readonly="employeePrepNoteReadonly"
          @update:note="handleEmployeePrepNoteUpdate"
        />
      </template>

      <template v-else-if="isTaskAnEmployee">
        <PendingTaskToUploadVideoBanner
          class="checkpoint-edit-form-v2__banner"
          :read-only="pastVersionView"
          :assignedUserFirstName="pendingAssignedToFirstName"
          :assignedUserLastName="pendingAssignedToLastName"
          :assignedUserAvatar="pendingAssignedToAvatar"
          :assignedByFirstName="pendingAssignedByFirstName"
          :assignedByLastName="pendingAssignedByLastName"
          :assignedByAvatar="pendingAssignedByAvatar"
          :assignedDate="pendingAssignedDate"
          :dueDate="pendingDueDate"
          :lockState="lockState"
          :sendReminderLoading="sendReminderLoading"
          :canSendUploadTaskReminder="canSendUploadTaskReminder"
          @cancel="handlePendingVideoTaskCancelClick"
          @reassign="handlePendingVideoTaskReassignClick"
          @send-reminder="handlePendingVideoTaskSendReminder"
        />

        <!-- Short instructions to employee — carried into content shapes, kept only while a note exists. -->
        <CheckpointPrepNoteField
          v-if="hasEmployeePrepNote"
          class="checkpoint-edit-form-v2__prep-note"
          :note="employeePrepNote"
          :prose="isEmployeePrepNoteProseSurface"
          :readonly="employeePrepNoteReadonly"
          @update:note="handleEmployeePrepNoteUpdate"
        />
      </template>

      <!-- Video: same branching as CheckpointInspectionInstructions (kit tabs vs generate bar). -->
      <div v-else-if="isVideo" class="checkpoint-edit-form-v2__video">
        <!-- Short instructions to employee — above the content tab strip, kept only while a note exists. -->
        <CheckpointPrepNoteField
          v-if="hasEmployeePrepNote"
          class="checkpoint-edit-form-v2__prep-note"
          :note="employeePrepNote"
          :prose="isEmployeePrepNoteProseSurface"
          :readonly="employeePrepNoteReadonly"
          @update:note="handleEmployeePrepNoteUpdate"
        />

        <!-- ER-12337 — a run that finished while the editor was unmounted: collapsed
             "Thought for {N}s" banner above the generated microlearning (Rule 2 done state). -->
        <IQThinkingBanner
          v-if="iqVideoDone"
          class="checkpoint-edit-form-v2__iq-thinking-banner"
          variant="microlearning"
          state="done"
          :done-elapsed-ms="iqVideoDoneElapsedMs"
        />

        <!-- Encoding in progress — original processing UI (do not restyle). -->
        <div
          v-if="showOriginalVideoProcessingUi"
          class="checkpoint-edit-form-v2__video-processing"
          role="status"
          aria-live="polite"
        >
          <div class="checkpoint-edit-form-v2__video-processing-loader-wrap" aria-hidden="true">
            <StreamProcessingLoaderSvg class="checkpoint-edit-form-v2__video-processing-loader" />
          </div>
          <p class="checkpoint-edit-form-v2__video-processing-text">
            Video is being processed... This usually takes a few minutes.
          </p>
        </div>

        <!-- After signed URL: IQ video_to_components — original IQThinkingBanner (not task-page IQ pills). -->
        <template v-else-if="videoUploadFlowPhase === 'validating'">
          <IQThinkingBanner
            class="checkpoint-edit-form-v2__iq-thinking-banner"
            variant="microlearning"
            state="thinking"
          />
        </template>

        <!-- IQ generate in progress / failed — hide video + generate bar. -->
        <template v-else-if="iqVideoBannerState">
          <IQThinkingBanner
            class="checkpoint-edit-form-v2__iq-thinking-banner"
            variant="microlearning"
            :state="iqVideoBannerState"
            :started-at="iqVideoRun && iqVideoRun.startedAt"
            @retry="handleIqVideoGenerationRetry"
          />
        </template>

        <!-- Verification failed — verify-video-context could not run
             (VIDEO_CONTEXT_VERIFICATION_FAILED). Distinct from a mismatch verdict; cleared by
             retrying the same asset. Generate stays blocked (no fall-through). Must precede the
             mismatch branch so a retry-after-FAIL does not fall back to the persisted mismatch.
             Encapsulated by idea-MDP-452 (the flag that gates this whole editor). -->
        <template v-else-if="videoUploadFlowPhase === 'verification-failed'">
          <CheckpointVideoContextValidation
            class="checkpoint-edit-form-v2__context-validation"
            phase="verification-failed"
            :video="contextValidationVideoUrl || videoPassChapterVideoUrl"
            :verification-error="
              contextValidationVerificationError || VERIFICATION_FAILED_DEFAULT_ERROR
            "
            :retry-disabled="criteriaReadonly"
            @retry-click="handleContextValidationRetry"
          />
        </template>

        <!-- Mismatch — UploadVideoWizard context check fail OR persisted mismatch flag. -->
        <template
          v-else-if="videoUploadFlowPhase === 'mismatch' || isProposedVideoMicrolearningMismatch"
        >
          <CheckpointVideoContextValidation
            class="checkpoint-edit-form-v2__context-validation"
            phase="mismatch"
            :video="contextValidationVideoUrl || videoPassChapterVideoUrl"
            :error="contextValidationError || videoPassError"
            :replace-disabled="criteriaReadonly"
            :retry-disabled="criteriaReadonly"
            @replace-click="openUploadVideoWizard"
            @retry-click="handleContextValidationRetry"
          />
        </template>

        <!-- No quiz/inspection yet — Source Video + Generate Inspection. -->
        <template v-else-if="showVideoGenerateInspectionOnly">
          <MicrolearningVideoWithGenerateInspectionBar
            class="checkpoint-edit-form-v2__video-bar"
            section-title="Uploaded Video"
            generate-button-label="Generate Microlearning"
            :disabled="criteriaReadonly || hasActiveVideoMicrolearningRun"
            @suggest-instructions="handleSuggestVideoInstructions"
          />
          <CheckpointMicrolearningVideoCard
            class="checkpoint-edit-form-v2__video-card"
            hide-actions-bar
            :videoUrl="displayVideoUrl"
            :videoId="checkpoint?.microlearning_id || checkpoint?.microlearning_cloudflare_video_id"
            :checkpointId="checkpointId"
            :responsibilityId="responsibilityId"
            :checkpointPrefix="prefixDisplay"
            :checkpointCriteria="checkpoint?.criteria || ''"
            :checkpointInstructions="checkpoint?.instructions || ''"
            :microlearningInstructions="checkpoint?.microlearning_instructions || null"
            :setToMicrolearningInstructions="checkpoint?.set_to_microlearning_instructions"
            @replace="openUploadVideoWizard"
            @delete="showRemoveMicrolearningDialog = true"
            @refresh-checkpoint="handleRefreshCheckpoint"
          />
        </template>

        <!-- Quiz / inspection present — menu + tabs (InspectionInstructions pass / with-instructions). -->
        <template v-else>
          <CheckpointMicrolearningMenu
            v2
            type="Video"
            :tab-index="articleMenuTabIndex"
            :has-proposed-quiz="hasProposedQuiz"
            :has-proposed-inspection="hasProposedInspection"
            :quiz-question-count="proposedQuizQuestions.length"
            :inspection-step-count="proposedInspectionSteps.length"
            :proposed-quiz-is-autopass="proposedQuizIsAutopass"
            :quiz-mode="resolvedQuizMode"
            :readonly="criteriaReadonly"
            :video-status-badge="videoTabStatusBadge"
            @tabChange="handleArticleMenuTabChange"
            @remove-request="showRemoveMicrolearningDialog = true"
            @update:quizMode="handleQuizModeChange"
          />

          <!-- Video tab: VideoWithChaptersView (MDP-578) or legacy MicrolearningViewEdit editor surface. -->
          <VideoWithChaptersView
            v-if="videoWithChaptersViewEnabled && articleMenuTabIndex === 0"
            class="checkpoint-edit-form-v2__video-view-edit"
            :video-url="videoPassChapterVideoUrl"
            :duration-seconds="videoWithChaptersDurationSeconds"
            :chapters="videoWithChaptersForView"
            :summary-lead="videoWithChaptersSummaryLead"
            :summary-bullets="videoWithChaptersSummaryBullets"
            :editable="canEditCheckpointContent"
            :summary-saving="videoSummarySaving"
            :chapter-saving-index="videoChapterSavingIndex"
            @summary-save="handleVideoSummarySave"
            @chapter-save="handleVideoChapterSave"
          />
          <MicrolearningViewEdit
            v-else-if="articleMenuTabIndex === 0"
            class="checkpoint-edit-form-v2__video-view-edit"
            :video="videoPassChapterVideoUrl"
            :error="null"
            :summary="videoPassChapterSummary"
            :steps="videoPassChapterSteps"
            :showSteps="false"
            :editable="!criteriaReadonly"
            :summarySaving="videoSummarySaving"
            :chapterSavingIndex="videoChapterSavingIndex"
            @summary-save="handleVideoSummarySave"
            @chapter-save="handleVideoChapterSave"
          />

          <CheckpointMicrolearningTabs
            :tab-index="articleMenuTabIndex"
            :quiz-mode="resolvedQuizMode"
            :show-quiz-mode-quiz-actions="showQuizModeQuizActions"
            :show-quiz-mode-inspection-actions="showQuizModeInspectionActions"
            :is-quiz-at-question-limit="isQuizAtQuestionLimit"
            :is-inspection-at-step-limit="isInspectionAtStepLimit"
            :quiz-row-bulk-menu-items="quizRowBulkMenuItems"
            :inspection-row-bulk-menu-items="inspectionRowBulkMenuItems"
            :is-video="true"
            :video-quiz-generation-status="videoQuizGenerationStatus"
            :is-video-quiz-generation-in-progress="isVideoQuizGenerationInProgress"
            :inline-iq-trace-entries="inlineIqTraceEntries"
            :video-iq-generation-activity-label="videoIqGenerationActivityLabel"
            :quiz-suggest-banner-state="quizSuggestBannerState"
            :quiz-undo-visible="quizUndoVisible"
            :quiz-undo-busy="quizUndoBusy"
            :show-quiz-editor="showQuizEditor"
            :proposed-quiz-payload="proposedQuizPayload"
            :proposed-quiz-questions="proposedQuizQuestions"
            :criteria-readonly="criteriaReadonly"
            :video-inspection-generation-status="videoInspectionGenerationStatus"
            :is-video-inspection-generation-in-progress="isVideoInspectionGenerationInProgress"
            :inspection-suggest-banner-state="inspectionSuggestBannerState"
            :show-inspection-editor="showInspectionEditor"
            :proposed-inspection-protocol-parsed="proposedInspectionProtocolParsed"
            :proposed-inspection-steps="proposedInspectionSteps"
            :proposed-inspection-confirm-every-step="proposedInspectionConfirmEveryStep"
            :inspection-suggest-disabled="inspectionSuggestDisabled"
            :video-url="inspectionEditorVideoUrl"
            @suggest-quiz="handleSuggestQuiz"
            @suggest-inspection="handleSuggestInspection"
            @quiz-bulk-action="handleQuizBulkMenuAction"
            @quiz-suggest-retry="handleQuizSuggestRetry"
            @quiz-undo-suggestions="handleUndoQuizSuggestion"
            @quiz-dismiss-undo="handleDismissQuizUndo"
            @inspection-suggest-retry="handleInspectionSuggestRetry"
            @add-question="handleAddQuestion"
            @delete-question="handleDeleteQuestion"
            @update-question="handleUpdateQuestion"
            @reorder-questions="handleReorderQuestions"
            @add-step="handleAddInspectionStep"
            @update-step="handleUpdateInspectionStep"
            @delete-step="handleDeleteInspectionStep"
            @update:confirm-every-step="handleInspectionConfirmEveryStepChange"
            @delete-protocol-requested="handleDeleteInspectionProtocolRequested"
            @timestamp-click="handleInspectionTimestampClick"
          />
        </template>

        <DeleteQuizDialog
          :dialog="showDeleteEntireQuizDialog"
          :submitting="deleteEntireQuizSubmitting"
          @close="showDeleteEntireQuizDialog = false"
          @confirm="onConfirmDeleteEntireQuizDialog"
        />
        <RemoveMicrolearningDialog
          :value="showRemoveMicrolearningDialog"
          @input="showRemoveMicrolearningDialog = $event"
          @onAction="handleRemoveMicrolearningConfirm"
        />
        <GenerateVideoMicrolearningDialog
          :value="showGenerateVideoMicrolearningDialog"
          :initial-video-type="generateVideoMicrolearningDialogInitialVideoType"
          @input="onGenerateVideoMicrolearningDialogInput"
          @generate="handleGenerateVideoMicrolearningDialogSubmit"
        />
      </div>

      <!-- Article proposed microlearning — Quiz/Inspection tabs shared with CheckpointEditForm. -->
      <div v-else-if="isArticle" class="checkpoint-edit-form-v2__article">
        <!-- Short instructions to employee — above the content tab strip, kept only while a note exists. -->
        <CheckpointPrepNoteField
          v-if="hasEmployeePrepNote"
          class="checkpoint-edit-form-v2__prep-note"
          :note="employeePrepNote"
          :prose="isEmployeePrepNoteProseSurface"
          :readonly="employeePrepNoteReadonly"
          @update:note="handleEmployeePrepNoteUpdate"
        />

        <CheckpointMicrolearningMenu
          v2
          ref="articleMenuRef"
          type="Article"
          :tab-index="articleMenuTabIndex"
          :article-html="proposedArticleHtmlForEditor"
          :article-body="proposedArticleBodyForEditor"
          :article-title-hint="checkpoint?.criteria || ''"
          :article-editor-key="articleMenuEditorKey"
          :proposed-article="proposedArticleForMenu"
          :has-proposed-quiz="hasProposedQuiz"
          :has-proposed-inspection="hasProposedInspection"
          :quiz-question-count="proposedQuizQuestions.length"
          :inspection-step-count="proposedInspectionSteps.length"
          :proposed-quiz-is-autopass="proposedQuizIsAutopass"
          :quiz-mode="resolvedQuizMode"
          :readonly="criteriaReadonly"
          :save-request-token="articleSaveRequestToken"
          @update:article-html="handleProposedArticleHtmlUpdate"
          @tabChange="handleArticleMenuTabChange"
          @remove-request="showRemoveMicrolearningDialog = true"
          @article-save="handleProposedArticleSave"
          @article-discard="handleProposedArticleDiscard"
          @can-save-change="articleCanSave = $event"
          @update:quizMode="handleQuizModeChange"
          @import-scribe-request="openScribeImportDialog"
          @import-pdf-request="openPdfImportDialog"
          @import-word-request="openWordImportDialog"
        />

        <ScribeImportDialog
          :open="scribeImportDialogOpen"
          :account-id="getAccountId()"
          :responsibility-id="responsibilityId"
          :checkpoint-id="checkpointId"
          :start-import="handleScribeImportStart"
          :poll-status="handleScribeImportPoll"
          @close="handleScribeImportClose"
          @success="handleScribeImportSuccess"
        />

        <PdfImportDialog
          :open="pdfImportDialogOpen"
          :account-id="getAccountId()"
          :responsibility-id="responsibilityId"
          :checkpoint-id="checkpointId"
          :start-import="handlePdfImportStart"
          :poll-status="handlePdfImportPoll"
          @close="handlePdfImportClose"
          @success="handlePdfImportSuccess"
        />

        <WordImportDialog
          :open="wordImportDialogOpen"
          :account-id="getAccountId()"
          :responsibility-id="responsibilityId"
          :checkpoint-id="checkpointId"
          :start-import="handleWordImportStart"
          :poll-status="handleWordImportPoll"
          @close="handleWordImportClose"
          @success="handleWordImportSuccess"
        />

        <ExActionsPopUp
          :value="showImportReplaceConfirm"
          icon="checkpoint_pass"
          :destructive="true"
          :headline="'Replace article content?'"
          :htmlInfo="'Importing will replace the current article content — continue?'"
          :actionLabel="'Continue'"
          :cancelLabel="'Cancel'"
          :backgroundPatternBottomRight="true"
          :applyBorderClass="true"
          :width="500"
          :centerHeadline="true"
          data-testid="scribe-replace-confirm-dialog"
          @onAction="handleImportReplaceConfirm"
          @input="onImportReplaceConfirmInput"
        />

        <CheckpointMicrolearningTabs
          :tab-index="articleMenuTabIndex"
          :quiz-mode="resolvedQuizMode"
          :show-quiz-mode-quiz-actions="showQuizModeQuizActions"
          :show-quiz-mode-inspection-actions="showQuizModeInspectionActions"
          :is-quiz-at-question-limit="isQuizAtQuestionLimit"
          :is-inspection-at-step-limit="isInspectionAtStepLimit"
          :quiz-row-bulk-menu-items="quizRowBulkMenuItems"
          :inspection-row-bulk-menu-items="inspectionRowBulkMenuItems"
          :show-skip-banners="true"
          :skip-quiz-banner-body="skipQuizBannerBody"
          :quiz-suggest-banner-state="quizSuggestBannerState"
          :quiz-undo-visible="quizUndoVisible"
          :quiz-undo-busy="quizUndoBusy"
          :show-quiz-editor="showQuizEditor"
          :proposed-quiz-payload="proposedQuizPayload"
          :proposed-quiz-questions="proposedQuizQuestions"
          :criteria-readonly="criteriaReadonly"
          :inspection-suggest-banner-state="inspectionSuggestBannerState"
          :show-inspection-editor="showInspectionEditor"
          :proposed-inspection-protocol-parsed="proposedInspectionProtocolParsed"
          :proposed-inspection-steps="proposedInspectionSteps"
          :proposed-inspection-confirm-every-step="proposedInspectionConfirmEveryStep"
          :inspection-suggest-disabled="inspectionSuggestDisabled"
          @suggest-quiz="handleSuggestQuiz"
          @suggest-inspection="handleSuggestInspection"
          @quiz-bulk-action="handleQuizBulkMenuAction"
          @quiz-suggest-retry="handleQuizSuggestRetry"
          @quiz-undo-suggestions="handleUndoQuizSuggestion"
          @quiz-dismiss-undo="handleDismissQuizUndo"
          @inspection-suggest-retry="handleInspectionSuggestRetry"
          @add-question="handleAddQuestion"
          @delete-question="handleDeleteQuestion"
          @update-question="handleUpdateQuestion"
          @reorder-questions="handleReorderQuestions"
          @add-step="handleAddInspectionStep"
          @update-step="handleUpdateInspectionStep"
          @delete-step="handleDeleteInspectionStep"
          @update:confirm-every-step="handleInspectionConfirmEveryStepChange"
          @delete-protocol-requested="handleDeleteInspectionProtocolRequested"
        />
        <DeleteQuizDialog
          :dialog="showDeleteEntireQuizDialog"
          :submitting="deleteEntireQuizSubmitting"
          @close="showDeleteEntireQuizDialog = false"
          @confirm="onConfirmDeleteEntireQuizDialog"
        />
        <RemoveMicrolearningDialog
          :value="showRemoveMicrolearningDialog"
          @input="showRemoveMicrolearningDialog = $event"
          @onAction="handleRemoveMicrolearningConfirm"
        />
      </div>

      <DiscardDraftDialog
        :value="showDiscardDraftDialog"
        @input="onDiscardDraftDialogInput"
        @onAction="handleDiscardDraftConfirm"
      />

      <DiscardProposedChangesDialog
        :value="showDiscardProposedDialog"
        @input="onDiscardProposedDialogInput"
        @onAction="handleDiscardProposedConfirm"
      />

      <PublishVideoInspectionKitDialog
        :show="showPublishProposedDialog"
        @publish="handlePublishProposedConfirm"
        @close="onPublishProposedDialogClose"
      />

      <!-- Shared: create-from-inspector-only convert + Propose Changes + video upload (must stay outside type branches). -->
      <CreateCheckpointDialog
        :dialog="showAddMicrolearningDialog"
        :mode="createCheckpointDialogMode"
        :levelProp="addMicrolearningLevelProp"
        :criteriaProp="addMicrolearningCriteriaProp"
        :criteriaPrefixProp="addMicrolearningPrefixProp"
        :selectedTypeProp="addMicrolearningSelectedTypeProp"
        :omitTypes="createCheckpointDialogOmitTypes"
        typeOnly
        @cancel="handleAddMicrolearningDialogCancel"
        @next="handleAddMicrolearningDialogNext"
      />
      <TaskEmployeeToUploadVideoDialog
        :dialog="showTaskEmployeeFromAddDialog || showReassignUploadTaskDialog"
        :checkpoint-description="addMicrolearningCheckpointDescription"
        :mode="showReassignUploadTaskDialog ? 'reassign' : 'assign'"
        :submit-label="showReassignUploadTaskDialog ? 'Reassign' : 'Assign Task'"
        :initial-employee="reassignInitialEmployee"
        :initial-deadline="reassignInitialDeadline"
        :wizard="!showReassignUploadTaskDialog"
        @back="handleTaskEmployeeDialogBack"
        @onAction="handleTaskEmployeeDialogAction"
      />
      <CancelUploadTaskDialog
        :value="showCancelUploadTaskDialog"
        :loading="cancelUploadTaskLoading"
        @input="showCancelUploadTaskDialog = $event"
        @confirm="handleCancelUploadTaskConfirm"
      />
      <UploadVideoWizard
        :value="showUploadWizard"
        :state="uploadWizardState"
        :file="uploadWizardFile"
        :progress="uploadWizardProgress"
        title="Upload Video"
        submit-label="Upload Video"
        retry-label="Upload again"
        @input="onUploadWizardInput"
        @go-back="handleUploadWizardGoBack"
        @close="handleUploadWizardClose"
        @select="handleUploadWizardSelect"
        @remove="handleUploadWizardRemove"
        @submit="handleUploadWizardSubmit"
      />

      <v-dialog
        v-model="showInspectionVideoPlayer"
        max-width="680px"
        class="checkpoint-edit-form-v2__video-dialog"
        @click:outside="closeInspectionVideoDialog"
      >
        <v-card class="checkpoint-edit-form-v2__video-dialog-card">
          <v-card-title class="checkpoint-edit-form-v2__video-dialog-title">
            <span class="checkpoint-edit-form-v2__video-dialog-title-text">Watch Video</span>
            <v-spacer />
            <v-btn
              icon
              aria-label="Close video dialog"
              class="checkpoint-edit-form-v2__video-dialog-close"
              @click="closeInspectionVideoDialog"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="checkpoint-edit-form-v2__video-dialog-content">
            <div
              v-if="inspectionVideoSrc"
              class="checkpoint-edit-form-v2__video-dialog-iframe-wrap"
            >
              <iframe
                :key="inspectionVideoIframeKey"
                :src="inspectionVideoSrc"
                title="Checkpoint microlearning video"
                class="checkpoint-edit-form-v2__video-dialog-iframe"
                allow="
                  accelerometer;
                  autoplay;
                  clipboard-write;
                  encrypted-media;
                  gyroscope;
                  picture-in-picture;
                  fullscreen;
                "
                allowfullscreen
                frameborder="0"
              />
            </div>
            <div v-else class="checkpoint-edit-form-v2__video-dialog-empty">
              <v-icon size="64" color="grey">mdi-video-off</v-icon>
              <p>No video content available</p>
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>

      <ChatterDrawer
        ref="drawerContainer"
        :drawer="drawer"
        iconName="org_icon_checkpoint"
        type="Checkpoint"
        :typeValue="checklistCriteria"
        :listData="listData"
        object-type="checkpoint"
        :object-id="listData?.id ?? null"
        :chatterList="chatterList"
        :totalCheckpointChatter="totalCheckpointChatter"
        @closeDrawer="closeDrawer"
        @requestMoreCheckpointsChatter="handleRequestMoreChatter"
      />

      <component
        :is="dialog.component"
        v-for="dialog in menuDialogConfigs"
        :key="dialog.key"
        :show="dialog.show"
        :name="menuActionCheckpointName"
        v-on="dialog.listeners"
      />
    </section>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import CheckpointHeader from "@components/CheckpointHeader/CheckpointHeader.vue";
import MicrolearningEditBar from "@components/MicrolearningEditBar/MicrolearningEditBar.vue";
import CheckpointVersionHistoryPanel from "@components/CheckpointVersionHistoryPanel/CheckpointVersionHistoryPanel.vue";
import CheckpointPastVersionCard from "@components/CheckpointPastVersionCard/CheckpointPastVersionCard.vue";
import {
  mapCheckpointVersionHistoryRows,
  mapCheckpointVersionSnapshotToCheckpoint,
} from "@components/CheckpointVersionHistoryPanel/mapCheckpointVersionHistory";
import CriteriaViewEditInPlace from "@components/CriteriaViewEditInPlace/CriteriaViewEditInPlace.vue";
import NoLearningNeededBanner from "@components/NoLearningNeededBanner/NoLearningNeededBanner.vue";
import PendingTaskToUploadVideoBanner from "@components/PendingTaskToUploadVideoBanner/PendingTaskToUploadVideoBanner.vue";
import CheckpointPrepNoteField from "@components/CheckpointPrepNoteField/CheckpointPrepNoteField.vue";
import CheckpointTypeBanner from "@components/CheckpointTypeBanner/CheckpointTypeBanner.vue";
import InspectionsTableByVersion from "@components/InspectionsTableByVersion/InspectionsTableByVersion.vue";
import InspectionsTableByVersionFilterModal from "@components/InspectionsTableByVersionFilterModal/InspectionsTableByVersionFilterModal.vue";
import LockCheckpointBanner from "@components/LockCheckpointBanner/LockCheckpointBanner.vue";
import CreateCheckpointDialog from "@components/CreateCheckpointDialog/CreateCheckpointDialog.vue";
import TaskEmployeeToUploadVideoDialog from "@components/TaskEmployeeToUploadVideoDialog/TaskEmployeeToUploadVideoDialog.vue";
import CancelUploadTaskDialog from "@components/CancelUploadTaskDialog/CancelUploadTaskDialog.vue";
import CheckpointMicrolearningMenu from "@components/CheckpointMicrolearningMenu/CheckpointMicrolearningMenu.vue";
import CheckpointMicrolearningTabs from "@components/CheckpointMicrolearningTabs/CheckpointMicrolearningTabs.vue";
import ScribeImportDialog from "@components/ScribeImportDialog/ScribeImportDialog.vue";
import PdfImportDialog from "@components/PdfImportDialog/PdfImportDialog.vue";
import WordImportDialog from "@components/WordImportDialog/WordImportDialog.vue";
import CheckpointMicrolearningVideoCard from "@components/CheckpointMicrolearningVideoCard/CheckpointMicrolearningVideoCard.vue";
import MicrolearningVideoWithGenerateInspectionBar from "@components/MicrolearningVideoWithGenerateInspectionBar/MicrolearningVideoWithGenerateInspectionBar.vue";
import RemoveMicrolearningDialog from "@components/RemoveMicrolearningDialog/RemoveMicrolearningDialog.vue";
import DiscardDraftDialog from "@components/DiscardDraftDialog/DiscardDraftDialog.vue";
import DiscardProposedChangesDialog from "@components/DiscardProposedChangesDialog/DiscardProposedChangesDialog.vue";
import PublishVideoInspectionKitDialog from "@components/PublishVideoInspectionKitDialog/PublishVideoInspectionKitDialog.vue";
import GenerateVideoMicrolearningDialog from "@components/GenerateVideoMicrolearningDialog/GenerateVideoMicrolearningDialog.vue";
import IQThinkingBanner from "@components/IQThinkingBanner/IQThinkingBanner.vue";
import { useIQChatStore } from "@components/IQOverlay/store/iqChatStore";
import { useIqVideoMicrolearningStore } from "@components/IQOverlay/store/iqVideoMicrolearningStore";
import { useIqVideoProcessingStore } from "@components/IQOverlay/store/iqVideoProcessingStore";
import { useIqCheckpointSuggestStore } from "@components/IQOverlay/store/iqCheckpointSuggestStore";
import MicrolearningViewEdit from "@components/MicrolearningViewEdit/MicrolearningViewEdit.vue";
import VideoWithChaptersView from "@components/VideoWithChaptersView/VideoWithChaptersView.vue";
import {
  LD_FLAG_IDEA_MDP_452,
  LD_FLAG_VIDEO_WITH_CHAPTERS_VIEW,
} from "@components/constants/launchDarklyFlagKeys";
import {
  MIN_QUIZ_ANSWERS,
  MAX_QUIZ_ANSWERS,
  QUIZ_QUESTION_INVALID_MESSAGE,
} from "@components/constants/quizLimits";
import DeleteQuizDialog from "@components/DeleteQuizDialog/DeleteQuizDialog.vue";
import StreamProcessingLoaderSvg from "@components/StreamProcessingLoaderSvg/StreamProcessingLoaderSvg.vue";
import UploadVideoWizard from "@components/UploadVideoWizard/UploadVideoWizard.vue";
import CheckpointVideoContextValidation from "@components/CheckpointVideoContextValidation/CheckpointVideoContextValidation.vue";
import ChatterDrawer from "@components/Drawer/ChatterDrawer.vue";
import ActivateCheckpointDialog from "@components/ActivateCheckpointDialog/ActivateCheckpointDialog.vue";
import DeleteCheckpointDialog from "@components/DeleteCheckpointDialog/DeleteCheckpointDialog.vue";
import RetireCheckpointDialog from "@components/RetireCheckpointDialog/RetireCheckpointDialog.vue";
import ReactivateCheckpointDialog from "@components/ReactivateCheckpointDialog/ReactivateCheckpointDialog.vue";
import ArchiveCheckpointDialog from "@components/ArchiveCheckpointDialog/ArchiveCheckpointDialog.vue";
import UnarchiveCheckpointDialog from "@components/UnarchiveCheckpointDialog/UnarchiveCheckpointDialog.vue";
import {
  changeCheckpointV2,
  checkpointStateUpdate,
  deleteResponsibilityCheckpoint,
  removeCheckpointMicrolearning,
  addCheckpointQuestion,
  changeCheckpointQuestion,
  deleteCheckpointQuestion,
  addCheckpointInspectionStep,
  changeCheckpointInspectionStep,
  deleteCheckpointInspectionStep,
  deleteCheckpointProtocol,
  getCheckpointVideoSignedUrl,
  saveVideoInspectionInstructions,
  notifyCheckpointPresenceUpdated,
  assignVideoUploadTask,
  cancelUploadTask,
  reassignVideoUploadTask,
  sendUploadTaskReminder,
  publishProposedInspectionKit,
  proposeChangeStartingFromCopy,
  discardChangeProposal,
  getCheckpointVersions,
  getResponsibilityCheckpointDetailV2,
  importScribeArticle,
  getScribeImportJobStatus,
  importPdfArticle,
  getPdfImportJobStatus,
  importWordArticle,
  getWordImportJobStatus,
} from "@/api/responsibilityAPI";
import {
  getCloudflareVideoStatus,
  confirmProposedVideo,
  generateVideoUploadUrl,
} from "@/api/proficiencyAPI";
import { useCheckpointPresence } from "@/composables/checkpoint/useCheckpointPresence";
import { useCheckpointInspectionsByVersion } from "@/composables/checkpoint/useCheckpointInspectionsByVersion";
import { maybeNormalizeCriteriaBodyEn } from "@components/utils/normalizeCriteriaBodyEn";
import { formatCriteriaPrefix } from "@components/utils/formatCriteriaPrefix";
import { formatArticleHtml, shouldPreferArticleHtml } from "@components/utils/formatArticleHtml";
import { prepareScribeImportContent } from "@components/utils/scribeImport";
import { buildPdfImportSummaryMessage } from "@components/utils/pdfImport";
import { buildWordImportSummaryMessage } from "@components/utils/wordImport";
import ExActionsPopUp from "@components/common/ExActionsPopUp.vue";
import { useNotificationStore } from "@/store/pinia/notifications";
import { useCheckpointVersionsStore } from "@/store/pinia/checkpointVersions";
import {
  isCheckpointActivateDisabled,
  hasPendingUploadTask as hasPendingUploadTaskForCheckpoint,
} from "@/utils/draftCheckpointValidity";
import {
  isProcessingMicrolearningString,
  hasMeaningfulProposedArticle,
  hasProposedVideoAsset,
  hasValue,
  needsSignedVideoUrlResolution,
} from "@/utils/activeCheckpointMicrolearningValidity";
import {
  cloudflareStreamIframeHtml,
  cloudflareStreamIframeSrc,
  extractCloudflareStreamVideoUidFromEmbeds,
  isCloudflareSignedUrl,
  normalizeCloudflareStreamVideoUid,
} from "@/utils/simulatedCloudflareStreamWebhook";
import {
  VIDEO_POST_UPLOAD_TOAST_MESSAGE,
  VIDEO_POST_UPLOAD_TOAST_TITLE,
  isPlayableSignedVideoUrl,
  pollCloudflareStreamStatus,
} from "@/utils/videoPostUploadReveal";
import {
  buildVideoToComponentsCheckpointPatch,
  checkpointHasGeneratedVideoMicrolearningKit,
} from "@/utils/buildVideoToComponentsCheckpointPatch";
import {
  buildVideoToComponentsPayload,
  buildIqVideoSuggestEndpoint,
} from "@/utils/buildVideoToComponentsPayload";
import {
  buildContextValidationSuggestedInstructions,
  isContextValidationFail,
  isContextVerificationFailed,
  VERIFICATION_FAILED_DEFAULT_ERROR,
  VIDEO_CONTEXT_STREAM_INTERRUPTED_ERROR,
  buildClearContextValidationMismatchPatch,
  isVideoContextMismatchCheckpoint,
  resolvePersistedVideoMismatchError,
} from "@/utils/buildContextValidationSuggestedInstructions";
import {
  MAX_CHECKPOINT_QUIZ_QUESTIONS,
  buildArticleMicrolearningContentForSuggest,
  buildVideoMicrolearningContentForSuggest,
  mapSuggestedQuizQuestionsToApi,
} from "@/utils/buildSuggestQuizPayload";
import {
  MAX_INSPECTION_STEPS,
  filterDuplicateSuggestedInspectionSteps,
  mapSuggestedStepsToEditorRows,
} from "@/utils/buildSuggestInspectionProtocolPayload";
import { askIQStream } from "@/api/askIQApi";
import { suggestQuizStream } from "@/api/suggestQuizApi";
import { suggestInspectionProtocolStream } from "@/api/suggestInspectionProtocolApi";
import configService from "@/services/config";
import store from "@/store";
import * as tus from "tus-js-client";

const notificationStore = useNotificationStore();
const checkpointVersionsStore = useCheckpointVersionsStore();
const { proxy } = getCurrentInstance();

/** MDP-578 — learner-style video + TOC preview on the Video tab (replaces MicrolearningViewEdit when on). */
const videoWithChaptersViewEnabled = ref(false);

onMounted(async () => {
  try {
    videoWithChaptersViewEnabled.value =
      (await proxy?.$getFlag?.(LD_FLAG_VIDEO_WITH_CHAPTERS_VIEW, true)) === true;
  } catch {
    videoWithChaptersViewEnabled.value = true;
  }
});
/** ER-12788 — Undo after Suggest Quiz (idea-MDP-452). Off until the flag resolves true. */
const quizUndoEnabled = ref(false);
onMounted(async () => {
  try {
    quizUndoEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_452, false)) === true;
  } catch {
    quizUndoEnabled.value = false;
  }
});
const showRemoveMicrolearningDialog = ref(false);
const showDiscardDraftDialog = ref(false);
const showDiscardProposedDialog = ref(false);
const showDeleteEntireQuizDialog = ref(false);
const deleteEntireQuizSubmitting = ref(false);
const showGenerateVideoMicrolearningDialog = ref(false);
const scribeImportDialogOpen = ref(false);
const pdfImportDialogOpen = ref(false);
const wordImportDialogOpen = ref(false);
const articleMenuRef = ref(null);
// ER-11753 / ER-12395 — a successful Scribe or PDF import that found a non-empty editor is held
// here until the author confirms the replace in the shared confirm dialog.
const pendingImportContent = ref(null);
// The toast ({ type, message }) shown once the held content has been applied.
const pendingImportNotice = ref(null);
const showImportReplaceConfirm = ref(false);
/** ER-12337 — video-microlearning generation state lives in a Pinia store keyed by
 * checkpoint id so it survives tab switches / editor unmounts. */
const iqVideoMicrolearningStore = useIqVideoMicrolearningStore();
const iqChatStore = useIQChatStore();
/**
 * ER-12337 — the upload → Cloudflare encoding → signed URL → video_to_components context
 * validation workflow and the quiz/inspection suggestion streams live in checkpoint-keyed
 * Pinia stores, so they survive the editor unmounting on a responsibility-tab / route change.
 * The editor rehydrates their status/results from the stores and re-fetches once a background
 * persist completes.
 */
const iqVideoProcessingStore = useIqVideoProcessingStore();
const iqCheckpointSuggestStore = useIqCheckpointSuggestStore();

/** This checkpoint's durable video-processing record (reactive via the Pinia store). */
function videoRecord() {
  return iqVideoProcessingStore.get(checkpointId.value);
}
function ensureVideoRecord() {
  return iqVideoProcessingStore.ensure(checkpointId.value);
}
function videoProcessingCoord() {
  return iqVideoProcessingStore.coord(checkpointId.value);
}
/** Two-way binding onto a durable record field, so `xxx.value` reads/writes the store. */
function bindVideoState(key) {
  return computed({
    get: () => videoRecord()[key],
    set: (value) => {
      ensureVideoRecord()[key] = value;
    },
  });
}

/** Everything the video-processing runner needs from the editor at start time. */
function buildVideoProcessingContext() {
  return {
    checkpointId: checkpointId.value,
    responsibilityId: props.responsibilityId,
    accountId: getAccountId(),
    checkpoint: props.checkpoint,
    /**
     * The run outlives the editor; a late-arriving Cloudflare id only shows up in the live
     * prop after refresh-checkpoint, so give the store a way to re-read it.
     */
    getCheckpoint: () => props.checkpoint,
    prefix: prefixDisplay.value,
    criteria: props.checkpoint?.criteria || "",
    responsibilityName: props.responsibilityName || "",
    responsibilityCode: props.responsibilityCode || "",
    responsibilityPurpose: props.responsibilityPurpose || "",
    selection: inlineVideoToComponentsSelection.value,
    displayVideoUrl: displayVideoUrl.value,
    videoPassChapterVideoUrl: videoPassChapterVideoUrl.value,
    proposedInstructions: displayCheckpoint.value?.proposed_instructions ?? null,
  };
}

/** The run for THIS checkpoint (null when none) — reactive via the Pinia store. */
const iqVideoRun = computed(() => iqVideoMicrolearningStore.getRun(props.checkpoint?.id ?? null));
/** null | 'thinking' | 'failed' — inline IQThinkingBanner, derived from the store run. */
const iqVideoBannerState = computed(() => {
  const run = iqVideoRun.value;
  if (!run) return null;
  if (run.status === "thinking") return "thinking";
  if (
    run.status === "failed" &&
    run.outcome !== "cancelled" &&
    run.outcome !== "mismatch"
  ) {
    return "failed";
  }
  return null;
});
/** True while a run is in flight for THIS checkpoint — Generate must stay disabled. */
const hasActiveVideoMicrolearningRun = computed(() =>
  iqVideoMicrolearningStore.hasActiveRun(props.checkpoint?.id ?? null)
);
/** True once this checkpoint's run has completed (collapsed "Thought for {N}s" banner). */
const iqVideoDone = computed(() => iqVideoRun.value?.status === "done");
/** Thinking duration for the done banner, reconstructed from the stored timestamps. */
const iqVideoDoneElapsedMs = computed(() => {
  const run = iqVideoRun.value;
  if (!run || run.status !== "done") return 0;
  const startedAt = typeof run.startedAt === "number" ? run.startedAt : Date.now();
  const finishedAt = typeof run.finishedAt === "number" ? run.finishedAt : startedAt;
  return Math.max(0, finishedAt - startedAt);
});

function beginInlineIqThinkingStream() {
  iqChatStore.startThinking();
  iqChatStore.updateCurrentReasoning("");
}

function updateInlineIqThinkingStream(text) {
  iqChatStore.updateCurrentReasoning(text);
}

function endInlineIqThinkingStream() {
  iqChatStore.stopThinking();
  iqChatStore.updateCurrentReasoning("");
}

const inlineVideoToComponentsSelection = ref(null);
const articleCanSave = ref(false);
const articleSaveRequestToken = ref(0);
const articleMenuTabIndex = ref(0);
/**
 * ER-12337 — quiz/inspection suggestion status lives in iqCheckpointSuggestStore keyed by
 * checkpoint id, so a responsibility-tab / route change does not drop an in-flight stream.
 */
const quizSuggestRun = computed(() => iqCheckpointSuggestStore.getQuizRun(checkpointId.value));
/** null | 'thinking' | 'failed' — IQThinkingBanner while /suggest-quiz/ streams. */
const quizSuggestBannerState = computed(() => quizSuggestRun.value?.bannerState ?? null);
/** null | 'connecting' | 'streaming' | 'done' | 'stalled' for video quiz generation. */
const videoQuizGenerationStatus = computed(() => quizSuggestRun.value?.videoStatus ?? null);
const inspectionSuggestRun = computed(() =>
  iqCheckpointSuggestStore.getInspectionRun(checkpointId.value)
);
/** null | 'thinking' | 'failed' — IQThinkingBanner while /suggest-inspection-protocol/ streams. */
const inspectionSuggestBannerState = computed(
  () => inspectionSuggestRun.value?.bannerState ?? null
);
/** null | 'connecting' | 'streaming' | 'done' | 'stalled' for video inspection suggest. */
const videoInspectionGenerationStatus = computed(
  () => inspectionSuggestRun.value?.videoStatus ?? null
);
/** null | 'connecting' | 'streaming' | 'done' | 'stalled' during upload context validation. */
const videoContextValidationGenerationStatus = bindVideoState("validationStatus");
/**
 * Max-wait for IQ SSE streams (video context validation + inspection suggestions).
 * 6 minutes: /video_to_components/ can spend several minutes on Cloudflare Stream → MP4
 * download + Gemini Files upload before the first verdict event (ER-12371 AC-8).
 */
const IQ_STREAM_TIMEOUT_MS = 360000;


const isVideoQuizGenerationInProgress = computed(
  () =>
    videoQuizGenerationStatus.value === "connecting" ||
    videoQuizGenerationStatus.value === "streaming"
);
const isQuizSuggesting = computed(
  () => quizSuggestBannerState.value === "thinking" || isVideoQuizGenerationInProgress.value
);
/** CheckpointIQGenerationStatus shows panel chrome only — no streamed thinking / JSON lines. */
const inlineIqTraceEntries = computed(() => []);
const videoIqGenerationActivityLabel = "Watching the video…";
const showQuizEditor = computed(
  () => !quizSuggestBannerState.value && !videoQuizGenerationStatus.value
);
const isVideoInspectionGenerationInProgress = computed(
  () =>
    videoInspectionGenerationStatus.value === "connecting" ||
    videoInspectionGenerationStatus.value === "streaming"
);
const isInspectionSuggesting = computed(
  () =>
    inspectionSuggestBannerState.value === "thinking" || isVideoInspectionGenerationInProgress.value
);
const showInspectionEditor = computed(
  () =>
    resolvedQuizMode.value !== "SKIP_INSPECTION" &&
    !inspectionSuggestBannerState.value &&
    !videoInspectionGenerationStatus.value
);
const isVideoContextValidationInProgress = computed(
  () =>
    videoContextValidationGenerationStatus.value === "connecting" ||
    videoContextValidationGenerationStatus.value === "streaming"
);

const quizRowBulkMenuItems = [
  {
    name: "Delete quiz",
    value: "delete-quiz",
    icon: "icons/dropdown/trash.svg",
    negative: true,
    destructive: true,
  },
];

const inspectionRowBulkMenuItems = [
  {
    name: "Delete protocol",
    value: "delete-protocol",
    icon: "icons/dropdown/trash.svg",
    negative: true,
    destructive: true,
  },
];

const isQuizAtQuestionLimit = computed(
  () => proposedQuizQuestions.value.length >= MAX_CHECKPOINT_QUIZ_QUESTIONS
);

const isInspectionAtStepLimit = computed(
  () => proposedInspectionSteps.value.length >= MAX_INSPECTION_STEPS
);

const showQuizModeQuizActions = computed(
  () =>
    !criteriaReadonly.value &&
    articleMenuTabIndex.value === 1 &&
    resolvedQuizMode.value !== "SKIP_QUIZ" &&
    proposedQuizQuestions.value.length > 0
);

const showQuizModeInspectionActions = computed(
  () =>
    !criteriaReadonly.value &&
    articleMenuTabIndex.value === 2 &&
    resolvedQuizMode.value !== "SKIP_INSPECTION" &&
    proposedInspectionSteps.value.length > 0
);

const inspectionSuggestDisabled = computed(
  () => criteriaReadonly.value || resolvedQuizMode.value === "SKIP_INSPECTION"
);

/** Remounts CriteriaViewEditInPlace so its dirty baseline resets after a successful save. */
const criteriaFormEpoch = ref(0);
const savedCriteria = ref("");
const savedPrefix = ref("can");
const savedPrepNote = ref("");
const isCriteriaSavePending = ref(false);

const LEVEL_BY_NAME = {
  Apprentice: { id: 1, name: "Apprentice", value: "apprentice" },
  Professional: { id: 2, name: "Professional", value: "professional" },
  Coach: { id: 3, name: "Coach", value: "coach" },
  Master: { id: 4, name: "Master", value: "master" },
};

function normalizePrefixForApi(value) {
  if (value == null || value === "") return "can";
  return String(value).toLowerCase();
}

function formatPrefixDisplay(prefix) {
  const normalized = normalizePrefixForApi(prefix);
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

const props = defineProps({
  /** Draft checkpoint: { level, criteria, prefix, status, proposed_microlearning_type, ... }. */
  checkpoint: {
    type: Object,
    required: true,
  },
  responsibilityId: {
    type: [String, Number],
    default: null,
  },
  responsibilityName: {
    type: String,
    default: "",
  },
  responsibilityCode: {
    type: String,
    default: "",
  },
  responsibilityPurpose: {
    type: String,
    default: "",
  },
  chatterList: {
    type: Array,
    default: () => [],
  },
  totalCheckpointChatter: {
    type: Number,
    default: 0,
  },
  /**
   * After UploadVideoWizard TUS (create path): { videoId, thumbnail, checkpointId }.
   * Confirm-proposed-video is deferred until video_to_components returns.
   */
  pendingVideoConfirm: {
    type: Object,
    default: null,
  },
  /** Version history snapshot: hide editor chrome; render published content read-only. */
  pastVersionView: {
    type: Boolean,
    default: false,
  },
  /**
   * Optional host override for Inspections tab rows until inspections-by-version
   * loads (ER-11594). Shape: `{ id?, versionLabel, publishedDate?, isCurrent?,
   * configurationSummary?, rows[] }`.
   */
  inspectionVersionGroups: {
    type: Array,
    default: () => [],
  },
  /** Reset-pending inspection rows shown above the version groups (forwarded to the table). */
  inspectionResetGroup: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits([
  "back-to-list",
  "get-chatter-list",
  "requestMoreCheckpointsChatter",
  "optionClick",
  "discard-draft",
  "lock-to-edit",
  "exit-edit",
  "takeover-edit-lock",
  "lock-changed",
  "activate",
  "update-criteria",
  "update-prefix",
  "update-employee-prep-note",
  "pending-video-task-cancel",
  "pending-video-task-reassign",
  "pending-video-task-send-reminder",
  "proposed-knowledge-article-html-change",
  "proposed-knowledge-article-remove",
  "proposed-knowledge-article-save",
  "proposed-knowledge-article-discard",
  "refresh-checkpoint",
  "reload-checkpoint-detail",
  "checkpoint-updated",
  "checkpoint-activated",
  "checkpoint-count-changed",
  "add-microlearning",
  "propose-changes",
  "video-uploaded",
  "video-url-ready",
  "pending-video-confirm-cleared",
  "inspection-reset",
  "inspection-chatter-open",
  "inspection-reset-all",
]);

/**
 * ER-12337 — a background video-processing workflow may finish (and need to refresh the
 * parent / notify peers) while the editor is unmounted. The store delivers those domain
 * events through this emitter while mounted, and queues them otherwise; drain on mount.
 *
 * The read-only past-version form is rendered as a NESTED editor (CheckpointPastVersionCard)
 * while the live editor stays mounted, so it must never take over the emitter.
 */
const videoProcessingEmitter = (name, payload) => emit(name, payload);
if (!props.pastVersionView) {
  iqVideoProcessingStore.registerEmitter(videoProcessingEmitter);
}
onUnmounted(() => {
  iqVideoProcessingStore.unregisterEmitter(videoProcessingEmitter);
});
onMounted(() => {
  if (!props.pastVersionView) iqVideoProcessingStore.drainEmits();
});

const drawer = ref(false);
const checklistLevel = ref("");
const checklistCriteria = ref("");

const showVersionHistoryPanel = ref(false);
const versionHistoryLoading = ref(false);
const versionHistoryError = ref(null);
const versionHistoryRows = ref([]);
const versionSnapshotsById = ref({});
const selectedVersionId = ref(null);
const viewingPastVersion = ref(null);
let versionHistoryRequestId = 0;
/** ER-11249: current published version number for the V{n} pill in the edit bar. */
const currentVersionNumber = ref(null);
/** ER-11208: current live version's publish message + status for the history-icon tooltip. */
const currentPublishMessage = ref(null);
const currentPublishMessageStatus = ref(null);
/** ER-11208: LaunchDarkly kill switch for the history-icon hover tooltip. */
const historyTooltipEnabled = computed(() => Boolean(store.getters["featureFlags/ideaMDP586"]));
const listData = ref({});
const showActivateCheckpointDialog = ref(false);
const showPublishProposedDialog = ref(false);
const showDeleteCheckpointDialog = ref(false);
const showRetireCheckpointDialog = ref(false);
const showReActivateCheckpointDialog = ref(false);
const showArchiveCheckpointDialog = ref(false);
const showUnarchiveCheckpointDialog = ref(false);
const currentCheckPoint = ref(null);

const menuActionCheckpointName = computed(() => {
  const criteria = currentCheckPoint.value?.criteria || props.checkpoint?.criteria || "";
  return [prefixDisplay.value, criteria].filter(Boolean).join(" ").trim();
});

const menuDialogConfigs = computed(() => [
  {
    key: "activate",
    component: ActivateCheckpointDialog,
    show: showActivateCheckpointDialog.value,
    listeners: {
      onActivate: handleConfirmActivateFromMenu,
      onClose: () => {
        if (proxy?.$loader?.isAppLoading) return;
        showActivateCheckpointDialog.value = false;
      },
    },
  },
  {
    key: "delete",
    component: DeleteCheckpointDialog,
    show: showDeleteCheckpointDialog.value,
    listeners: {
      onDelete: handleConfirmDeleteFromMenu,
      onClose: () => (showDeleteCheckpointDialog.value = false),
    },
  },
  {
    key: "retire",
    component: RetireCheckpointDialog,
    show: showRetireCheckpointDialog.value,
    listeners: {
      onRetire: handleConfirmRetireFromMenu,
      onClose: () => {
        if (proxy?.$loader?.isAppLoading) return;
        showRetireCheckpointDialog.value = false;
      },
    },
  },
  {
    key: "reactivate",
    component: ReactivateCheckpointDialog,
    show: showReActivateCheckpointDialog.value,
    listeners: {
      onReactivate: handleConfirmReactivateFromMenu,
      onClose: () => {
        if (proxy?.$loader?.isAppLoading) return;
        showReActivateCheckpointDialog.value = false;
      },
    },
  },
  {
    key: "archive",
    component: ArchiveCheckpointDialog,
    show: showArchiveCheckpointDialog.value,
    listeners: {
      onArchive: handleConfirmArchiveFromMenu,
      onClose: () => {
        if (proxy?.$loader?.isAppLoading) return;
        showArchiveCheckpointDialog.value = false;
      },
    },
  },
  {
    key: "unarchive",
    component: UnarchiveCheckpointDialog,
    show: showUnarchiveCheckpointDialog.value,
    listeners: {
      onUnarchive: handleConfirmUnarchiveFromMenu,
      onClose: () => {
        if (proxy?.$loader?.isAppLoading) return;
        showUnarchiveCheckpointDialog.value = false;
      },
    },
  },
]);

function setCurrentCheckpointFromPayload(payload) {
  const rawData = payload?.data || props.checkpoint || null;
  currentCheckPoint.value = rawData ? JSON.parse(JSON.stringify(rawData)) : null;
}

function onHeaderMenuAction(payload) {
  const action = payload?.item?.value;
  setCurrentCheckpointFromPayload(payload);

  switch (action) {
    case "activate":
      if (isCheckpointActivateDisabled(props.checkpoint)) {
        return;
      }
      showActivateCheckpointDialog.value = true;
      return;
    case "delete":
      showDeleteCheckpointDialog.value = true;
      return;
    case "retire":
      showRetireCheckpointDialog.value = true;
      return;
    case "reactivate":
      showReActivateCheckpointDialog.value = true;
      return;
    case "archive":
      showArchiveCheckpointDialog.value = true;
      return;
    case "unarchive":
      showUnarchiveCheckpointDialog.value = true;
      return;
    case "move":
      emit("optionClick", {
        item: { value: "move_checkpoint", name: "Move" },
        data: currentCheckPoint.value,
        showDialog: true,
      });
      return;
    default:
      emit("optionClick", payload);
  }
}

async function handleConfirmDeleteFromMenu() {
  showDeleteCheckpointDialog.value = false;
  const accountId = getAccountId();
  const id = currentCheckPoint.value?.id ?? checkpointId.value;
  if (!accountId || props.responsibilityId == null || id == null) {
    return;
  }
  try {
    await deleteResponsibilityCheckpoint(accountId, props.responsibilityId, id);
    emit("checkpoint-count-changed");
    emit("back-to-list");
    emit("refresh-checkpoint");
  } catch (error) {
    console.error("[CheckpointEditFormV2] Error deleting checkpoint:", error);
  }
}

async function handleConfirmActivateFromMenu() {
  const accountId = getAccountId();
  const id = currentCheckPoint.value?.id ?? checkpointId.value;
  if (!accountId || props.responsibilityId == null || id == null) {
    showActivateCheckpointDialog.value = false;
    return;
  }
  // Keep dialog open so ExActionsPopUp Activate button shows $loader spinner.
  proxy?.$loader?.setAppLoading?.(true);
  try {
    // Publish proposed kit into active fields, then ensure checkpoint status is active.
    const publishResponse = await publishProposedInspectionKit(
      accountId,
      props.responsibilityId,
      id
    );
    let updated = publishResponse?.data ?? null;
    if (!updated || String(updated.status ?? "").toLowerCase() !== "active") {
      const activateResponse = await changeCheckpointV2(accountId, props.responsibilityId, id, {
        status: "active",
      });
      updated = activateResponse?.data ?? { ...(updated || {}), status: "active" };
    }
    showActivateCheckpointDialog.value = false;
    void notifyPeersOfCheckpointUpdate();
    if (updated) {
      emit("checkpoint-updated", { ...updated, status: updated.status || "active" });
    } else {
      emit("refresh-checkpoint");
    }
    emit("checkpoint-count-changed");
    emit("checkpoint-activated");
  } catch (error) {
    console.error("[CheckpointEditFormV2] Error activating checkpoint:", error);
    notificationStore.error(
      error?.response?.data?.detail ??
        error?.response?.data?.message ??
        "Could not activate checkpoint."
    );
  } finally {
    proxy?.$loader?.setAppLoading?.(false);
  }
}

async function handleConfirmRetireFromMenu() {
  const accountId = getAccountId();
  const id = currentCheckPoint.value?.id ?? checkpointId.value;
  if (!accountId || props.responsibilityId == null || id == null) {
    showRetireCheckpointDialog.value = false;
    return;
  }
  // Keep dialog open so ExActionsPopUp Retire button shows $loader spinner.
  proxy?.$loader?.setAppLoading?.(true);
  try {
    const response = await checkpointStateUpdate(accountId, props.responsibilityId, id, "retire");
    showRetireCheckpointDialog.value = false;
    if (response?.data) {
      void notifyPeersOfCheckpointUpdate();
      emit("checkpoint-updated", response.data);
    } else {
      void notifyPeersOfCheckpointUpdate();
      emit("refresh-checkpoint");
    }
    emit("checkpoint-count-changed");
  } catch (error) {
    console.error("[CheckpointEditFormV2] Error retiring checkpoint:", error);
    emit("refresh-checkpoint");
  } finally {
    proxy?.$loader?.setAppLoading?.(false);
  }
}

async function handleConfirmReactivateFromMenu() {
  const accountId = getAccountId();
  const id = currentCheckPoint.value?.id ?? checkpointId.value;
  if (!accountId || props.responsibilityId == null || id == null) {
    showReActivateCheckpointDialog.value = false;
    return;
  }
  // Keep dialog open so ExActionsPopUp Reactivate button shows $loader spinner.
  proxy?.$loader?.setAppLoading?.(true);
  try {
    const response = await checkpointStateUpdate(
      accountId,
      props.responsibilityId,
      id,
      "reactivate"
    );
    showReActivateCheckpointDialog.value = false;
    if (response?.data) {
      void notifyPeersOfCheckpointUpdate();
      emit("checkpoint-updated", response.data);
    } else {
      void notifyPeersOfCheckpointUpdate();
      emit("refresh-checkpoint");
    }
    emit("checkpoint-count-changed");
    emit("checkpoint-activated");
  } catch (error) {
    console.error("[CheckpointEditFormV2] Error reactivating checkpoint:", error);
    emit("refresh-checkpoint");
  } finally {
    proxy?.$loader?.setAppLoading?.(false);
  }
}

async function handleConfirmArchiveFromMenu() {
  const accountId = getAccountId();
  const id = currentCheckPoint.value?.id ?? checkpointId.value;
  if (!accountId || props.responsibilityId == null || id == null) {
    showArchiveCheckpointDialog.value = false;
    return;
  }
  // Keep dialog open so ExActionsPopUp Archive button shows $loader spinner.
  proxy?.$loader?.setAppLoading?.(true);
  try {
    const response = await changeCheckpointV2(accountId, props.responsibilityId, id, {
      status: "archived",
    });
    showArchiveCheckpointDialog.value = false;
    if (response?.data) {
      void notifyPeersOfCheckpointUpdate();
      emit("checkpoint-updated", response.data);
    } else {
      void notifyPeersOfCheckpointUpdate();
      emit("refresh-checkpoint");
    }
    emit("checkpoint-count-changed");
  } catch (error) {
    console.error("[CheckpointEditFormV2] Error archiving checkpoint:", error);
    emit("refresh-checkpoint");
  } finally {
    proxy?.$loader?.setAppLoading?.(false);
  }
}

async function handleConfirmUnarchiveFromMenu(updatedName) {
  const checkpointData = { ...(currentCheckPoint.value || {}) };
  const trimmedName = updatedName ? updatedName.trim() : "";
  const originalCriteria = checkpointData?.criteria ? checkpointData.criteria.trim() : "";
  if (trimmedName && trimmedName !== originalCriteria) {
    checkpointData.criteria = trimmedName;
    checkpointData._nameUpdated = true;
  }

  const accountId = getAccountId();
  const id = checkpointData?.id ?? checkpointId.value;
  if (!accountId || props.responsibilityId == null || id == null) {
    showUnarchiveCheckpointDialog.value = false;
    return;
  }

  const payload = { status: "retired" };
  if (checkpointData._nameUpdated && checkpointData.criteria && checkpointData.criteria.trim()) {
    payload.criteria = checkpointData.criteria.trim();
  }
  // Keep dialog open so ExActionsPopUp Unarchive button shows $loader spinner.
  proxy?.$loader?.setAppLoading?.(true);
  try {
    const response = await changeCheckpointV2(accountId, props.responsibilityId, id, payload);
    showUnarchiveCheckpointDialog.value = false;
    if (response?.data) {
      void notifyPeersOfCheckpointUpdate();
      emit("checkpoint-updated", response.data);
    } else {
      void notifyPeersOfCheckpointUpdate();
      emit("refresh-checkpoint");
    }
    emit("checkpoint-count-changed");
  } catch (error) {
    console.error("[CheckpointEditFormV2] Error unarchiving checkpoint:", error);
    emit("refresh-checkpoint");
  } finally {
    proxy?.$loader?.setAppLoading?.(false);
  }
}

const openChatter = (data) => {
  checklistLevel.value = data?.level || readinessLevel.value?.name || "Apprentice";
  checklistCriteria.value = data?.criteria || props.checkpoint?.criteria || "";
  listData.value = {
    id: data?.id ?? checkpointId.value,
    level: checklistLevel.value,
    criteria: checklistCriteria.value,
    prefix: data?.prefix || props.checkpoint?.prefix,
    instructions: data?.instructions || props.checkpoint?.instructions,
  };
  drawer.value = true;
  emit("get-chatter-list", listData.value);
};

const closeDrawer = (data) => {
  if (data) {
    drawer.value = false;
  }
};

const handleRequestMoreChatter = (event) => {
  emit("requestMoreCheckpointsChatter", event);
};

async function handleHeaderLevelUpdate(nextLevel) {
  if (!canEditCheckpointContent.value) return;
  if (nextLevel == null || !LEVEL_BY_NAME[nextLevel]) {
    return;
  }
  if (readinessLevel.value?.name === nextLevel) {
    return;
  }
  const accountId = getAccountId();
  if (!accountId || props.responsibilityId == null || checkpointId.value == null) {
    return;
  }
  try {
    const response = await changeCheckpointV2(
      accountId,
      props.responsibilityId,
      checkpointId.value,
      {
        criteria: props.checkpoint?.criteria || "",
        prefix: formatCriteriaPrefix(props.checkpoint?.prefix || "Can"),
        instructions: props.checkpoint?.instructions || "<p></p>",
        proficiency_level: LEVEL_BY_NAME[nextLevel].id,
        status: props.checkpoint?.status || "draft",
        account: accountId,
        responsibility: props.responsibilityId,
      }
    );
    if (response?.data) {
      void notifyPeersOfCheckpointUpdate();
      emit("checkpoint-updated", response.data);
    } else {
      void notifyPeersOfCheckpointUpdate();
      emit("refresh-checkpoint");
    }
  } catch (error) {
    console.error("[CheckpointEditFormV2] Error updating checkpoint level:", error);
    emit("refresh-checkpoint");
  }
}

function snapshotCriteriaBaseline(checkpoint = props.checkpoint) {
  savedCriteria.value = checkpoint?.criteria || "";
  savedPrefix.value = normalizePrefixForApi(checkpoint?.prefix);
  savedPrepNote.value = checkpoint?.employee_prep_note ?? "";
}

function splitDisplayName(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/);
  if (!parts.length || !parts[0]) return { first: "", last: "" };
  return { first: parts[0], last: parts.slice(1).join(" ") };
}

/** Current user ids from localStorage accessPermission (user.id + access record id). */
function getCurrentUserIds() {
  try {
    const raw = localStorage.getItem("accessPermission");
    const accessPermission = raw ? JSON.parse(raw) : null;
    if (!accessPermission) return { userId: null, accessId: null };
    return {
      userId: accessPermission.user?.id ?? null,
      accessId: accessPermission.id ?? null,
    };
  } catch {
    return { userId: null, accessId: null };
  }
}

/** Prefer lock_checkpoint (API) then legacy locked boolean. */
function isCheckpointLocked(checkpoint) {
  if (checkpoint?.lock_checkpoint != null) return Boolean(checkpoint.lock_checkpoint);
  return Boolean(checkpoint?.locked);
}

/** API uses locked_by_user; keep locked_by / edit_lock_holder_* as fallbacks. */
function getLockHolder(checkpoint) {
  return checkpoint?.locked_by_user ?? checkpoint?.locked_by ?? null;
}

/**
 * Prefer locked_by_user_id (acquire/release API user id), then holder object /
 * legacy access-holder fields (SSE may send access id).
 */
function getLockHolderId(checkpoint) {
  const holder = getLockHolder(checkpoint);
  return (
    checkpoint?.locked_by_user_id ??
    holder?.id ??
    checkpoint?.edit_lock_holder_access_id ??
    checkpoint?.edit_lock_holder_access ??
    null
  );
}

function formatLockEditorName(holder) {
  if (!holder) return "";
  const fromParts = [holder.first_name, holder.last_name].filter(Boolean).join(" ").trim();
  if (fromParts) return fromParts;
  return holder.name || holder.username || holder.email || "";
}

function resolveLockState(checkpoint) {
  if (!isCheckpointLocked(checkpoint)) return "unlocked";

  const holderId = getLockHolderId(checkpoint);

  // Locked with no holder payload → treat as locked by self (e.g. after create/PATCH).
  if (holderId == null) return "lockedBySelf";

  const { userId, accessId } = getCurrentUserIds();
  const holder = String(holderId);
  if (
    (userId != null && holder === String(userId)) ||
    (accessId != null && holder === String(accessId))
  ) {
    return "lockedBySelf";
  }
  return "lockedByOther";
}

/**
 * Active checkpoints store published content on active_* fields (proposed_* cleared on publish).
 * Draft checkpoints keep reading proposed_* / draft microlearning fields.
 * Migration fallback: if active_* is empty, keep reading proposed_* so UI does not break.
 */
const ACTIVE_CONTENT_BY_PROPOSED = {
  proposed_microlearning_type: "active_microlearning_type",
  proposed_article: "active_article",
  proposed_quiz: "active_quiz",
  proposed_quiz_is_autopass: "active_quiz_autopass",
  proposed_tutorial: "active_tutorial",
  proposed_video_id: "active_video_id",
  proposed_apivideo_id: "active_apivideo_id",
  proposed_cloudflarevideo_id: "activecloudflarevideo_id",
  proposed_video_source: "active_video_source",
  proposed_video_source_thumbnail: "active_video_source_thumbnail",
  proposed_video_microlearning: "active_video_microlearning",
  proposed_qr_code: "active_qr_code",
  proposed_inspection_protocol: "active_inspection_protocol",
  proposed_inspection_type: "active_inspection_type",
  proposed_instructions: "active_inspection_instructions",
  microlearning: "active_microlearning",
  microlearning_video_id: "active_microlearning_video_id",
  microlearning_cloudflare_video_id: "active_microlearning_cloudflare_video_id",
  microlearning_video_thumbnail: "active_microlearning_video_thumbnail",
};

const BOOLEAN_ACTIVE_CONTENT_KEYS = new Set(["proposed_quiz_is_autopass"]);

function isPopulatedContentValue(value) {
  if (value == null) return false;
  if (typeof value === "boolean") return true;
  if (typeof value === "string") return value.trim() !== "";
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return true;
}

function pickActiveOrProposed(activeValue, proposedValue) {
  return isPopulatedContentValue(activeValue) ? activeValue : (proposedValue ?? null);
}

const isActiveCheckpoint = computed(
  () => String(props.checkpoint?.status ?? "").toLowerCase() === "active"
);

const isRetiredCheckpoint = computed(
  () => String(props.checkpoint?.status ?? "").toLowerCase() === "retired"
);

const isArchivedCheckpoint = computed(
  () => String(props.checkpoint?.status ?? "").toLowerCase() === "archived"
);

/**
 * Active checkpoint has a change proposal when proposed_microlearning_type is set
 * (publish clears proposed_* including type).
 */
const hasProposedChanges = computed(() => {
  if (!isActiveCheckpoint.value) return false;
  const c = props.checkpoint;
  return (
    isPopulatedContentValue(c?.proposed_microlearning_type) ||
    isPopulatedContentValue(c?.proposed_inspection_type) ||
    isPopulatedContentValue(c?.proposed_quiz) ||
    isPopulatedContentValue(c?.proposed_inspection_protocol)
  );
});

/** Active | Proposed Changes tab while `active-with-changes`. */
const editBarSelectedTab = ref("proposed-changes");

watch(
  hasProposedChanges,
  (hasProposed) => {
    if (hasProposed) {
      editBarSelectedTab.value = "proposed-changes";
      return;
    }
    if (editBarSelectedTab.value === "proposed-changes") {
      editBarSelectedTab.value = "primary";
    }
  },
  { immediate: true }
);

watch(
  () => props.checkpoint?.id,
  () => {
    viewingPastVersion.value = null;
  }
);

const isViewingProposedChanges = computed(
  () => hasProposedChanges.value && editBarSelectedTab.value === "proposed-changes"
);

/**
 * MDP-592: Inspections tab in the edit bar. Mapped for active checkpoints only —
 * draft / retired / archived have no inspections surface — and gated by the
 * idea-MDP-592 rollout flag. Hidden after a 403/404 from inspections-by-version.
 */
const inspectionsTabEligible = computed(
  () => isActiveCheckpoint.value && Boolean(store.getters["featureFlags/ideaMDP592"])
);

const {
  versionGroups: fetchedInspectionVersionGroups,
  resetGroup: fetchedInspectionResetGroup,
  inspectionCount: fetchedInspectionCount,
  hasLoaded: inspectionsHasLoaded,
  unavailable: inspectionsUnavailable,
  filterPayload: inspectionFilterPayload,
  chipFilters: inspectionChipFilters,
  employeeSearch: inspectionEmployeeSearch,
  loading: inspectionsLoading,
  filterModalOpen: inspectionFilterModalOpen,
  applyFilters: applyInspectionFilters,
  applyChipFilters: applyInspectionChipFilters,
  setEmployeeSearch: setInspectionEmployeeSearch,
  resetInspectionRow,
  bulkResetInspections,
  canBulkReset: canBulkResetInspections,
} = useCheckpointInspectionsByVersion({
  accountId: () => getAccountId(),
  responsibilityId: () => props.responsibilityId,
  checkpointId: () => props.checkpoint?.id ?? null,
  accessId: () => getCurrentUserIds().accessId,
  enabled: inspectionsTabEligible,
  notifyError: (message) => notificationStore.error(message),
});

const inspectionBulkResetBusy = ref(false);

const showInspectionsTab = computed(
  () => inspectionsTabEligible.value && !inspectionsUnavailable.value
);

const inspectionChatterTitle = computed(
  () => checklistCriteria.value || props.checkpoint?.criteria || ""
);

/** Inspections tab body replaces the microlearning content while it is selected. */
const showInspectionsPanel = computed(
  () => showInspectionsTab.value && editBarSelectedTab.value === "inspections"
);

const displayInspectionVersionGroups = computed(() =>
  inspectionsHasLoaded.value ? fetchedInspectionVersionGroups.value : props.inspectionVersionGroups
);

const displayInspectionResetGroup = computed(() =>
  inspectionsHasLoaded.value ? fetchedInspectionResetGroup.value : props.inspectionResetGroup
);

/** Tab count badge: filtered `count` after load, otherwise derived from host props. */
const inspectionsCount = computed(() => {
  if (inspectionsHasLoaded.value) return fetchedInspectionCount.value;
  const resetRows = displayInspectionResetGroup.value?.length ?? 0;
  return displayInspectionVersionGroups.value.reduce(
    (total, group) => total + (Array.isArray(group?.rows) ? group.rows.length : 0),
    resetRows
  );
});

watch(showInspectionsTab, (enabled) => {
  if (!enabled && editBarSelectedTab.value === "inspections") {
    editBarSelectedTab.value = hasProposedChanges.value ? "proposed-changes" : "primary";
  }
});

/**
 * Checkpoint props with active_* content mapped onto proposed_* keys when viewing
 * published content: Active tab (not Proposed Changes), RETIRED, or ARCHIVED default view.
 * When viewing Proposed Changes, keep raw proposed_* so edits show correctly.
 */
const displayCheckpoint = computed(() => {
  const ckpt = props.checkpoint;
  if (!ckpt) return ckpt;

  const useActiveContent =
    props.pastVersionView ||
    isRetiredCheckpoint.value ||
    isArchivedCheckpoint.value ||
    (isActiveCheckpoint.value && !isViewingProposedChanges.value);
  if (!useActiveContent) return ckpt;

  const view = { ...ckpt };
  Object.entries(ACTIVE_CONTENT_BY_PROPOSED).forEach(([proposedKey, activeKey]) => {
    if (BOOLEAN_ACTIVE_CONTENT_KEYS.has(proposedKey)) {
      // Prefer active autopass only when active quiz content exists; else keep proposed flag.
      const activeQuizPopulated = isPopulatedContentValue(ckpt.active_quiz);
      view[proposedKey] = activeQuizPopulated
        ? Boolean(ckpt[activeKey])
        : Boolean(ckpt[proposedKey]);
      return;
    }
    view[proposedKey] = pickActiveOrProposed(ckpt[activeKey], ckpt[proposedKey]);
  });
  return view;
});

const microlearningType = computed(() => {
  const proposed = String(displayCheckpoint.value?.proposed_microlearning_type ?? "").trim();
  if (proposed) return proposed.toLowerCase();
  // Migrated drafts/actives may only have active_microlearning_type populated.
  return String(displayCheckpoint.value?.active_microlearning_type ?? "")
    .trim()
    .toLowerCase();
});

const isInspectorOnly = computed(() => {
  const t = microlearningType.value;
  // Default to inspector-only UI when type is unset (legacy / storybook fixtures).
  return !t || t === "inspector_only";
});

const isTaskAnEmployee = computed(() => microlearningType.value === "task_an_employee");

const isArticle = computed(() => microlearningType.value === "article");

const isVideo = computed(() => microlearningType.value === "video");

/**
 * Live eligibility signal for Send Reminder, sourced from the backend's
 * `can_send_upload_task_reminder` field (ER-11907). Absent/undefined on payloads from
 * before the backend field shipped is treated as eligible so existing behaviour is kept.
 */
const canSendUploadTaskReminder = computed(() => {
  const v = displayCheckpoint.value?.can_send_upload_task_reminder;
  return v === undefined || v === null ? true : Boolean(v);
});

/**
 * Post-upload encoding gate — keep the processing UI until cloudflare-video-status
 * reports ready_to_stream (same as CheckpointInspectionInstructions / UploadMicroLearningPopup).
 * Do not use a fixed reveal timer.
 *
 * ER-12337 — these are durable per-checkpoint fields in iqVideoProcessingStore so the
 * processing UI survives editor unmount/remount; `xxx.value` still works via the binding.
 */
const postProposeVideoRevealPending = bindVideoState("revealPending");
const cloudflareStreamReady = bindVideoState("streamReady");
const resolvedPlayableVideoUrl = bindVideoState("playableUrl");
/** UploadVideoWizard (same as create-checkpoint) — not UploadMicroLearningPopup. */
const showUploadWizard = ref(false);
const uploadWizardState = ref("upload");
const uploadWizardFile = ref(null);
const uploadWizardProgress = ref(0);
/** Active Cloudflare TUS upload — aborted when user removes the file mid-progress. */
let activeWizardTusUpload = null;
let activeWizardTusReject = null;
let wizardUploadCancelled = false;
/** Convert inspector_only → microlearning via CreateCheckpointDialog (omit inspector_only). */
const showAddMicrolearningDialog = ref(false);
/** `create` (Add Microlearning) | `propose-changes` (active Propose Changes). */
const createCheckpointDialogMode = ref("create");
const createCheckpointDialogOmitTypes = computed(() =>
  createCheckpointDialogMode.value === "propose-changes" ? [] : ["inspector_only"]
);
const showTaskEmployeeFromAddDialog = ref(false);
const showReassignUploadTaskDialog = ref(false);
const showCancelUploadTaskDialog = ref(false);
const cancelUploadTaskLoading = ref(false);
const sendReminderLoading = ref(false);
const pendingAddMicrolearningPayload = ref(null);
const addMicrolearningSelectedTypeProp = ref("video");
const addMicrolearningCriteriaProp = ref("");
const addMicrolearningPrefixProp = ref("Can");
const addMicrolearningLevelProp = ref({ id: 1, name: "Apprentice", value: "apprentice" });
/**
 * New add/replace video flow phases after wizard TUS:
 * null | 'processing' | 'validating' | 'mismatch' | 'verification-failed'
 *
 * ER-12337 — durable in iqVideoProcessingStore (keyed by checkpoint id).
 */
const videoUploadFlowPhase = bindVideoState("phase");
/**
 * After a fresh context-validation result, prefer this over the persisted mismatch
 * flag until the parent re-fetches (PASS must hide mismatch immediately).
 */
const contextValidationMismatchOverride = bindVideoState("mismatchOverride");
const pendingUploadVideoId = bindVideoState("pendingVideoId");
const pendingUploadThumbnail = bindVideoState("pendingThumbnail");
const contextValidationVideoUrl = bindVideoState("contextVideoUrl");
const contextValidationError = bindVideoState("error");
const contextValidationVerificationError = bindVideoState("verificationError");
const contextValidationIqUserPrompt = computed(() =>
  buildFromVideoIqUserPrompt("Validate whether this video matches the checkpoint context.")
);

const isVideoProcessing = computed(() => {
  if (!isVideo.value) return false;
  // Context validation / mismatch / verification-failed show their own UI — do not treat as encoding.
  if (
    videoUploadFlowPhase.value === "validating" ||
    videoUploadFlowPhase.value === "mismatch" ||
    videoUploadFlowPhase.value === "verification-failed"
  ) {
    return false;
  }
  if (videoUploadFlowPhase.value === "processing") return true;
  if (postProposeVideoRevealPending.value) return true;
  if (!cloudflareStreamReady.value) return true;
  if (resolvedPlayableVideoUrl.value) return false;
  return isProcessingMicrolearningString(displayCheckpoint.value?.microlearning);
});

/**
 * Original dashed processing strip only while encoding.
 * Must stay false during validating / mismatch / verification-failed —
 * otherwise their banners never show.
 */
const showOriginalVideoProcessingUi = computed(
  () =>
    videoUploadFlowPhase.value !== "validating" &&
    videoUploadFlowPhase.value !== "mismatch" &&
    videoUploadFlowPhase.value !== "verification-failed" &&
    (isVideoProcessing.value || videoUploadFlowPhase.value === "processing")
);

/**
 * Mirror CheckpointInspectionInstructions video lanes:
 * - mismatch → context validation FAIL (upload/replace or generate)
 * - generate bar → context validation PASS, no kit yet
 * - menu + tabs → generate microlearning PASS with quiz / inspection / TOC saved
 */
const hasGeneratedVideoMicrolearningKit = computed(() =>
  checkpointHasGeneratedVideoMicrolearningKit(displayCheckpoint.value)
);

const showVideoWithQuizAndInspection = computed(
  () =>
    isVideo.value &&
    !isVideoProcessing.value &&
    !isProposedVideoMicrolearningMismatch.value &&
    hasGeneratedVideoMicrolearningKit.value
);

/** Context validation PASS, video ready, no generated kit — Source Video + Generate Microlearning. */
const showVideoGenerateInspectionOnly = computed(
  () =>
    isVideo.value &&
    !isVideoProcessing.value &&
    !isProposedVideoMicrolearningMismatch.value &&
    !showVideoWithQuizAndInspection.value &&
    !!displayVideoUrl.value
);

/**
 * Resolve the checkpoint's raw playback reference before signed-URL fetching.
 * Active / retired / archived views map `active_microlearning` on `displayCheckpoint`;
 * Proposed Changes on a published active checkpoint must still fall back to `active_microlearning`.
 */
const rawCheckpointMicrolearningUrl = computed(() => {
  const ckpt = props.checkpoint;
  if (!ckpt) return null;

  const fromDisplay = displayCheckpoint.value?.microlearning;
  if (isPopulatedContentValue(fromDisplay) && !isProcessingMicrolearningString(fromDisplay)) {
    return fromDisplay;
  }

  const active = ckpt.active_microlearning;
  if (isPopulatedContentValue(active) && !isProcessingMicrolearningString(active)) {
    return active;
  }

  const proposed = ckpt.microlearning;
  return isPopulatedContentValue(proposed) && !isProcessingMicrolearningString(proposed)
    ? proposed
    : null;
});

/** Fresh signed URL when `microlearning` / `active_microlearning` is a bare Cloudflare uid. */
const resolvedCheckpointSignedVideoUrl = ref(null);

const displayVideoUrl = computed(
  () =>
    resolvedPlayableVideoUrl.value ||
    resolvedCheckpointSignedVideoUrl.value ||
    rawCheckpointMicrolearningUrl.value ||
    null
);

const proposedArticleForMenu = computed(() => displayCheckpoint.value?.proposed_article ?? null);

const proposedArticleHtmlForEditor = computed(() => {
  const raw = displayCheckpoint.value?.proposed_knowledge_article_html;
  if (raw != null && String(raw).trim() !== "") {
    return formatArticleHtml(raw);
  }
  return formatArticleHtml(proposedArticleForMenu.value);
});

const proposedArticleBodyForEditor = computed(() => {
  const body = proposedArticleForMenu.value?.body;
  if (body == null) return null;
  let parsed = null;
  if (typeof body === "object") {
    parsed = body;
  } else if (typeof body === "string") {
    try {
      const json = JSON.parse(body);
      parsed = json && typeof json === "object" ? json : null;
    } catch {
      parsed = null;
    }
  }
  if (!parsed || shouldPreferArticleHtml(parsed)) return null;
  return parsed;
});

const articleMenuEditorKey = computed(() =>
  props.checkpoint?.id != null ? String(props.checkpoint.id) : "checkpoint"
);

function getAccountId() {
  try {
    return JSON.parse(localStorage.getItem("currentAccountID"));
  } catch {
    return null;
  }
}

const checkpointId = computed(() => props.checkpoint?.id ?? null);

const lockStateForHeartbeat = computed(() => resolveLockState(props.checkpoint));

const PEER_UPDATE_TOAST_MS = null;

let checkpointUpdatedToastId = null;
const pendingPeerReload = ref(false);

/**
 * After a successful checkpoint mutation, tell peers via presence SSE.
 * Failures must not roll back or surface as save errors.
 */
async function notifyPeersOfCheckpointUpdate() {
  const accountId = getAccountId();
  const id = checkpointId.value ?? currentCheckPoint.value?.id;
  if (accountId == null || id == null) return;
  try {
    const response = await notifyCheckpointPresenceUpdated(accountId, id);
    if (response?.data?.broadcast === false) {
      try {
        await notifyCheckpointPresenceUpdated(accountId, id);
      } catch {
        // Optional single retry failed — save still succeeded.
      }
    }
  } catch (error) {
    if (!error?.response || error.response.status >= 500) {
      console.error("[CheckpointEditFormV2] checkpoint-updated notify failed:", error);
    }
  }
}

/** Ask parent to re-fetch checkpoint detail after a mutation. */
function handleRefreshCheckpoint() {
  emit("refresh-checkpoint");
}

/** Single-checkpoint GET may nest the entity under `results`, `checkpoint`, or `data`. */
function unwrapCheckpointDetailEntity(raw) {
  let el = raw;
  if (el && typeof el === "object" && !Array.isArray(el)) {
    if (Array.isArray(el.results) && el.results[0]) el = el.results[0];
    else if (el.checkpoint && typeof el.checkpoint === "object") el = el.checkpoint;
    else if (el.data && typeof el.data === "object" && !Array.isArray(el.data)) el = el.data;
  }
  if (Array.isArray(el)) el = el[0];
  return el && typeof el === "object" ? el : null;
}

/** Re-fetch full checkpoint detail so nested proposed_* fields match the detail API shape. */
async function fetchCheckpointDetailAfterMutation() {
  const accountId = getAccountId();
  if (!accountId || props.responsibilityId == null || checkpointId.value == null) return false;
  try {
    const res = await getResponsibilityCheckpointDetailV2(
      accountId,
      props.responsibilityId,
      checkpointId.value
    );
    const el = unwrapCheckpointDetailEntity(res?.data);
    if (!el) {
      handleRefreshCheckpoint();
      return false;
    }
    emit("checkpoint-updated", el);
    return true;
  } catch (err) {
    if (err?.response?.status !== 404) {
      console.error("[CheckpointEditFormV2] refresh checkpoint detail:", err);
    }
    handleRefreshCheckpoint();
    return false;
  }
}

/** Mutation responses are often partial — always re-fetch full checkpoint detail. */
async function emitCheckpointMutationResult() {
  void notifyPeersOfCheckpointUpdate();
  await fetchCheckpointDetailAfterMutation();
}

/** User clicked Reload — parent fetches detail by id; form remounts on fresh props. */
function reloadCheckpointFromRemote() {
  pendingPeerReload.value = true;
  emit("reload-checkpoint-detail");
}

/**
 * Peer saved this checkpoint.
 * Never auto-overwrite. Sticky toast with Reload until dismiss or reload.
 */
function handlePeerCheckpointUpdated(data) {
  if (!data || typeof data !== "object") return;
  const activeId = checkpointId.value;
  if (activeId != null && String(data.checkpoint_id) !== String(activeId)) return;

  const { accessId } = getCurrentUserIds();
  if (accessId != null && String(data.updated_by?.access_id) === String(accessId)) return;

  const name = data.updated_by?.display_name?.trim() || "Another user";
  if (checkpointUpdatedToastId != null) {
    notificationStore.removeToast(checkpointUpdatedToastId);
    checkpointUpdatedToastId = null;
  }
  checkpointUpdatedToastId = notificationStore.showConfirm(
    `${name} updated this checkpoint.`,
    () => {
      checkpointUpdatedToastId = null;
      reloadCheckpointFromRemote();
    },
    () => {
      checkpointUpdatedToastId = null;
    },
    "Reload to see the latest changes, or dismiss to keep viewing your current version.",
    "Reload",
    PEER_UPDATE_TOAST_MS
  );
}

/**
 * Live viewers + edit lock via presence SSE.
 * While we hold the lock, heartbeat uses heartbeat-edit-lock (also refreshes presence TTL).
 * SSE lock_changed is emitted to the parent so editingCheckpoint stays the source of truth.
 */
const { presenceAvatars, editLock } = useCheckpointPresence({
  accountId: () => getAccountId(),
  checkpointId: () => checkpointId.value,
  enabled: () => checkpointId.value != null,
  holdsEditLock: () => lockStateForHeartbeat.value === "lockedBySelf",
  onCheckpointUpdated: handlePeerCheckpointUpdated,
  onPublishMessageReady: () => void loadCurrentVersionMeta(),
});

watch(
  () => props.checkpoint,
  () => {
    if (!pendingPeerReload.value) return;
    pendingPeerReload.value = false;
    snapshotCriteriaBaseline(props.checkpoint);
    criteriaFormEpoch.value += 1;
  },
  { deep: true }
);

watch(
  editLock,
  (lock) => {
    if (lock == null) return;
    emit("lock-changed", lock);
  },
  { deep: true }
);

async function fetchPlayableSignedUrl(payload = {}) {
  const accountId = getAccountId();
  if (!accountId || props.responsibilityId == null || checkpointId.value == null) {
    return isPlayableSignedVideoUrl(payload?.signedUrl) ? payload.signedUrl : null;
  }
  try {
    const response = await getCheckpointVideoSignedUrl(
      accountId,
      props.responsibilityId,
      checkpointId.value
    );
    const url = response?.data?.url ?? payload?.signedUrl ?? null;
    return isPlayableSignedVideoUrl(url) ? url : null;
  } catch {
    return isPlayableSignedVideoUrl(payload?.signedUrl) ? payload.signedUrl : null;
  }
}

watch(
  () => [isVideo.value, rawCheckpointMicrolearningUrl.value, checkpointId.value],
  async ([video, rawUrl]) => {
    if (!video || !rawUrl) {
      resolvedCheckpointSignedVideoUrl.value = null;
      return;
    }
    if (!needsSignedVideoUrlResolution(rawUrl, isCloudflareSignedUrl)) {
      resolvedCheckpointSignedVideoUrl.value = null;
      return;
    }
    resolvedCheckpointSignedVideoUrl.value = await fetchPlayableSignedUrl();
  },
  { immediate: true }
);

/**
 * Keep processing UI until cloudflare-video-status says ready_to_stream, then resolve the
 * signed URL and show the player — no fixed min/max reveal timer.
 *
 * ER-12337 — the actual runner lives in iqVideoProcessingStore keyed by checkpoint id, so
 * navigating away does not cancel encoding/reveal; this is a thin adapter for call sites.
 */
function runVideoPostUploadReveal(payload = {}, { showToast = true } = {}) {
  if (!isVideo.value) return undefined;
  return iqVideoProcessingStore.reveal(buildVideoProcessingContext(), payload, { showToast });
}

/** UploadVideoWizard finished TUS — show processing, then IQ context validation before confirm. */
function resetUploadWizard() {
  uploadWizardState.value = "upload";
  uploadWizardFile.value = null;
  uploadWizardProgress.value = 0;
}

const addMicrolearningCheckpointDescription = computed(() => {
  const payload = pendingAddMicrolearningPayload.value;
  if (payload) {
    return [payload.prefix, payload.criteria].filter(Boolean).join(" ").trim();
  }
  return [prefixDisplay.value, props.checkpoint?.criteria].filter(Boolean).join(" ").trim();
});

function syncAddMicrolearningDialogPropsFromCheckpoint() {
  addMicrolearningCriteriaProp.value = props.checkpoint?.criteria || "";
  addMicrolearningPrefixProp.value = prefixDisplay.value || "Can";
  addMicrolearningLevelProp.value = readinessLevel.value || {
    id: 1,
    name: "Apprentice",
    value: "apprentice",
  };
  addMicrolearningSelectedTypeProp.value = "video";
}

function syncAddMicrolearningDialogPropsFromPayload(payload) {
  if (!payload) return;
  addMicrolearningCriteriaProp.value = payload.criteria || "";
  addMicrolearningPrefixProp.value = payload.prefix || "Can";
  addMicrolearningLevelProp.value = payload.level || {
    id: 1,
    name: "Apprentice",
    value: "apprentice",
  };
  addMicrolearningSelectedTypeProp.value = payload.type || "video";
}

function handleAddMicrolearningClick() {
  if (!canEditCheckpointContent.value) return;
  emit("add-microlearning");
  createCheckpointDialogMode.value = "create";
  syncAddMicrolearningDialogPropsFromCheckpoint();
  pendingAddMicrolearningPayload.value = null;
  showTaskEmployeeFromAddDialog.value = false;
  showAddMicrolearningDialog.value = true;
}

function openProposeChangesDialog() {
  createCheckpointDialogMode.value = "propose-changes";
  syncAddMicrolearningDialogPropsFromCheckpoint();
  addMicrolearningSelectedTypeProp.value = "copy_active";
  pendingAddMicrolearningPayload.value = null;
  showTaskEmployeeFromAddDialog.value = false;
  showAddMicrolearningDialog.value = true;
}

function handleAddMicrolearningDialogCancel() {
  showAddMicrolearningDialog.value = false;
  pendingAddMicrolearningPayload.value = null;
  createCheckpointDialogMode.value = "create";
}

async function applyAddMicrolearningTypeUpdate(payload) {
  const accountId = getAccountId();
  if (
    !accountId ||
    props.responsibilityId == null ||
    checkpointId.value == null ||
    !payload?.type
  ) {
    return false;
  }

  const lockCheckpoint = payload.type !== "task_an_employee";
  try {
    const response = await changeCheckpointV2(
      accountId,
      props.responsibilityId,
      checkpointId.value,
      {
        criteria: payload.criteria || "",
        prefix: formatCriteriaPrefix(payload.prefix || "Can"),
        instructions: props.checkpoint?.instructions || "<p></p>",
        proficiency_level: payload.level?.id ?? readinessLevel.value?.id ?? 1,
        status: props.checkpoint?.status || "draft",
        proposed_microlearning_type: payload.type,
        lock_checkpoint: lockCheckpoint,
        account: accountId,
        responsibility: props.responsibilityId,
      }
    );
    if (response?.data) {
      void notifyPeersOfCheckpointUpdate();
      emit("checkpoint-updated", response.data);
    } else {
      void notifyPeersOfCheckpointUpdate();
      emit("refresh-checkpoint");
    }
    emit("checkpoint-count-changed");
    return true;
  } catch (error) {
    console.error("[CheckpointEditFormV2] Error converting microlearning type:", error);
    notificationStore.error(
      error?.response?.data?.detail ??
        error?.response?.data?.message ??
        "Could not update checkpoint type. Please try again."
    );
    return false;
  }
}

async function handleAddMicrolearningDialogNext(payload) {
  if (!payload?.type) return;

  // Propose Changes: copy active microlearning into proposed_* via API.
  if (createCheckpointDialogMode.value === "propose-changes" && payload.type === "copy_active") {
    const copied = await applyCopyActiveProposeChanges();
    if (!copied) return;
    showAddMicrolearningDialog.value = false;
    pendingAddMicrolearningPayload.value = null;
    createCheckpointDialogMode.value = "create";
    editBarSelectedTab.value = "proposed-changes";
    emit("lock-to-edit");
    return;
  }

  pendingAddMicrolearningPayload.value = payload;
  syncAddMicrolearningDialogPropsFromPayload(payload);

  if (payload.type === "task_an_employee") {
    showAddMicrolearningDialog.value = false;
    showTaskEmployeeFromAddDialog.value = true;
    return;
  }

  if (payload.type === "video") {
    showAddMicrolearningDialog.value = false;
    resetUploadWizard();
    showUploadWizard.value = true;
    return;
  }

  // article / inspector_only (and any other non-wizard type): update type immediately.
  const updated = await applyAddMicrolearningTypeUpdate(payload);
  if (updated) {
    showAddMicrolearningDialog.value = false;
    pendingAddMicrolearningPayload.value = null;
    if (createCheckpointDialogMode.value === "propose-changes") {
      editBarSelectedTab.value = "proposed-changes";
    }
    createCheckpointDialogMode.value = "create";
  }
}

async function applyCopyActiveProposeChanges() {
  const accountId = getAccountId();
  if (!accountId || props.responsibilityId == null || checkpointId.value == null) {
    notificationStore.error("Missing account or checkpoint information.");
    return false;
  }

  proxy?.$loader?.setAppLoading?.(true);
  try {
    const response = await proposeChangeStartingFromCopy(
      accountId,
      props.responsibilityId,
      checkpointId.value
    );
    if (response?.data) {
      void notifyPeersOfCheckpointUpdate();
      emit("checkpoint-updated", response.data);
    } else {
      void notifyPeersOfCheckpointUpdate();
      emit("refresh-checkpoint");
    }
    emit("checkpoint-count-changed");
    return true;
  } catch (error) {
    console.error("[CheckpointEditFormV2] Error copying active microlearning:", error);
    notificationStore.error(
      error?.response?.data?.detail ??
        error?.response?.data?.message ??
        "Could not copy active content. Please try again."
    );
    return false;
  } finally {
    proxy?.$loader?.setAppLoading?.(false);
  }
}

function formatUploadTaskDeadline(deadline) {
  if (typeof deadline === "string" && /^\d{4}-\d{2}-\d{2}$/.test(deadline)) {
    return deadline;
  }
  if (deadline instanceof Date && !Number.isNaN(deadline.getTime())) {
    return deadline.toISOString().slice(0, 10);
  }
  if (deadline?.toISOString) {
    return deadline.toISOString().slice(0, 10);
  }
  const d = new Date(deadline);
  if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  return null;
}

function handleTaskEmployeeDialogBack() {
  if (showReassignUploadTaskDialog.value) {
    showReassignUploadTaskDialog.value = false;
    return;
  }
  showTaskEmployeeFromAddDialog.value = false;
  showAddMicrolearningDialog.value = true;
}

async function handleTaskEmployeeFromAddAssign(assignPayload) {
  if (!pendingAddMicrolearningPayload.value || !assignPayload?.employee) return;

  const deadlineStr = formatUploadTaskDeadline(assignPayload.deadline);
  if (!deadlineStr) return;

  const notify =
    assignPayload.employee.access === true ? assignPayload.notifyEmployee === true : false;
  const accountId = getAccountId();
  if (!accountId || props.responsibilityId == null || checkpointId.value == null) return;

  proxy?.$loader?.setAppLoading?.(true);
  try {
    const updated = await applyAddMicrolearningTypeUpdate(pendingAddMicrolearningPayload.value);
    if (!updated) return;

    await assignVideoUploadTask(accountId, props.responsibilityId, checkpointId.value, {
      assignee_id: assignPayload.employee.id,
      deadline: deadlineStr,
      notify,
    });

    showTaskEmployeeFromAddDialog.value = false;
    pendingAddMicrolearningPayload.value = null;
    notificationStore.success("Task assigned successfully");
    emit("refresh-checkpoint");
  } catch (error) {
    console.error("[CheckpointEditFormV2] Error assigning video upload task:", error);
    notificationStore.error(
      error?.response?.data?.message ?? error?.response?.data?.detail ?? "Failed to assign task."
    );
  } finally {
    proxy?.$loader?.setAppLoading?.(false);
  }
}

async function handleTaskEmployeeReassign(assignPayload) {
  if (!assignPayload?.employee) return;

  const accountId = getAccountId();
  if (!accountId || props.responsibilityId == null || checkpointId.value == null) return;

  const notify =
    assignPayload.employee.access === true ? assignPayload.notifyEmployee === true : false;
  const payload = {
    assignee_id: assignPayload.employee.id,
    notify,
  };

  // Deadline: omit to leave unchanged; include YYYY-MM-DD to update; null/"" to clear.
  const deadlineStr = formatUploadTaskDeadline(assignPayload.deadline);
  const initialDeadlineStr = formatUploadTaskDeadline(reassignInitialDeadline.value);
  if (deadlineStr !== initialDeadlineStr) {
    payload.deadline = deadlineStr;
  }

  proxy?.$loader?.setAppLoading?.(true);
  try {
    await reassignVideoUploadTask(accountId, props.responsibilityId, checkpointId.value, payload);
    showReassignUploadTaskDialog.value = false;
    notificationStore.success("Task reassigned successfully");
    emit("pending-video-task-reassign", { checkpoint: props.checkpoint });
    emit("refresh-checkpoint");
  } catch (error) {
    console.error("[CheckpointEditFormV2] Error reassigning video upload task:", error);
    const detail = error?.response?.data?.detail ?? error?.response?.data?.message;
    notificationStore.error(detail ?? "Failed to reassign task.");
    // Terminal task — refresh so UI drops reassign affordance.
    if (
      typeof detail === "string" &&
      detail.toLowerCase().includes("cannot reassign a completed or canceled")
    ) {
      emit("refresh-checkpoint");
    }
  } finally {
    proxy?.$loader?.setAppLoading?.(false);
  }
}

async function handleTaskEmployeeDialogAction(assignPayload) {
  if (showReassignUploadTaskDialog.value || assignPayload?.mode === "reassign") {
    await handleTaskEmployeeReassign(assignPayload);
    return;
  }
  await handleTaskEmployeeFromAddAssign(assignPayload);
}

function handlePendingVideoTaskCancelClick() {
  if (lockState.value !== "lockedBySelf") return;
  showCancelUploadTaskDialog.value = true;
}

async function handleCancelUploadTaskConfirm() {
  const accountId = getAccountId();
  if (!accountId || props.responsibilityId == null || checkpointId.value == null) return;

  cancelUploadTaskLoading.value = true;
  proxy?.$loader?.setAppLoading?.(true);
  try {
    const response = await cancelUploadTask(accountId, props.responsibilityId, checkpointId.value);
    showCancelUploadTaskDialog.value = false;
    notificationStore.success("Upload task cancelled");
    emit("pending-video-task-cancel", { checkpoint: props.checkpoint });
    // BE returns updated checkpoint (draft, inspector-only, no upload task).
    if (response?.data) {
      emit("checkpoint-updated", response.data);
    } else {
      emit("refresh-checkpoint");
    }
  } catch (error) {
    console.error("[CheckpointEditFormV2] Error canceling upload task:", error);
    const detail = error?.response?.data?.detail ?? error?.response?.data?.message;
    const status = error?.response?.status;

    if (status === 403 || detail === "CHECKPOINT_LOCK_NOT_HELD") {
      showCancelUploadTaskDialog.value = false;
      notificationStore.error(
        "You must hold the edit lock to cancel this upload task. Acquire or take over the lock, then try again."
      );
    } else if (detail === "NO_ACTIVE_UPLOAD_TASK") {
      // Task already gone — close confirm and refresh to inspector-only UI.
      showCancelUploadTaskDialog.value = false;
      emit("refresh-checkpoint");
    } else {
      notificationStore.error(detail ?? "Failed to cancel upload task.");
    }
  } finally {
    cancelUploadTaskLoading.value = false;
    proxy?.$loader?.setAppLoading?.(false);
  }
}

function handlePendingVideoTaskReassignClick() {
  if (lockState.value !== "lockedBySelf") return;
  showReassignUploadTaskDialog.value = true;
}

async function handlePendingVideoTaskSendReminder() {
  const accountId = getAccountId();
  if (!accountId || props.responsibilityId == null || checkpointId.value == null) return;
  if (sendReminderLoading.value) return;

  sendReminderLoading.value = true;
  try {
    await sendUploadTaskReminder(accountId, props.responsibilityId, checkpointId.value);
    notificationStore.success("Reminder sent successfully");
    emit("pending-video-task-send-reminder", { checkpoint: props.checkpoint });
  } catch (error) {
    console.error("[CheckpointEditFormV2] Error sending upload task reminder:", error);
    const detail = error?.response?.data?.detail ?? error?.response?.data?.message;

    if (detail === "ASSIGNEE_EMAIL_MISSING") {
      notificationStore.error(
        "This employee has no access to the Employee Portal. Please grant him/her access first and try again."
      );
    } else if (detail === "NO_ACTIVE_UPLOAD_TASK" || detail === "UPLOAD_TASK_NOT_ACTIVE") {
      // Task is gone or terminal — refresh so the banner reflects the real state.
      notificationStore.error("This upload task is no longer active.");
      emit("refresh-checkpoint");
    } else {
      notificationStore.error(detail ?? "Failed to send reminder.");
    }
  } finally {
    sendReminderLoading.value = false;
  }
}

const reassignInitialEmployee = computed(() => {
  const assigned = props.checkpoint?.assigned_to;
  if (!assigned) return null;
  const firstName =
    assigned.firstName || assigned.first_name || splitDisplayName(assigned.name).first || "";
  const lastName =
    assigned.lastName || assigned.last_name || splitDisplayName(assigned.name).last || "";
  return {
    id: assigned.id,
    firstName,
    lastName,
    fullName: assigned.name || `${firstName} ${lastName}`.trim(),
    name: assigned.name || `${firstName} ${lastName}`.trim(),
    avatar: assigned.avatar ?? null,
    access: true,
    title: assigned.title || assigned.primary_position?.name || "",
  };
});

const reassignInitialDeadline = computed(() => props.checkpoint?.deadline ?? null);

function openUploadVideoWizard() {
  if (!canEditCheckpointContent.value) return;
  resetUploadWizard();
  showUploadWizard.value = true;
}

function onUploadWizardInput(open) {
  if (uploadWizardState.value === "progress" && !open) {
    showUploadWizard.value = true;
    return;
  }
  showUploadWizard.value = open;
  if (!open) {
    resetUploadWizard();
    if (pendingAddMicrolearningPayload.value?.type === "video") {
      // Closing via v-model without go-back cancels convert flow.
      pendingAddMicrolearningPayload.value = null;
    }
  }
}

function handleUploadWizardGoBack() {
  if (uploadWizardState.value === "progress") {
    showUploadWizard.value = true;
    return;
  }
  showUploadWizard.value = false;
  resetUploadWizard();
  if (pendingAddMicrolearningPayload.value?.type === "video") {
    showAddMicrolearningDialog.value = true;
  }
}

function handleUploadWizardClose() {
  if (uploadWizardState.value === "progress") {
    showUploadWizard.value = true;
    return;
  }
  showUploadWizard.value = false;
  resetUploadWizard();
  pendingAddMicrolearningPayload.value = null;
}

function handleUploadWizardSelect({ file, tooLong }) {
  if (tooLong) {
    uploadWizardState.value = "too-long";
    uploadWizardFile.value = file || null;
    return;
  }
  uploadWizardFile.value = file || null;
  uploadWizardState.value = file ? "selected" : "upload";
}

function abortActiveWizardTusUpload() {
  wizardUploadCancelled = true;
  const upload = activeWizardTusUpload;
  activeWizardTusUpload = null;
  if (upload) {
    try {
      upload.abort();
    } catch (e) {
      // ignore abort errors
    }
  }
  if (activeWizardTusReject) {
    activeWizardTusReject(Object.assign(new Error("Upload cancelled"), { cancelled: true }));
    activeWizardTusReject = null;
  }
}

function handleUploadWizardRemove() {
  // Stop in-flight TUS; keep wizard open so the user can pick another video.
  abortActiveWizardTusUpload();
  uploadWizardFile.value = null;
  uploadWizardState.value = "upload";
  uploadWizardProgress.value = 0;
}

function generateVideoThumbnail(file) {
  return new Promise((resolve) => {
    const video = document.createElement("video");
    const url = URL.createObjectURL(file);
    video.src = url;
    video.muted = true;
    video.playsInline = true;
    video.currentTime = 1;
    video.addEventListener("seeked", () => {
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth || 640;
      canvas.height = video.videoHeight || 360;
      canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.85);
    });
    video.addEventListener("error", () => {
      URL.revokeObjectURL(url);
      resolve(null);
    });
    video.load();
  });
}

/**
 * TUS to Cloudflare only — confirm-proposed-video waits until video_to_components returns.
 * Mirrors EditCheckpointsTab.uploadCheckpointVideoViaCloudflare upload half.
 */
async function uploadVideoFileViaCloudflareTus(file) {
  const accountId = getAccountId();
  if (!accountId || checkpointId.value == null) {
    throw new Error("Missing account or checkpoint for video upload");
  }

  const setProgress = (n) => {
    uploadWizardProgress.value = Math.max(0, Math.min(100, Math.round(Number(n) || 0)));
  };

  setProgress(0);
  const thumbnail = await generateVideoThumbnail(file);
  if (wizardUploadCancelled) {
    throw Object.assign(new Error("Upload cancelled"), { cancelled: true });
  }

  const urlRes = await generateVideoUploadUrl(accountId, checkpointId.value, file.size);
  if (wizardUploadCancelled) {
    throw Object.assign(new Error("Upload cancelled"), { cancelled: true });
  }
  const uploadUrl = urlRes.data?.upload_url;
  const videoId = urlRes.data?.video_id;
  if (!uploadUrl || !videoId) {
    throw new Error("Missing Cloudflare upload URL or video id");
  }

  await new Promise((resolve, reject) => {
    let settled = false;
    const safeResolve = () => {
      if (settled) return;
      settled = true;
      activeWizardTusReject = null;
      activeWizardTusUpload = null;
      resolve();
    };
    const safeReject = (err) => {
      if (settled) return;
      settled = true;
      activeWizardTusReject = null;
      activeWizardTusUpload = null;
      reject(err);
    };

    const upload = new tus.Upload(file, {
      uploadUrl,
      chunkSize: 256 * 1024 * 20,
      storeFingerprintForResuming: false,
      retryDelays: [0, 3000, 5000, 10000],
      onProgress: (bytesUploaded, bytesTotal) => {
        if (wizardUploadCancelled) return;
        setProgress(Math.round((bytesUploaded / bytesTotal) * 90));
      },
      onSuccess: () => {
        if (wizardUploadCancelled) {
          safeReject(Object.assign(new Error("Upload cancelled"), { cancelled: true }));
          return;
        }
        safeResolve();
      },
      onError: (err) => {
        if (wizardUploadCancelled) {
          safeReject(Object.assign(err || new Error("Upload cancelled"), { cancelled: true }));
          return;
        }
        safeReject(err);
      },
    });
    activeWizardTusUpload = upload;
    activeWizardTusReject = safeReject;
    upload.start();
  });

  if (wizardUploadCancelled) {
    throw Object.assign(new Error("Upload cancelled"), { cancelled: true });
  }

  setProgress(100);
  return { videoId, thumbnail };
}

async function handleUploadWizardSubmit(file) {
  if (!file || uploadWizardState.value === "progress") return;

  wizardUploadCancelled = false;
  uploadWizardFile.value = file;
  uploadWizardState.value = "progress";
  uploadWizardProgress.value = 0;

  try {
    // Convert inspector_only → video: update type before TUS (mirrors create-then-upload).
    if (pendingAddMicrolearningPayload.value?.type === "video") {
      const updated = await applyAddMicrolearningTypeUpdate(pendingAddMicrolearningPayload.value);
      if (wizardUploadCancelled) return;
      if (!updated) {
        uploadWizardState.value = "error";
        uploadWizardProgress.value = 0;
        return;
      }
      pendingAddMicrolearningPayload.value = null;
      await nextTick();
    }

    const { videoId, thumbnail } = await uploadVideoFileViaCloudflareTus(file);
    if (wizardUploadCancelled) return;
    showUploadWizard.value = false;
    resetUploadWizard();
    notificationStore.info(VIDEO_POST_UPLOAD_TOAST_TITLE, VIDEO_POST_UPLOAD_TOAST_MESSAGE, 8000);
    // After TUS: confirm-proposed-video → status → signed URL → video_to_components.
    // ER-12337 — runs in iqVideoProcessingStore so navigation does not abort it.
    await iqVideoProcessingStore.startPostUploadValidation(buildVideoProcessingContext(), {
      videoId,
      thumbnail,
    });
  } catch (err) {
    if (wizardUploadCancelled || err?.cancelled) {
      // User removed the file mid-upload — stay on dropzone; do not close or show error.
      return;
    }
    console.error("[CheckpointEditFormV2] UploadVideoWizard upload failed:", err);
    uploadWizardState.value = "error";
    uploadWizardProgress.value = 0;
    notificationStore.error("Upload failed. Please try again.");
  }
}

/**
 * Create-checkpoint UploadVideoWizard path: parent deferred confirm and passed pending payload.
 * ER-12337 — the runner lives in iqVideoProcessingStore keyed by checkpoint id, so the
 * processing/validation workflow survives the editor unmounting on navigation.
 */
function applyPendingVideoConfirm(pending) {
  if (!pending?.videoId) return;
  if (String(pending.checkpointId) !== String(checkpointId.value)) return;
  const record = iqVideoProcessingStore.get(checkpointId.value);
  if (record.pendingVideoId && String(record.pendingVideoId) === String(pending.videoId)) {
    return;
  }
  void iqVideoProcessingStore.startPostUploadValidation(buildVideoProcessingContext(), {
    videoId: pending.videoId,
    thumbnail: pending.thumbnail ?? null,
  });
}

watch(() => props.pendingVideoConfirm, applyPendingVideoConfirm);

async function handleVideoUrlReadyFromPopup(payload) {
  showUploadWizard.value = false;
  await runVideoPostUploadReveal(payload, { showToast: false });
}

function applyVideoNavigationState([video, , rawUrl]) {
  if (!video) {
    // Not a video checkpoint any more — drop any (stale) workflow for this checkpoint.
    iqVideoProcessingStore.reset(checkpointId.value);
    return;
  }
  if (resolvedPlayableVideoUrl.value) return;
  // UploadVideoWizard path: status → signed URL → video_to_components → confirm (do not reveal yet).
  if (props.pendingVideoConfirm?.videoId || videoUploadFlowPhase.value) return;
  // A persisted processing placeholder (fresh page load / navigating back in) must resume the
  // Cloudflare encoding poll. `rawCheckpointMicrolearningUrl` deliberately filters processing
  // placeholders out, so read the displayed value directly to catch that state.
  const displayedMicrolearning = displayCheckpoint.value?.microlearning;
  if (
    isProcessingMicrolearningString(displayedMicrolearning) ||
    isProcessingMicrolearningString(rawUrl)
  ) {
    void runVideoPostUploadReveal();
  }
}

watch(
  () => [isVideo.value, props.checkpoint?.id, rawCheckpointMicrolearningUrl.value],
  applyVideoNavigationState
);

/**
 * Initial run of the two flows above is deferred to `onMounted` (not `{ immediate: true }`)
 * because the context they build reads `prefixDisplay` / `videoPassChapterVideoUrl`, which
 * are declared later in setup.
 */
onMounted(() => {
  applyPendingVideoConfirm(props.pendingVideoConfirm);
  applyVideoNavigationState([
    isVideo.value,
    props.checkpoint?.id,
    rawCheckpointMicrolearningUrl.value,
  ]);
});


watch(
  () => props.checkpoint?.id,
  () => {
    // ER-12337 — do not clobber a background workflow's verdict on remount of the SAME
    // checkpoint (only reset when there is nothing in flight/unresolved for it).
    if (!iqVideoProcessingStore.hasActivity(checkpointId.value)) {
      contextValidationMismatchOverride.value = null;
      contextValidationError.value = "";
      contextValidationVerificationError.value = "";
    }
    snapshotCriteriaBaseline();
  },
  { immediate: true }
);

watch(
  () => [
    props.checkpoint?.proposed_video_microlearning_is_mismatch,
    props.checkpoint?.proposed_instructions,
  ],
  () => {
    if (
      videoUploadFlowPhase.value === "validating" ||
      videoUploadFlowPhase.value === "processing"
    ) {
      return;
    }
    if (isVideoContextMismatchCheckpoint(props.checkpoint)) {
      const persistedError = resolvePersistedVideoMismatchError(
        props.checkpoint?.proposed_instructions
      );
      if (persistedError && !contextValidationError.value) {
        contextValidationError.value = persistedError;
      }
    }
  },
  { immediate: true, deep: true }
);

const isCriteriaDirty = computed(() => {
  const currentCriteria = props.checkpoint?.criteria || "";
  const currentPrefix = normalizePrefixForApi(props.checkpoint?.prefix);
  const currentPrepNote = props.checkpoint?.employee_prep_note ?? "";
  return (
    currentCriteria !== savedCriteria.value ||
    currentPrefix !== savedPrefix.value ||
    currentPrepNote !== savedPrepNote.value
  );
});

function safeParseJson(value) {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function normalizeCompetenceCategory(category) {
  const normalized = (category || "ability").toLowerCase();
  return normalized === "behavior" ? "behaviour" : normalized;
}

function mapInstructionStep(step = {}) {
  const seqRaw = step.step_sequence ?? step.step_no;
  const step_sequence =
    seqRaw != null && seqRaw !== "" && Number.isFinite(Number(seqRaw)) ? Number(seqRaw) : null;
  return {
    // IQ video-components / suggest paths persist the canonical Content-model
    // shape (step_type + what_to_look_for), while this editor works in the IAAD
    // shape. Mirror what_to_look_for into both prose fields (ER-12476).
    competence_category: normalizeCompetenceCategory(
      step.competence_category || step.competency_category || step.step_type
    ),
    inspector_action:
      step.inspector_action ||
      step.action ||
      step.chapter_title ||
      step.what_to_look_for ||
      step.look_for ||
      "",
    look_for:
      step.look_for ||
      step.chapter_description ||
      step.what_to_look_for ||
      step.inspector_action ||
      "",
    pass_criteria: step.pass_criteria ?? step.chapter_summary ?? "",
    fail_criteria: step.fail_criteria ?? "",
    timestamp_ref:
      step.timestamp_ref ??
      (step.start_time && step.end_time ? `${step.start_time} - ${step.end_time}` : ""),
    video_jump_url: step.video_jump_url ?? "",
    step_sequence,
  };
}

function extractInspectionProtocolStepsFromPayload(p) {
  if (!p || typeof p !== "object") return [];
  // Prefer the longest non-empty list so AI flat `{steps}` is not hidden by a
  // parallel empty/partial `inspection_instructions.steps` from older add-step.
  const candidates = [
    p.steps_to_inspect,
    p.instructions?.steps,
    p.instructions?.instructions?.steps,
    p.inspection_instructions?.instructions?.steps,
    p.inspection_instructions?.steps,
    p.steps,
  ].filter((list) => Array.isArray(list) && list.length);
  if (!candidates.length) return [];
  return candidates.reduce((best, list) => (list.length > best.length ? list : best));
}

function mergeProposedInspectionProtocolSnapshot(prev, stepsMapped, confirmEveryStep) {
  const next =
    prev && typeof prev === "object" && !Array.isArray(prev)
      ? JSON.parse(JSON.stringify(prev))
      : {};
  next.requires_confirmation_at_every_step = !!confirmEveryStep;
  const stepsOut = stepsMapped.map((s) => {
    const row = {
      competence_category: s.competence_category,
      inspector_action: s.inspector_action ?? "",
      look_for: s.look_for ?? "",
      pass_criteria: s.pass_criteria ?? "",
      fail_criteria: s.fail_criteria ?? "",
      timestamp_ref: s.timestamp_ref ?? "",
      video_jump_url: s.video_jump_url ?? "",
    };
    if (s.step_sequence != null && Number.isFinite(Number(s.step_sequence))) {
      row.step_sequence = Number(s.step_sequence);
    }
    return row;
  });

  // Write into the longest existing steps list (same preference as extract),
  // so AI flat `{steps}` and nested shapes stay in place on edit/delete.
  const locations = [
    {
      len: Array.isArray(next.steps_to_inspect) ? next.steps_to_inspect.length : 0,
      set: () => {
        next.steps_to_inspect = stepsOut;
      },
    },
    {
      len: Array.isArray(next.steps) ? next.steps.length : 0,
      set: () => {
        next.steps = stepsOut;
      },
    },
    {
      len:
        next.inspection_instructions &&
        typeof next.inspection_instructions === "object" &&
        Array.isArray(next.inspection_instructions.steps)
          ? next.inspection_instructions.steps.length
          : 0,
      set: () => {
        const ii =
          next.inspection_instructions && typeof next.inspection_instructions === "object"
            ? next.inspection_instructions
            : {};
        next.inspection_instructions = { ...ii, steps: stepsOut };
      },
    },
    {
      len:
        next.inspection_instructions?.instructions &&
        typeof next.inspection_instructions.instructions === "object" &&
        Array.isArray(next.inspection_instructions.instructions.steps)
          ? next.inspection_instructions.instructions.steps.length
          : 0,
      set: () => {
        const ii =
          next.inspection_instructions && typeof next.inspection_instructions === "object"
            ? next.inspection_instructions
            : {};
        const inst = ii.instructions && typeof ii.instructions === "object" ? ii.instructions : {};
        next.inspection_instructions = {
          ...ii,
          instructions: { ...inst, steps: stepsOut },
        };
      },
    },
    {
      len:
        next.instructions &&
        typeof next.instructions === "object" &&
        Array.isArray(next.instructions.steps)
          ? next.instructions.steps.length
          : 0,
      set: () => {
        const inst =
          next.instructions && typeof next.instructions === "object" ? next.instructions : {};
        next.instructions = { ...inst, steps: stepsOut };
      },
    },
  ];
  const best = locations.reduce((acc, loc) => (loc.len > (acc?.len ?? 0) ? loc : acc), null);
  if (best && best.len > 0) {
    best.set();
    return next;
  }

  if (Array.isArray(next.steps_to_inspect) || "steps_to_inspect" in next) {
    next.steps_to_inspect = stepsOut;
    return next;
  }
  if (Array.isArray(next.steps) || "steps" in next) {
    next.steps = stepsOut;
    return next;
  }
  if (next.inspection_instructions != null && typeof next.inspection_instructions === "object") {
    const ii = next.inspection_instructions;
    next.inspection_instructions = { ...ii, steps: stepsOut };
    return next;
  }
  if (next.instructions != null && typeof next.instructions === "object") {
    next.instructions = { ...next.instructions, steps: stepsOut };
    return next;
  }
  next.steps = stepsOut;
  return next;
}

const proposedQuizParsed = computed(() => {
  const raw = displayCheckpoint.value?.proposed_quiz ?? null;
  if (raw == null || raw === "") return null;
  return safeParseJson(raw);
});

const proposedQuizPayload = computed(() => {
  const quiz = proposedQuizParsed.value;
  if (!quiz || typeof quiz !== "object" || Array.isArray(quiz)) return null;
  if (Array.isArray(quiz.questions)) return quiz;
  if (quiz.quiz && typeof quiz.quiz === "object" && Array.isArray(quiz.quiz.questions)) {
    return quiz.quiz;
  }
  return null;
});

const proposedQuizQuestions = computed(() => {
  const quiz = proposedQuizParsed.value;
  if (Array.isArray(quiz)) return quiz;
  if (!quiz || typeof quiz !== "object") return [];
  if (Array.isArray(quiz.questions)) return quiz.questions;
  if (Array.isArray(quiz.items)) return quiz.items;
  if (Array.isArray(quiz.quiz?.questions)) return quiz.quiz.questions;
  if (Array.isArray(quiz.quiz?.items)) return quiz.quiz.items;
  return [];
});

const hasProposedQuiz = computed(() => proposedQuizQuestions.value.length > 0);

const proposedQuizIsAutopass = computed(() =>
  Boolean(displayCheckpoint.value?.proposed_quiz_is_autopass)
);

const QUIZ_MODE_VALUES = ["BOTH_REQUIRED", "SKIP_INSPECTION", "SKIP_QUIZ"];

/** Active tab / retired / archived / past version → active_quiz_mode; Proposed tab → quiz_mode. */
const viewUsesActiveQuizMode = computed(() => {
  if (props.pastVersionView || isRetiredCheckpoint.value || isArchivedCheckpoint.value) {
    return true;
  }
  return isActiveCheckpoint.value && !isViewingProposedChanges.value;
});

function resolveActiveQuizMode(ckpt) {
  if (!ckpt) return "BOTH_REQUIRED";
  const activeMode = ckpt.active_quiz_mode;
  if (QUIZ_MODE_VALUES.includes(activeMode)) return activeMode;
  if (Boolean(ckpt.active_quiz_autopass)) return "SKIP_INSPECTION";
  return "BOTH_REQUIRED";
}

function resolveProposedQuizMode(ckpt) {
  if (!ckpt) return "BOTH_REQUIRED";
  const mode = ckpt.quiz_mode;
  if (QUIZ_MODE_VALUES.includes(mode)) return mode;
  if (Boolean(ckpt.proposed_quiz_is_autopass)) return "SKIP_INSPECTION";
  const activeMode = ckpt.active_quiz_mode;
  if (QUIZ_MODE_VALUES.includes(activeMode)) return activeMode;
  return "BOTH_REQUIRED";
}

const resolvedQuizMode = computed(() => {
  const ckpt = props.checkpoint;
  if (viewUsesActiveQuizMode.value) return resolveActiveQuizMode(ckpt);
  return resolveProposedQuizMode(ckpt);
});

/** Prefer the content tab that still applies for the selected quiz mode. */
function applyRelatedMenuTabForQuizMode(mode) {
  if (mode === "SKIP_QUIZ" && articleMenuTabIndex.value === 1) {
    articleMenuTabIndex.value = 2;
  } else if (mode === "SKIP_INSPECTION" && articleMenuTabIndex.value === 2) {
    articleMenuTabIndex.value = 1;
  }
}

watch(
  resolvedQuizMode,
  (mode) => {
    applyRelatedMenuTabForQuizMode(mode);
  },
  { immediate: true }
);

const skipQuizBannerBody = computed(() => {
  const n = proposedQuizQuestions.value.length;
  if (n <= 0) {
    return "Questions stay in this draft but won't be published. Switch back to Quiz and Inspection to see them again.";
  }
  return `Your ${n} ${
    n === 1 ? "question" : "questions"
  } are kept in this draft but won't be published. Switch back to Quiz and Inspection to see them again.`;
});

const proposedInspectionProtocolParsed = computed(() => {
  const raw = displayCheckpoint.value?.proposed_inspection_protocol ?? null;
  if (raw == null || raw === "") return null;
  return safeParseJson(raw);
});

const proposedInspectionConfirmEveryStep = computed(() => {
  const p = proposedInspectionProtocolParsed.value;
  return !!(p && typeof p === "object" && p.requires_confirmation_at_every_step);
});

const proposedInspectionSteps = computed(() =>
  extractInspectionProtocolStepsFromPayload(proposedInspectionProtocolParsed.value).map(
    mapInstructionStep
  )
);

const hasProposedInspection = computed(() => proposedInspectionSteps.value.length > 0);

/** Fail / mismatch from video_to_components — flag, FAIL proposed_instructions, or in-session result. */
const isProposedVideoMicrolearningMismatch = computed(() => {
  if (contextValidationMismatchOverride.value != null) {
    return contextValidationMismatchOverride.value;
  }
  return isVideoContextMismatchCheckpoint(displayCheckpoint.value);
});

/**
 * PASS (or fail) payload for Video tab MicrolearningViewEdit —
 * prefers `microlearning_instructions`, falls back to proposed_instructions JSON.
 */
const videoPassMicrolearningPayload = computed(() => {
  const ml = displayCheckpoint.value?.microlearning_instructions;
  if (ml != null && ml !== "" && typeof ml === "object" && !Array.isArray(ml)) {
    return ml;
  }
  if (typeof ml === "string" && ml.trim()) {
    const parsed = safeParseJson(ml);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) return parsed;
  }
  const proposed = displayCheckpoint.value?.proposed_instructions;
  if (proposed == null || proposed === "") return null;
  const parsedProposed = safeParseJson(proposed);
  if (parsedProposed && typeof parsedProposed === "object" && !Array.isArray(parsedProposed)) {
    return parsedProposed;
  }
  return null;
});

/**
 * CheckpointTypeBanner variant for the currently selected microlearning type.
 * Video uses the pre-generation ("Video") copy until the microlearning kit is
 * generated; once generated it switches to "Video with TOC" or "Video Tutorial"
 * based on whether the generated payload carries `table_of_contents` chapters.
 * Returns null when the banner should not render (task_an_employee / unknown).
 */
const checkpointTypeBannerType = computed(() => {
  if (isInspectorOnly.value) return "Inspector Only";
  if (isArticle.value) return "Article";
  if (!isVideo.value) return null;

  if (!hasGeneratedVideoMicrolearningKit.value) return "Video";

  const s = videoPassMicrolearningPayload.value;
  const tocChapters = s?.table_of_contents?.chapters;
  const hasToc = Array.isArray(tocChapters) && tocChapters.length > 0;
  return hasToc ? "Video with TOC" : "Video Tutorial";
});

function parseVideoTimestampToSeconds(value) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.max(0, value);
  }
  const raw = String(value ?? "").trim();
  if (!raw) return 0;
  const timePart = raw.split("-")[0].trim();
  const segments = timePart.split(":").map((part) => parseInt(part, 10));
  if (segments.some((part) => Number.isNaN(part))) return 0;
  if (segments.length === 3) {
    return segments[0] * 3600 + segments[1] * 60 + segments[2];
  }
  if (segments.length === 2) {
    return segments[0] * 60 + segments[1];
  }
  return segments[0] ?? 0;
}

function formatVideoTimestampLabel(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const total = Math.floor(seconds);
  const minutes = Math.floor(total / 60);
  const secs = total % 60;
  return `${minutes}:${secs.toString().padStart(2, "0")}`;
}

function resolveChapterThumbnailUrl(chapter = {}) {
  const candidate =
    chapter.thumbnail ??
    chapter.thumbnail_url ??
    chapter.thumbnailUrl ??
    chapter.chapter_thumbnail ??
    chapter.chapter_thumbnail_url ??
    "";
  return String(candidate ?? "").trim();
}

/** Normalized video chapter row — preserves IQ `thumbnail` / jump URLs for VideoWithChaptersView. */
function mapVideoPassChapterStep(ch = {}, { migrated = false } = {}) {
  return {
    ...mapInstructionStep(ch),
    chapter_title: migrated
      ? ch.chapter_title ?? ch.look_for ?? ch.inspector_action ?? ""
      : ch.chapter_title ?? "",
    chapter_description: migrated
      ? ch.chapter_description ?? ch.look_for ?? ""
      : ch.chapter_description ?? "",
    chapter_summary: migrated
      ? ch.chapter_summary ?? ch.pass_criteria ?? ""
      : ch.chapter_summary ?? "",
    start_time: migrated ? ch.start_time ?? ch.timestamp_ref ?? "" : ch.start_time ?? "",
    end_time: ch.end_time ?? "",
    thumbnail: resolveChapterThumbnailUrl(ch),
    video_jump_url: ch.video_jump_url ?? "",
  };
}

/** TOC chapters for MicrolearningViewEdit / MicrolearningChapterCard (same as InspectionInstructions). */
const videoPassChapterSteps = computed(() => {
  const s = videoPassMicrolearningPayload.value;
  if (!s) return [];
  const tocChapters = s?.table_of_contents?.chapters;
  const mappedChapters = Array.isArray(s?.chapters) ? s.chapters : [];

  if (Array.isArray(tocChapters) && tocChapters.length > 0) {
    return tocChapters.map((ch) => mapVideoPassChapterStep(ch));
  }

  if (mappedChapters.length > 0) {
    return mappedChapters.map((ch) => mapVideoPassChapterStep(ch));
  }

  const migratedSteps = Array.isArray(s?.steps) ? s.steps : [];
  return migratedSteps.map((ch) => mapVideoPassChapterStep(ch, { migrated: true }));
});

const videoWithChaptersForView = computed(() =>
  videoPassChapterSteps.value.map((step, index) => {
    const startTimeSeconds = parseVideoTimestampToSeconds(step.start_time);
    const startTimeLabel =
      String(step.start_time ?? "").trim() || formatVideoTimestampLabel(startTimeSeconds);
    const title =
      String(step.chapter_title ?? step.inspector_action ?? "").trim() ||
      `Chapter ${index + 1}`;
    const description =
      String(
        step.chapter_description ?? step.chapter_summary ?? step.look_for ?? step.pass_criteria ?? ""
      ).trim() || title;
    const thumbnailUrl = resolveChapterThumbnailUrl(step);

    return {
      chapterNumber: index + 1,
      title,
      startTimeSeconds,
      startTimeLabel,
      description,
      thumbnailUrl,
      videoJumpUrl: String(step.video_jump_url ?? "").trim(),
    };
  })
);

const videoWithChaptersDurationSeconds = computed(() => {
  const payload = videoPassMicrolearningPayload.value;
  const fromPayload = Number(
    payload?.video_duration ??
      payload?.duration_seconds ??
      payload?.table_of_contents?.duration_seconds ??
      payload?.table_of_contents?.video_duration
  );
  if (Number.isFinite(fromPayload) && fromPayload > 0) {
    return fromPayload;
  }

  let maxSeconds = 0;
  for (const step of videoPassChapterSteps.value) {
    maxSeconds = Math.max(
      maxSeconds,
      parseVideoTimestampToSeconds(step.start_time),
      parseVideoTimestampToSeconds(step.end_time)
    );
  }
  return maxSeconds;
});

const videoWithChaptersSummaryLead = computed(() => {
  const payload = videoPassMicrolearningPayload.value;
  const videoSummary = String(
    payload?.videoSummary ?? payload?.table_of_contents?.videoSummary ?? ""
  ).trim();
  if (videoSummary) return videoSummary;

  const summaryField = payload?.microlearning_summary;
  if (summaryField && typeof summaryField === "object" && !Array.isArray(summaryField)) {
    const lead = String(summaryField.summary_lead ?? "").trim();
    if (lead) return lead;
  }

  const summary = videoPassChapterSummary.value;
  if (summary && typeof summary === "object" && !Array.isArray(summary)) {
    const overview = String(summary.overview ?? summary.text ?? "").trim();
    if (overview) return overview;
  }
  if (typeof summary === "string" && summary.trim()) {
    return summary.trim();
  }
  return "";
});

const videoWithChaptersSummaryBullets = computed(() => {
  const summaryField = videoPassMicrolearningPayload.value?.microlearning_summary;
  if (summaryField && typeof summaryField === "object" && !Array.isArray(summaryField)) {
    return (summaryField.summary_bullets ?? [])
      .map((bullet) => String(bullet ?? "").trim())
      .filter(Boolean);
  }
  return [];
});

const videoPassChapterVideoUrl = computed(() => {
  const s = videoPassMicrolearningPayload.value;
  const fromPayload = s?.video_url || s?.video || null;
  const payloadNeedsResolution =
    fromPayload && needsSignedVideoUrlResolution(String(fromPayload), isCloudflareSignedUrl);

  const fromDisplay = displayVideoUrl.value;
  if (fromDisplay && (payloadNeedsResolution || !fromPayload)) {
    return extractVideoUrlFromIframeHtml(String(fromDisplay)) || fromDisplay;
  }
  if (fromPayload) {
    return extractVideoUrlFromIframeHtml(String(fromPayload)) || fromPayload;
  }
  return fromDisplay ? extractVideoUrlFromIframeHtml(String(fromDisplay)) || fromDisplay : "";
});

const inspectionEditorVideoUrl = computed(() =>
  isVideo.value ? videoPassChapterVideoUrl.value || "" : ""
);

const showInspectionVideoPlayer = ref(false);
const inspectionVideoSrc = ref(null);
const inspectionVideoIframeKey = ref(0);

function resolveInspectionVideoPlaybackUrl(videoJumpUrl) {
  if (!videoJumpUrl || typeof videoJumpUrl !== "string") return null;
  const trimmed = videoJumpUrl.trim();
  if (!trimmed) return null;
  return extractVideoUrlFromIframeHtml(trimmed) || trimmed;
}

function appendAutoplayToVideoUrl(url) {
  if (!url || url.includes("autoplay=")) return url;
  const separator = url.includes("?") ? "&" : "?";
  if (url.includes("#")) {
    const hashIdx = url.indexOf("#");
    const baseUrl = url.slice(0, hashIdx);
    const hash = url.slice(hashIdx + 1);
    return `${baseUrl}${separator}autoplay=true#${hash}`;
  }
  return `${url}${separator}autoplay=true`;
}

function handleInspectionTimestampClick(videoJumpUrl) {
  const resolved = resolveInspectionVideoPlaybackUrl(videoJumpUrl);
  if (!resolved) return;
  inspectionVideoSrc.value = appendAutoplayToVideoUrl(resolved);
  inspectionVideoIframeKey.value += 1;
  showInspectionVideoPlayer.value = true;
}

function closeInspectionVideoDialog() {
  showInspectionVideoPlayer.value = false;
  inspectionVideoSrc.value = null;
}

const videoPassChapterSummary = computed(() => {
  const s = videoPassMicrolearningPayload.value;
  if (!s) return null;
  const videoSummary = String(s.videoSummary ?? s.table_of_contents?.videoSummary ?? "").trim();
  if (videoSummary) return { overview: videoSummary };
  const summary = s.microlearning_summary ?? s.summary ?? null;
  if (summary == null) return null;
  if (typeof summary === "string" && summary.trim()) {
    return { overview: summary.trim() };
  }
  if (typeof summary === "object" && !Array.isArray(summary)) {
    if (String(summary.overview ?? summary.text ?? "").trim()) return summary;
    const migratedOverview = [summary.pass, summary.fail, summary.borderline]
      .filter((part) => String(part || "").trim())
      .join("\n\n");
    if (migratedOverview) return { ...summary, overview: migratedOverview };
    return summary;
  }
  return null;
});

const videoSummarySaving = ref(false);
const videoChapterSavingIndex = ref(-1);

function cloneMicrolearningPayload() {
  const base = videoPassMicrolearningPayload.value;
  if (!base || typeof base !== "object") return {};
  try {
    return JSON.parse(JSON.stringify(base));
  } catch {
    return { ...base };
  }
}

function buildMicrolearningInstructionsPatch(nextPayload) {
  const patch = {
    microlearning_instructions: nextPayload,
  };
  // Keep proposed_instructions in sync when it currently holds the same TOC payload.
  const proposed = props.checkpoint?.proposed_instructions;
  if (proposed != null && proposed !== "") {
    patch.proposed_instructions =
      typeof nextPayload === "string" ? nextPayload : JSON.stringify(nextPayload);
  }
  return patch;
}

async function persistMicrolearningInstructionsPatch(patch) {
  const accountId = getAccountId();
  if (!accountId || props.responsibilityId == null || checkpointId.value == null) {
    return false;
  }
  try {
    await changeCheckpointV2(accountId, props.responsibilityId, checkpointId.value, patch);
    emitCheckpointMutationResult();
    return true;
  } catch (error) {
    console.error("[CheckpointEditFormV2] Error saving microlearning content:", error);
    notificationStore.error(
      error?.response?.data?.detail ??
        error?.response?.data?.message ??
        "Could not save changes. Please try again."
    );
    return false;
  }
}

async function handleVideoSummarySave(payload) {
  if (!canEditCheckpointContent.value) {
    payload?.complete?.(new Error("readonly"));
    return;
  }

  // Two-part VIDEO_WITH_TOC summary: lead sentence + ordered bullet list.
  if (Array.isArray(payload?.summary_bullets) || typeof payload?.summary_lead === "string") {
    const summaryLead = String(payload.summary_lead ?? "").trim();
    const summaryBullets = (payload.summary_bullets ?? [])
      .map((bullet) => String(bullet ?? "").trim())
      .filter((bullet) => bullet.length > 0);
    if (!summaryLead && summaryBullets.length === 0) {
      payload?.complete?.(new Error("empty"));
      return;
    }

    videoSummarySaving.value = true;
    try {
      const next = cloneMicrolearningPayload();
      next.microlearning_summary = {
        summary_lead: summaryLead,
        summary_bullets: summaryBullets,
      };

      const ok = await persistMicrolearningInstructionsPatch(
        buildMicrolearningInstructionsPatch(next)
      );
      payload?.complete?.(ok ? null : new Error("save-failed"));
    } finally {
      videoSummarySaving.value = false;
    }
    return;
  }

  // Single-string summary (VIDEO_STEP_BY_STEP_TUTORIAL / migrated single-field).
  const summaryText = String(payload?.summary ?? "").trim();
  if (!summaryText) {
    payload?.complete?.(new Error("empty"));
    return;
  }

  videoSummarySaving.value = true;
  try {
    const next = cloneMicrolearningPayload();
    if (typeof next.microlearning_summary === "string" || next.microlearning_summary == null) {
      next.microlearning_summary = summaryText;
    }
    if (typeof next.summary === "string") {
      next.summary = summaryText;
    } else if (next.summary && typeof next.summary === "object") {
      next.summary = { ...next.summary, overview: summaryText };
    } else if (next.microlearning_summary == null) {
      next.summary = { overview: summaryText };
    }

    const ok = await persistMicrolearningInstructionsPatch(
      buildMicrolearningInstructionsPatch(next)
    );
    payload?.complete?.(ok ? null : new Error("save-failed"));
  } finally {
    videoSummarySaving.value = false;
  }
}

async function handleVideoChapterSave({ index, title, description, complete }) {
  if (!canEditCheckpointContent.value) {
    complete?.(new Error("readonly"));
    return;
  }
  const chapterTitle = String(title ?? "").trim();
  const chapterDescription = String(description ?? "").trim();
  if (!chapterTitle || !chapterDescription) {
    complete?.(new Error("empty"));
    return;
  }

  videoChapterSavingIndex.value = index;
  try {
    const next = cloneMicrolearningPayload();
    const tocChapters = next?.table_of_contents?.chapters;
    if (Array.isArray(tocChapters) && tocChapters[index]) {
      const chapter = { ...tocChapters[index] };
      chapter.chapter_title = chapterTitle;
      if (chapter.chapter_summary != null || chapter.chapter_description == null) {
        chapter.chapter_summary = chapterDescription;
      } else {
        chapter.chapter_description = chapterDescription;
      }
      tocChapters[index] = chapter;
      next.table_of_contents = { ...next.table_of_contents, chapters: tocChapters };
    } else if (Array.isArray(next.chapters) && next.chapters[index]) {
      const chapter = { ...next.chapters[index] };
      chapter.chapter_title = chapterTitle;
      chapter.title = chapterTitle;
      if (chapter.chapter_summary != null || chapter.chapter_description == null) {
        chapter.chapter_summary = chapterDescription;
      } else {
        chapter.chapter_description = chapterDescription;
      }
      next.chapters[index] = chapter;
    } else {
      complete?.(new Error("missing-chapter"));
      return;
    }

    const ok = await persistMicrolearningInstructionsPatch(
      buildMicrolearningInstructionsPatch(next)
    );
    complete?.(ok ? null : new Error("save-failed"));
  } finally {
    videoChapterSavingIndex.value = -1;
  }
}

/** Video tab badge in CheckpointMicrolearningMenu, e.g. "With TOC - 2 Chapters". */
const videoTabStatusBadge = computed(() => {
  const s = videoPassMicrolearningPayload.value;
  if (!s) return null;
  const tocChapters = s?.table_of_contents?.chapters;
  const mappedChapters = Array.isArray(s?.chapters) ? s.chapters : [];
  const hasToc = Array.isArray(tocChapters) && tocChapters.length > 0;
  const count = hasToc ? tocChapters.length : mappedChapters.length;
  if (count <= 0) return null;
  if (hasToc) {
    return `With TOC - ${count} ${count === 1 ? "Chapter" : "Chapters"}`;
  }
  return `Step by Step - ${count} ${count === 1 ? "Step" : "Steps"}`;
});

const videoPassError = computed(() => {
  if (!isProposedVideoMicrolearningMismatch.value) return null;
  if (contextValidationError.value) return contextValidationError.value;
  const persisted = resolvePersistedVideoMismatchError(
    displayCheckpoint.value?.proposed_instructions
  );
  if (persisted) return persisted;
  const s = videoPassMicrolearningPayload.value;
  const message =
    s?.error ??
    s?.validation?.reasoning ??
    s?.inspection_instructions?.validation?.reasoning ??
    null;
  if (message != null && String(message).trim()) return String(message).trim();
  return "This video does not match the context of the checkpoint.";
});

/**
 * Abort Cloudflare / signed-URL polls, unmount context-validation IQ, and clear
 * pending upload state so no further video endpoints run after cancel.
 *
 * ER-12337 — this is the ONLY way the workflow stops (plus logout); unmount/navigation
 * leaves the store-owned runner alive in the background.
 */
function stopVideoProcessingInFlight() {
  iqVideoProcessingStore.abort(checkpointId.value);
  try {
    const iqChatStore = useIQChatStore();
    iqChatStore.stopGenerating();
    iqChatStore.clearChat();
  } catch {
    // Pinia may be unavailable in isolated unit tests.
  }
}

/**
 * Same outcome as Discard draft: stop in-flight video work, then delete the draft.
 */
function handleVideoProcessingCancelAndDiscard() {
  stopVideoProcessingInFlight();
  iqVideoMicrolearningStore.abortRun(checkpointId.value);
  iqCheckpointSuggestStore.abortAllForCheckpoint(checkpointId.value);
  emit("discard-draft");
}

/** ER-12337 — Exit Edit is the one editor action that cancels in-flight runs (releases lock). */
function handleExitEdit() {
  iqVideoMicrolearningStore.abortRun(checkpointId.value);
  iqCheckpointSuggestStore.abortAllForCheckpoint(checkpointId.value);
  iqVideoProcessingStore.abort(checkpointId.value);
  emit("exit-edit");
}

function handleSuggestVideoInstructions() {
  if (hasActiveVideoMicrolearningRun.value) return;
  showGenerateVideoMicrolearningDialog.value = true;
}

const generateVideoMicrolearningDialogInitialVideoType = computed(() => {
  const fromSelection = inlineVideoToComponentsSelection.value?.videoType;
  if (fromSelection === "toc" || fromSelection === "walkthrough") {
    return fromSelection;
  }
  return "toc";
});

function buildGenerateVideoMicrolearningPromptSuffix(payload) {
  const { videoType, testingType } = payload || {};
  const videoLabel =
    videoType === "toc"
      ? "Video with Table of Contents"
      : "Step by Step Annotated Video Walkthrough";
  const testingLabel =
    testingType === "quiz_only"
      ? "Generate only Quiz with Auto Pass"
      : testingType === "inspection_only"
        ? "Generate only Inspection Protocol"
        : "Generate both Quiz and Inspection Protocol";
  return `Selected microlearning video type: ${videoLabel}. Selected testing/inspection: ${testingLabel}.`;
}

function buildFromVideoIqUserPrompt(extraPromptSuffix = "") {
  const checkpointInfo = [prefixDisplay.value, props.checkpoint?.criteria]
    .filter(Boolean)
    .join(" ");
  const responsibilityInfo = [props.responsibilityCode, props.responsibilityName]
    .filter(Boolean)
    .join(" ");
  const base = `Pls suggest Inspection instructions from video of Checkpoint: ${checkpointInfo} belonging to responsibility ${responsibilityInfo}`;
  return extraPromptSuffix ? `${base} ${extraPromptSuffix}` : base;
}

function extractVideoUrlFromIframeHtml(iframeHtml) {
  if (!iframeHtml) return "";
  if (!String(iframeHtml).includes("<iframe") && !String(iframeHtml).includes("src=")) {
    return String(iframeHtml);
  }
  const srcMatch = String(iframeHtml).match(/src=\\?"([^"\\]+)/);
  if (srcMatch && srcMatch[1]) return srcMatch[1];
  const fallbackMatch = String(iframeHtml).match(/src="([^"]+)"/);
  if (fallbackMatch && fallbackMatch[1]) return fallbackMatch[1];
  return "";
}

function onGenerateVideoMicrolearningDialogInput(open) {
  showGenerateVideoMicrolearningDialog.value = open;
}

function handleGenerateVideoMicrolearningDialogSubmit(payload) {
  showGenerateVideoMicrolearningDialog.value = false;
  if (hasActiveVideoMicrolearningRun.value) return;
  const selection = {
    videoType: payload?.videoType,
    testingType: payload?.testingType,
  };
  const suffix = buildGenerateVideoMicrolearningPromptSuffix(payload);
  const userPrompt = buildFromVideoIqUserPrompt(suffix);
  inlineVideoToComponentsSelection.value = selection;
  iqVideoMicrolearningStore.startRun({
    checkpointId: checkpointId.value,
    responsibilityId: props.responsibilityId,
    userPrompt,
    videoUrl: displayVideoUrl.value || "",
    checkpointPrefix: prefixDisplay.value,
    checkpointCriteria: props.checkpoint?.criteria || "",
    responsibilityName: props.responsibilityName || "",
    responsibilityPurpose: props.responsibilityPurpose || "",
    selection,
  });
}

function handleIqVideoGenerationRetry() {
  iqVideoMicrolearningStore.retryRun(checkpointId.value);
}

/**
 * Mismatch → Try Again: re-run video_to_components against the current video + current
 * criteria without a new upload. ER-12337 — the runner and its persisted verdict live in
 * iqVideoProcessingStore, so a retry survives navigating away mid-flight.
 */
function handleContextValidationRetry() {
  if (criteriaReadonly.value) return;
  iqVideoProcessingStore.retryContextValidation(buildVideoProcessingContext());
}

function handleArticleMenuTabChange(index) {
  const nextIndex = Number(index);
  articleMenuTabIndex.value = Number.isFinite(nextIndex) ? nextIndex : 0;
}

function handleProposedArticleHtmlUpdate(html) {
  emit("proposed-knowledge-article-html-change", { html });
}

function handleProposedArticleSave(payload) {
  emit("proposed-knowledge-article-save", payload);
}

function handleProposedArticleDiscard() {
  emit("proposed-knowledge-article-discard");
}

function openScribeImportDialog() {
  scribeImportDialogOpen.value = true;
}

function handleScribeImportClose() {
  scribeImportDialogOpen.value = false;
}

function openPdfImportDialog() {
  pdfImportDialogOpen.value = true;
}

function handlePdfImportClose() {
  pdfImportDialogOpen.value = false;
}

function openWordImportDialog() {
  wordImportDialogOpen.value = true;
}

function handleWordImportClose() {
  wordImportDialogOpen.value = false;
}

/**
 * ER-12400 — injected as WordImportDialog's `startImport` prop. POSTs the chosen .docx as
 * multipart; a 202 returns `{ job_id }` and the dialog takes over polling. 422 WORD_IMPORT_*
 * errors reject here and are surfaced inside the dialog.
 */
function handleWordImportStart(accountId, responsibilityId, checkpointId, formData) {
  return importWordArticle(accountId, responsibilityId, checkpointId, formData);
}

/** ER-12400 — injected as WordImportDialog's `pollStatus` prop. GETs the job status. */
function handleWordImportPoll(accountId, responsibilityId, checkpointId, jobId) {
  return getWordImportJobStatus(accountId, responsibilityId, checkpointId, jobId);
}

/**
 * ER-12394 — injected as PdfImportDialog's `startImport` prop. POSTs the chosen PDF as
 * multipart; a 202 returns `{ job_id }` and the dialog takes over polling. 422 PDF_IMPORT_*
 * errors reject here and are surfaced inside the dialog.
 */
function handlePdfImportStart(accountId, responsibilityId, checkpointId, formData) {
  return importPdfArticle(accountId, responsibilityId, checkpointId, formData);
}

/** ER-12394 — injected as PdfImportDialog's `pollStatus` prop. GETs the job status. */
function handlePdfImportPoll(accountId, responsibilityId, checkpointId, jobId) {
  return getPdfImportJobStatus(accountId, responsibilityId, checkpointId, jobId);
}

/**
 * ER-12335 — injected as the dialog's `startImport` prop. POSTs the v2 endpoint with the
 * pasted url; a 202 returns `{ job_id }` and the dialog takes over polling from there.
 * 422 SCRIBE_IMPORT_* errors reject here and are surfaced inside the dialog.
 */
function handleScribeImportStart(accountId, responsibilityId, checkpointId, { url }) {
  return importScribeArticle(accountId, responsibilityId, checkpointId, { url });
}

/** ER-12335 — injected as the dialog's `pollStatus` prop. GETs the job status. */
function handleScribeImportPoll(accountId, responsibilityId, checkpointId, jobId) {
  return getScribeImportJobStatus(accountId, responsibilityId, checkpointId, jobId);
}

/** ER-12335 — the dialog emitted `success` with the succeeded payload (equals today's 200 body). */
function handleScribeImportSuccess(result) {
  // ER-11753 — populate the editor from the returned TipTap doc. The backend
  // always re-fetches live (Rule 7); nothing is cached client-side. Steps the
  // backend flagged as failed re-hosts are turned into a visible broken-image
  // placeholder before the doc reaches the editor (Rule 5/6).
  const failedCount = Array.isArray(result?.failed_image_steps) ? result.failed_image_steps.length : 0;
  const notice =
    failedCount > 0
      ? {
          type: "warning",
          message: `${failedCount} screenshot(s) couldn't be imported and were replaced with placeholders.`,
        }
      : null;
  populateEditorFromImport(result?.content, notice);
}

/**
 * ER-12395 — the PDF dialog emitted `success` with `{ content, summary }`. Same populate path
 * as Scribe (Rule 9); the Rule 8 summary is toasted once the editor has been populated.
 * Reachable only via the "Import from PDF" menu item, which is gated by the idea-MDP-630 flag.
 */
function handlePdfImportSuccess(result) {
  const message = buildPdfImportSummaryMessage(result?.summary);
  populateEditorFromImport(result?.content, message ? { type: "info", message } : null);
}

/**
 * ER-12401 — the Word dialog emitted `success` with `{ content, summary }`. Same populate path
 * as Scribe and PDF (Rule 9); the Rule 8 summary is toasted once the editor has been populated.
 * Reachable only via the "Import from Word" menu item, which is gated by the idea-MDP-633 flag.
 */
function handleWordImportSuccess(result) {
  const message = buildWordImportSummaryMessage(result?.summary);
  populateEditorFromImport(result?.content, message ? { type: "info", message } : null);
}

function populateEditorFromImport(rawContent, notice) {
  const content = prepareScribeImportContent(rawContent);
  if (!content || typeof content !== "object") {
    notificationStore.error("The import returned no content to insert.");
    return;
  }
  if (hasArticleEditorContent()) {
    // Non-empty editor: hold the parsed doc and ask before replacing (Rule 6 / Rule 9).
    pendingImportContent.value = content;
    pendingImportNotice.value = notice;
    showImportReplaceConfirm.value = true;
    return;
  }
  applyImportedContent(content, notice);
}

function hasArticleEditorContent() {
  const menu = articleMenuRef.value;
  return Boolean(
    menu && typeof menu.hasArticleEditorContent === "function" && menu.hasArticleEditorContent()
  );
}

function applyImportedContent(content, notice) {
  const menu = articleMenuRef.value;
  if (menu && typeof menu.applyImportedArticle === "function") {
    menu.applyImportedArticle(content);
  }
  if (notice) {
    notificationStore[notice.type](notice.message);
  }
}

function clearPendingImport() {
  pendingImportContent.value = null;
  pendingImportNotice.value = null;
}

function handleImportReplaceConfirm() {
  const content = pendingImportContent.value;
  const notice = pendingImportNotice.value;
  clearPendingImport();
  showImportReplaceConfirm.value = false;
  if (content) {
    applyImportedContent(content, notice);
  }
}

function onImportReplaceConfirmInput(val) {
  showImportReplaceConfirm.value = val;
  if (!val) {
    clearPendingImport();
  }
}

/** Edit-bar Save → bumps token so CheckpointMicrolearningMenu runs the same Save path. */
function handleEditBarArticleSave() {
  if (!isArticle.value || !articleCanSave.value) return;
  articleSaveRequestToken.value += 1;
}

/**
 * Draft + dirty criteria/prefix → discard restores baseline (Exit Edit again).
 * Active-with-changes publish actions → open discard-proposed confirm.
 * Otherwise open confirm → Discard draft deletes draft.
 */
function handleEditBarDiscard() {
  if (
    editBarCheckpointState.value === "active-with-changes" &&
    ["can-publish", "cannot-publish"].includes(editBarAction.value)
  ) {
    showDiscardProposedDialog.value = true;
    return;
  }
  if (isCriteriaDirty.value) {
    emit("update-criteria", savedCriteria.value);
    emit("update-prefix", formatPrefixDisplay(savedPrefix.value));
    emit("update-employee-prep-note", savedPrepNote.value);
    return;
  }
  showDiscardDraftDialog.value = true;
}

function onDiscardDraftDialogInput(val) {
  if (proxy?.$loader?.isAppLoading) return;
  showDiscardDraftDialog.value = val;
}

function onDiscardProposedDialogInput(val) {
  if (proxy?.$loader?.isAppLoading) return;
  showDiscardProposedDialog.value = val;
}

function onPublishProposedDialogClose() {
  if (proxy?.$loader?.isAppLoading) return;
  showPublishProposedDialog.value = false;
}

/** Keep dialog open so ExActionsPopUp OK button shows $loader spinner. */
async function handleDiscardProposedConfirm() {
  const accountId = getAccountId();
  if (!accountId || props.responsibilityId == null || checkpointId.value == null) {
    showDiscardProposedDialog.value = false;
    notificationStore.error("Missing account or checkpoint information.");
    return;
  }

  proxy?.$loader?.setAppLoading?.(true);
  try {
    const response = await discardChangeProposal(
      accountId,
      props.responsibilityId,
      checkpointId.value
    );
    showDiscardProposedDialog.value = false;
    editBarSelectedTab.value = "proposed-changes";
    if (response?.data) {
      void notifyPeersOfCheckpointUpdate();
      emit("checkpoint-updated", response.data);
    } else {
      void notifyPeersOfCheckpointUpdate();
      emit("refresh-checkpoint");
    }
    emit("checkpoint-count-changed");
  } catch (error) {
    console.error("[CheckpointEditFormV2] Error discarding proposed changes:", error);
    notificationStore.error(
      error?.response?.data?.detail ??
        error?.response?.data?.message ??
        "Could not discard proposed changes."
    );
  } finally {
    proxy?.$loader?.setAppLoading?.(false);
  }
}

function handleDiscardDraftConfirm() {
  if (proxy?.$loader?.isAppLoading) return;
  showDiscardDraftDialog.value = false;
  stopVideoProcessingInFlight();
  iqVideoMicrolearningStore.abortRun(checkpointId.value);
  // The draft (and its checkpoint row) is about to be deleted — a still-running suggestion
  // must not persist questions/steps against it afterwards.
  iqCheckpointSuggestStore.abortAllForCheckpoint(checkpointId.value);
  emit("discard-draft");
}

function handleEditBarTabClick(tab) {
  if (tab === "inspections") {
    if (showInspectionsTab.value) editBarSelectedTab.value = "inspections";
    return;
  }
  if (editBarCheckpointState.value !== "active-with-changes") {
    // Only the Inspections tab can be left again outside active-with-changes.
    if (editBarSelectedTab.value === "inspections") editBarSelectedTab.value = "primary";
    return;
  }
  editBarSelectedTab.value = tab === "primary" ? "primary" : "proposed-changes";
}

function handleInspectionsFilterApply(payload) {
  applyInspectionFilters(payload);
}

function handleInspectionsChipChange(nextChips) {
  applyInspectionChipFilters(nextChips);
}

function handleInspectionsSearchChange(text) {
  setInspectionEmployeeSearch(text);
}

async function handleInspectionsResetAll() {
  emit("inspection-reset-all");
  if (!canBulkResetInspections.value || inspectionBulkResetBusy.value) return;
  if (proxy?.$loader?.isAppLoading) return;

  inspectionBulkResetBusy.value = true;
  proxy?.$loader?.setAppLoading?.(true);
  try {
    await bulkResetInspections("reset");
  } catch (error) {
    notificationStore.error(
      error?.response?.data?.detail ??
        error?.response?.data?.message ??
        "Failed to reset inspections. Please try again."
    );
  } finally {
    inspectionBulkResetBusy.value = false;
    proxy?.$loader?.setAppLoading?.(false);
  }
}

async function handleInspectionsRowReset(payload) {
  emit("inspection-reset", payload);
  if (proxy?.$loader?.isAppLoading) return;

  proxy?.$loader?.setAppLoading?.(true);
  try {
    await resetInspectionRow(payload);
  } catch (error) {
    notificationStore.error(
      error?.response?.data?.detail ??
        error?.response?.data?.message ??
        "Failed to reset inspection. Please try again."
    );
  } finally {
    proxy?.$loader?.setAppLoading?.(false);
  }
}

function handleInspectionsChatterOpen(payload) {
  emit("inspection-chatter-open", payload);
}

function closeVersionHistoryPanel() {
  showVersionHistoryPanel.value = false;
  versionHistoryLoading.value = false;
  versionHistoryError.value = null;
}

function handleVersionHistorySelect(version) {
  if (version?.id == null) return;
  selectedVersionId.value = version.id;

  const snapshot = versionSnapshotsById.value[version.id];
  if (!snapshot) return;

  const parentCheckpointId =
    checkpointId.value ?? currentCheckPoint.value?.id ?? props.checkpoint?.id ?? null;
  viewingPastVersion.value = {
    checkpoint: mapCheckpointVersionSnapshotToCheckpoint(snapshot, parentCheckpointId),
    versionNumber: version.versionNumber ?? snapshot.version_number,
  };
  closeVersionHistoryPanel();
}

function handlePastVersionBreadcrumbClick(breadcrumb) {
  if (!breadcrumb) return;
  viewingPastVersion.value = null;
  if (breadcrumb.text === "Checklist") {
    emit("back-to-list");
  }
}

async function handleVersionHistoryClick() {
  if (showVersionHistoryPanel.value) {
    closeVersionHistoryPanel();
    return;
  }

  const accountId = getAccountId();
  const id = checkpointId.value ?? currentCheckPoint.value?.id;
  if (accountId == null || id == null) {
    notificationStore.error("Missing account or checkpoint information.");
    return;
  }

  const requestId = ++versionHistoryRequestId;
  showVersionHistoryPanel.value = true;
  versionHistoryLoading.value = true;
  versionHistoryError.value = null;
  versionHistoryRows.value = [];
  versionSnapshotsById.value = {};
  selectedVersionId.value = null;

  try {
    const response = await getCheckpointVersions(accountId, id);
    if (requestId !== versionHistoryRequestId) return;
    const results = response?.data?.results ?? response?.data ?? [];
    const snapshots = {};
    results.forEach((row) => {
      if (row?.id != null) snapshots[row.id] = row;
    });
    versionSnapshotsById.value = snapshots;
    const rows = mapCheckpointVersionHistoryRows(results, {
      checkpointStatus: props.checkpoint?.status,
    });
    versionHistoryRows.value = rows;
    const current = rows.find((row) => row.isCurrent);
    selectedVersionId.value = current?.id ?? rows[0]?.id ?? null;
  } catch (error) {
    if (requestId !== versionHistoryRequestId) return;
    console.error("[CheckpointEditFormV2] Error loading version history:", error);
    versionHistoryRows.value = [];
    versionHistoryError.value =
      error?.response?.data?.detail ??
      error?.response?.data?.message ??
      "Unable to load version history.";
  } finally {
    if (requestId === versionHistoryRequestId) {
      versionHistoryLoading.value = false;
    }
  }
}

/**
 * ER-11249 / ER-11208: fetch the current live version's number + publish-message
 * metadata for the edit-bar V{n} pill and the history-icon "what changed" tooltip.
 * Runs on mount / checkpoint change for active states only (draft/retired/archived
 * never render either surface). Best-effort — a failure just hides the pill/tooltip
 * content, leaving the "Click to open history of versions" fallback hint.
 */
async function loadCurrentVersionMeta() {
  const accountId = getAccountId();
  const id = checkpointId.value ?? currentCheckPoint.value?.id;
  if (props.pastVersionView || accountId == null || id == null) return;
  const state = editBarCheckpointState.value;
  if (state !== "active" && state !== "active-with-changes") {
    currentVersionNumber.value = null;
    currentPublishMessage.value = null;
    currentPublishMessageStatus.value = null;
    return;
  }
  try {
    const meta = await checkpointVersionsStore.fetchCurrentVersionMeta(accountId, id);
    currentVersionNumber.value = meta?.versionNumber ?? null;
    currentPublishMessage.value = meta?.publishMessage ?? null;
    currentPublishMessageStatus.value = meta?.publishMessageStatus ?? null;
  } catch (error) {
    console.error("[CheckpointEditFormV2] Error loading current version metadata:", error);
    currentVersionNumber.value = null;
    currentPublishMessage.value = null;
    currentPublishMessageStatus.value = null;
  }
}

function handleEditBarPrimaryAction() {
  if (editBarAction.value === "save-changes") {
    void saveCriteriaAndPrefixChanges();
    return;
  }
  if (editBarAction.value === "propose-changes") {
    openProposeChangesDialog();
    return;
  }
  if (editBarAction.value === "can-publish") {
    showPublishProposedDialog.value = true;
    return;
  }
  if (editBarAction.value === "reactivate") {
    currentCheckPoint.value = props.checkpoint
      ? JSON.parse(JSON.stringify(props.checkpoint))
      : null;
    showReActivateCheckpointDialog.value = true;
    return;
  }
  if (editBarAction.value === "unarchive") {
    currentCheckPoint.value = props.checkpoint
      ? JSON.parse(JSON.stringify(props.checkpoint))
      : null;
    showUnarchiveCheckpointDialog.value = true;
    return;
  }
  if (editBarAction.value === "activate" || editBarAction.value === "activate-disabled") {
    if (isCheckpointActivateDisabled(props.checkpoint)) {
      return;
    }
    currentCheckPoint.value = props.checkpoint
      ? JSON.parse(JSON.stringify(props.checkpoint))
      : null;
    showActivateCheckpointDialog.value = true;
    return;
  }
}

/** Keep dialog open so ExActionsPopUp Publish button shows $loader spinner. */
async function handlePublishProposedConfirm() {
  const accountId = getAccountId();
  if (!accountId || props.responsibilityId == null || checkpointId.value == null) {
    showPublishProposedDialog.value = false;
    notificationStore.error("Missing account or checkpoint information.");
    return;
  }

  proxy?.$loader?.setAppLoading?.(true);
  try {
    const publishResponse = await publishProposedInspectionKit(
      accountId,
      props.responsibilityId,
      checkpointId.value
    );
    showPublishProposedDialog.value = false;
    const updated = publishResponse?.data ?? null;
    editBarSelectedTab.value = "proposed-changes";
    void notifyPeersOfCheckpointUpdate();
    if (updated) {
      emit("checkpoint-updated", updated);
    } else {
      emit("refresh-checkpoint");
    }
    emit("checkpoint-count-changed");
  } catch (error) {
    console.error("[CheckpointEditFormV2] Error publishing proposed inspection kit:", error);
    notificationStore.error(
      error?.response?.data?.detail ??
        error?.response?.data?.message ??
        "Could not publish proposed changes."
    );
  } finally {
    proxy?.$loader?.setAppLoading?.(false);
  }
}

/** Same PATCH shape as CheckpointEditForm.handleEditHeaderSave for criteria/prefix. */
async function saveCriteriaAndPrefixChanges() {
  if (!isCriteriaDirty.value || isCriteriaSavePending.value) return;
  const accountId = getAccountId();
  if (!accountId || props.responsibilityId == null || checkpointId.value == null) {
    notificationStore.error("Missing account or checkpoint information.");
    return;
  }

  const levelName = props.checkpoint?.level;
  const proficiencyFromLevel =
    levelName && LEVEL_BY_NAME[levelName] != null ? LEVEL_BY_NAME[levelName].id : 1;

  isCriteriaSavePending.value = true;
  proxy?.$loader?.setAppLoading?.(true);
  try {
    const ideaMDP452 = Boolean(store.getters["featureFlags/ideaMDP452"]);
    const normalizedCriteria = maybeNormalizeCriteriaBodyEn(
      props.checkpoint?.criteria || "",
      ideaMDP452
    );
    if (normalizedCriteria !== (props.checkpoint?.criteria || "")) {
      emit("update-criteria", normalizedCriteria);
    }
    await changeCheckpointV2(accountId, props.responsibilityId, checkpointId.value, {
      criteria: normalizedCriteria,
      prefix: formatCriteriaPrefix(props.checkpoint?.prefix || "Can"),
      employee_prep_note: props.checkpoint?.employee_prep_note ?? null,
      instructions: props.checkpoint?.instructions || "<p></p>",
      proficiency_level: proficiencyFromLevel,
      status: props.checkpoint?.status || "draft",
      account: accountId,
      responsibility: props.responsibilityId,
    });
    snapshotCriteriaBaseline();
    criteriaFormEpoch.value += 1;
    emitCheckpointMutationResult();
  } catch (err) {
    console.error("[CheckpointEditFormV2] Error updating checkpoint criteria:", err);
    notificationStore.error(
      err?.response?.data?.detail ??
        err?.response?.data?.message ??
        "Could not save checkpoint changes."
    );
  } finally {
    isCriteriaSavePending.value = false;
    proxy?.$loader?.setAppLoading?.(false);
  }
}

function buildArticleMicrolearningRemovePatch() {
  const patch = {
    proposed_inspection_protocol: null,
    proposed_microlearning_type: null,
    proposed_quiz: null,
    proposed_quiz_is_autopass: false,
    proposed_knowledge_article_html: null,
    proposed_article: null,
  };
  if (props.checkpoint?.proposed_inspection_type === "article") {
    patch.proposed_inspection_type = null;
  }
  return patch;
}

async function handleRemoveMicrolearningConfirm() {
  const accountId = getAccountId();
  if (!accountId || props.responsibilityId == null || checkpointId.value == null) {
    notificationStore.error("Missing account or checkpoint information.");
    return;
  }
  proxy?.$loader?.setAppLoading?.(true);
  try {
    await removeCheckpointMicrolearning(accountId, props.responsibilityId, checkpointId.value);
    await changeCheckpointV2(
      accountId,
      props.responsibilityId,
      checkpointId.value,
      buildArticleMicrolearningRemovePatch()
    );
    void notifyPeersOfCheckpointUpdate();
    handleRefreshCheckpoint();
    emit("proposed-knowledge-article-remove");
  } catch (err) {
    console.error("[CheckpointEditFormV2] Error removing proposed article:", err);
    notificationStore.error(
      err?.response?.data?.detail ??
        err?.response?.data?.message ??
        "Could not remove microlearning."
    );
  } finally {
    proxy?.$loader?.setAppLoading?.(false);
    showRemoveMicrolearningDialog.value = false;
  }
}

async function handleQuizModeChange(nextMode) {
  if (!QUIZ_MODE_VALUES.includes(nextMode) || nextMode === resolvedQuizMode.value) return;
  const accountId = getAccountId();
  if (!accountId || props.responsibilityId == null || checkpointId.value == null) return;

  try {
    await changeCheckpointV2(accountId, props.responsibilityId, checkpointId.value, {
      quiz_mode: nextMode,
    });
    emitCheckpointMutationResult();
  } catch (err) {
    console.error("[CheckpointEditFormV2] quiz_mode patch:", err);
    notificationStore.error(
      err?.response?.data?.detail ?? err?.response?.data?.message ?? "Could not update quiz mode."
    );
  }
}

async function handleQuizAutopassChange(nextValue) {
  await handleQuizModeChange(nextValue ? "SKIP_INSPECTION" : "BOTH_REQUIRED");
}

async function persistProposedInspectionProtocol(stepsMapped, confirmEveryStep) {
  const accountId = getAccountId();
  if (!accountId || props.responsibilityId == null || checkpointId.value == null) return false;
  const confirm =
    confirmEveryStep !== undefined && confirmEveryStep !== null
      ? !!confirmEveryStep
      : proposedInspectionConfirmEveryStep.value;
  const merged = mergeProposedInspectionProtocolSnapshot(
    proposedInspectionProtocolParsed.value,
    stepsMapped,
    confirm
  );
  proxy?.$loader?.setAppLoading?.(true);
  try {
    await changeCheckpointV2(accountId, props.responsibilityId, checkpointId.value, {
      proposed_inspection_protocol: JSON.stringify(merged),
    });
    emitCheckpointMutationResult();
    return true;
  } catch (err) {
    console.error("[CheckpointEditFormV2] proposed_inspection_protocol update:", err);
    notificationStore.error(
      err?.response?.data?.detail ??
        err?.response?.data?.message ??
        "Could not update inspection protocol."
    );
    return false;
  } finally {
    proxy?.$loader?.setAppLoading?.(false);
  }
}

async function handleInspectionConfirmEveryStepChange(nextValue) {
  await persistProposedInspectionProtocol(proposedInspectionSteps.value, !!nextValue);
}

function buildAddInspectionStepApiPayload(step) {
  const raw = String(step?.competence_category ?? "ability").toLowerCase();
  let competence_category = "Ability";
  if (raw === "knowledge") competence_category = "Knowledge";
  else if (raw === "behaviour" || raw === "behavior") competence_category = "Behavior";

  return {
    step: {
      competence_category,
      inspector_action: step?.inspector_action != null ? String(step.inspector_action).trim() : "",
      look_for: step?.look_for != null ? String(step.look_for).trim() : "",
      pass_criteria: step?.pass_criteria != null ? String(step.pass_criteria).trim() : "",
      fail_criteria: step?.fail_criteria != null ? String(step.fail_criteria).trim() : "",
    },
  };
}

async function handleAddInspectionStep(payload) {
  const step = payload?.step;
  const complete = typeof payload?.complete === "function" ? payload.complete : null;
  const accountId = getAccountId();
  if (!accountId || props.responsibilityId == null || checkpointId.value == null) {
    complete?.(new Error("Missing checkpoint context"));
    return;
  }

  const body = buildAddInspectionStepApiPayload(step);
  const s = body.step;
  if (!s.inspector_action || !s.look_for || !s.pass_criteria || !s.fail_criteria) {
    notificationStore.error("Inspect, look for, pass, and fail fields are required.");
    complete?.(new Error("validation"));
    return;
  }

  proxy?.$loader?.setAppLoading?.(true);
  try {
    await addCheckpointInspectionStep(accountId, props.responsibilityId, checkpointId.value, body);
    // ER-11777: the new step lands in proposed_inspection_protocol (not yet
    // published). Switch an active checkpoint to "Proposed Changes" immediately so
    // displayCheckpoint keeps reading proposed_inspection_protocol instead of
    // substituting active_* content and hiding the freshly-added step.
    if (isActiveCheckpoint.value) {
      editBarSelectedTab.value = "proposed-changes";
    }
    await emitCheckpointMutationResult();
    complete?.(null);
  } catch (err) {
    console.error("[CheckpointEditFormV2] add inspection step:", err);
    notificationStore.error(
      err?.response?.data?.detail ??
        err?.response?.data?.message ??
        "Could not add the inspection step."
    );
    complete?.(err);
  } finally {
    proxy?.$loader?.setAppLoading?.(false);
  }
}

async function handleUpdateInspectionStep(payload) {
  const index = payload?.index;
  const step = payload?.step;
  const complete = typeof payload?.complete === "function" ? payload.complete : null;
  if (index == null || index < 0 || !step) {
    complete?.(new Error("Invalid payload"));
    return;
  }
  const accountId = getAccountId();
  if (!accountId || props.responsibilityId == null || checkpointId.value == null) {
    notificationStore.error("Missing account or checkpoint context.");
    complete?.(new Error("Missing checkpoint context"));
    return;
  }

  const body = buildAddInspectionStepApiPayload(step);
  const s = body.step;
  if (!s.inspector_action || !s.look_for || !s.pass_criteria || !s.fail_criteria) {
    notificationStore.error("Inspect, look for, pass, and fail fields are required.");
    complete?.(new Error("validation"));
    return;
  }

  const existing = proposedInspectionSteps.value;
  if (index >= existing.length) {
    complete?.(new Error("Invalid payload"));
    return;
  }
  const prev = existing[index] || {};
  const stepId = prev.step_sequence ?? index + 1;

  proxy?.$loader?.setAppLoading?.(true);
  try {
    await changeCheckpointInspectionStep(
      accountId,
      props.responsibilityId,
      checkpointId.value,
      stepId,
      body
    );
    await emitCheckpointMutationResult();
    complete?.(null);
  } catch (err) {
    console.error("[CheckpointEditFormV2] update inspection step:", err);
    notificationStore.error(
      err?.response?.data?.detail ??
        err?.response?.data?.message ??
        "Could not update the inspection step."
    );
    complete?.(err);
  } finally {
    proxy?.$loader?.setAppLoading?.(false);
  }
}

async function handleDeleteInspectionStep(payload) {
  const index = payload?.index;
  const complete = typeof payload?.complete === "function" ? payload.complete : null;
  if (index == null || index < 0) {
    complete?.(new Error("Invalid payload"));
    return;
  }
  const accountId = getAccountId();
  if (!accountId || props.responsibilityId == null || checkpointId.value == null) {
    notificationStore.error("Missing account or checkpoint context.");
    complete?.(new Error("Missing checkpoint context"));
    return;
  }

  const existing = proposedInspectionSteps.value;
  if (index >= existing.length) {
    complete?.(new Error("Invalid payload"));
    return;
  }
  const stepId = existing[index]?.step_sequence ?? index + 1;

  proxy?.$loader?.setAppLoading?.(true);
  try {
    await deleteCheckpointInspectionStep(
      accountId,
      props.responsibilityId,
      checkpointId.value,
      stepId
    );
    await emitCheckpointMutationResult();
    complete?.(null);
  } catch (err) {
    console.error("[CheckpointEditFormV2] delete inspection step:", err);
    notificationStore.error(
      err?.response?.data?.detail ??
        err?.response?.data?.message ??
        "Could not delete the inspection step."
    );
    complete?.(err);
  } finally {
    proxy?.$loader?.setAppLoading?.(false);
  }
}

async function handleDeleteInspectionProtocolRequested(payload) {
  const complete = typeof payload?.complete === "function" ? payload.complete : null;
  const accountId = getAccountId();
  if (!accountId || props.responsibilityId == null || checkpointId.value == null) {
    notificationStore.error("Missing checkpoint context.");
    complete?.(new Error("Missing checkpoint context"));
    return;
  }

  try {
    await deleteCheckpointProtocol(accountId, props.responsibilityId, checkpointId.value);
    void notifyPeersOfCheckpointUpdate();
    emit("refresh-checkpoint");
    complete?.(null);
  } catch (err) {
    console.error("[CheckpointEditFormV2] delete protocol:", err);
    notificationStore.error(
      err?.response?.data?.detail ??
        err?.response?.data?.message ??
        "Could not delete the inspection protocol."
    );
    complete?.(err);
  }
}

function mapAddQuestionPayload(payload) {
  const questionText = payload?.question != null ? String(payload.question).trim() : "";
  const answers = Array.isArray(payload?.answers) ? payload.answers : [];
  const qAnswers = [];
  let aNo = 1;
  for (const item of answers) {
    const aText = item?.answer != null ? String(item.answer).trim() : "";
    if (!aText) continue;
    const isCorrect = item?.checked === true ? 1 : 0;
    const correctDescription =
      item?.correct_description != null ? String(item.correct_description).trim() : "";
    qAnswers.push({
      a_no: aNo,
      a_text: aText,
      isCorrect,
      correct_description: isCorrect === 1 ? correctDescription : "",
    });
    aNo += 1;
  }

  return {
    question: {
      q_text: questionText,
      q_answers: qAnswers,
    },
  };
}

function editorQuizRowToApiQuestion(row, sequentialQNo) {
  const q_text = String(row?.q_text ?? row?.text ?? row?.question ?? "").trim();
  const rawAnswers = Array.isArray(row?.q_answers)
    ? row.q_answers
    : Array.isArray(row?.answers)
      ? row.answers
      : [];
  const q_answers = [];
  let aNo = 1;
  for (const a of rawAnswers) {
    const a_text = String(a?.a_text ?? a?.text ?? a?.answer ?? "").trim();
    if (!a_text) continue;
    const isCorrectFlag =
      a?.isCorrect === 1 ||
      a?.isCorrect === true ||
      a?.is_correct === true ||
      Number(a?.isCorrect) === 1;
    const isCorrect = isCorrectFlag ? 1 : 0;
    const correct_description = isCorrect === 1 ? String(a?.correct_description ?? "").trim() : "";
    q_answers.push({
      a_no: aNo,
      a_text,
      isCorrect,
      correct_description: isCorrect === 1 ? correct_description : "",
    });
    aNo += 1;
  }
  return {
    q_no: sequentialQNo,
    q_text,
    q_answers,
  };
}

function buildCheckpointCriteriaForQuizSuggest() {
  return `${prefixDisplay.value} ${props.checkpoint?.criteria || ""}`.trim();
}

function resolveSuggestQuizMicrolearningContent() {
  if (isVideo.value) {
    return buildVideoMicrolearningContentForSuggest(videoPassMicrolearningPayload.value);
  }
  if (isArticle.value) {
    return buildArticleMicrolearningContentForSuggest({
      articleHtml: proposedArticleHtmlForEditor.value,
      articleBody: proposedArticleBodyForEditor.value,
    });
  }
  return "";
}

/**
 * ER-12337 — quiz/inspection suggestion runners live in iqCheckpointSuggestStore, keyed by
 * checkpoint id, so a stream started here keeps running after the editor unmounts.
 */
function buildSuggestContext() {
  return {
    checkpointId: checkpointId.value,
    responsibilityId: props.responsibilityId,
    accountId: getAccountId(),
    isVideo: isVideo.value,
    checkpointCriteria: buildCheckpointCriteriaForQuizSuggest(),
    microlearningContent: resolveSuggestQuizMicrolearningContent(),
    remainingSlots: MAX_CHECKPOINT_QUIZ_QUESTIONS,
    existingQuestions: [],
    existingSteps: [],
    existingInspectionSteps: [...proposedInspectionSteps.value],
    hasExistingQuiz: proposedQuizQuestions.value.length > 0,
    previousQuestions: quizUndoEnabled.value
      ? proposedQuizQuestions.value.map((row, i) => editorQuizRowToApiQuestion(row, i + 1))
      : [],
    isActiveCheckpoint: isActiveCheckpoint.value,
  };
}

function handleSuggestQuiz() {
  if (criteriaReadonly.value || isQuizSuggesting.value) return;
  if (resolvedQuizMode.value === "SKIP_QUIZ") return;

  const microlearningContent = resolveSuggestQuizMicrolearningContent();
  if (!microlearningContent.trim()) {
    notificationStore.error(
      isVideo.value
        ? "Microlearning content is required before quiz questions can be suggested."
        : "Article content is required before quiz questions can be suggested."
    );
    return;
  }

  const checkpointCriteria = buildCheckpointCriteriaForQuizSuggest();
  if (!checkpointCriteria) {
    notificationStore.error(
      "Checkpoint criteria is required before quiz questions can be suggested."
    );
    return;
  }

  iqCheckpointSuggestStore.startQuiz(buildSuggestContext());
}

function handleQuizSuggestRetry() {
  iqCheckpointSuggestStore.retryQuiz(buildSuggestContext());
}

const quizUndoBusy = ref(false);
const quizUndoVisible = computed(
  () =>
    quizUndoEnabled.value &&
    !criteriaReadonly.value &&
    !quizSuggestBannerState.value &&
    !videoQuizGenerationStatus.value &&
    !!iqCheckpointSuggestStore.getQuizUndoSnapshot(checkpointId.value)
);

function handleDismissQuizUndo() {
  iqCheckpointSuggestStore.clearQuizUndoSnapshot(checkpointId.value);
}

async function handleUndoQuizSuggestion() {
  if (quizUndoBusy.value) return;
  const accountId = getAccountId();
  if (!accountId || props.responsibilityId == null || checkpointId.value == null) {
    notificationStore.error("Missing checkpoint context.");
    return;
  }
  quizUndoBusy.value = true;
  try {
    const restored = await iqCheckpointSuggestStore.undoQuizSuggestion({
      accountId,
      responsibilityId: props.responsibilityId,
      checkpointId: checkpointId.value,
    });
    if (restored) {
      await emitCheckpointMutationResult();
      notificationStore.success("Previous quiz questions restored.");
    }
  } catch (err) {
    console.error("[CheckpointEditFormV2] undo suggested quiz questions:", err);
    notificationStore.error(
      err?.response?.data?.detail ??
        err?.response?.data?.message ??
        "Could not restore the previous quiz questions."
    );
  } finally {
    quizUndoBusy.value = false;
  }
}

// Leaving the editor forfeits undo; the nested read-only past-version form must not drop it.
onUnmounted(() => {
  if (!props.pastVersionView) iqCheckpointSuggestStore.clearQuizUndoSnapshot(checkpointId.value);
});

function handleSuggestInspection() {
  if (criteriaReadonly.value || isInspectionSuggesting.value) return;
  if (resolvedQuizMode.value === "SKIP_INSPECTION") return;

  const microlearningContent = resolveSuggestQuizMicrolearningContent();
  if (!microlearningContent.trim()) {
    notificationStore.error(
      isVideo.value
        ? "Microlearning content is required before inspection steps can be suggested."
        : "Article content is required before inspection steps can be suggested."
    );
    return;
  }

  const checkpointCriteria = buildCheckpointCriteriaForQuizSuggest();
  if (!checkpointCriteria) {
    notificationStore.error(
      "Checkpoint criteria is required before inspection steps can be suggested."
    );
    return;
  }

  iqCheckpointSuggestStore.startInspection(buildSuggestContext());
}

function handleInspectionSuggestRetry() {
  iqCheckpointSuggestStore.retryInspection(buildSuggestContext());
}

/**
 * ER-12337 — a suggestion may finish (and persist) while the editor was unmounted. The
 * mounted editor performs the single checkpoint refresh / tab switch once, on return.
 */
watch(
  () => iqCheckpointSuggestStore.getQuizRun(checkpointId.value)?.refreshPending,
  (pending) => {
    if (!pending) return;
    void emitCheckpointMutationResult();
    iqCheckpointSuggestStore.acknowledgeRefresh("quiz", checkpointId.value);
  },
  { immediate: true }
);

watch(
  () => iqCheckpointSuggestStore.getInspectionRun(checkpointId.value)?.refreshPending,
  (pending) => {
    if (!pending) return;
    const run = iqCheckpointSuggestStore.getInspectionRun(checkpointId.value);
    // ER-11777: keep freshly suggested steps visible on an active checkpoint.
    if (run?.switchToProposedTab && isActiveCheckpoint.value) {
      editBarSelectedTab.value = "proposed-changes";
    }
    void emitCheckpointMutationResult();
    iqCheckpointSuggestStore.acknowledgeRefresh("inspection", checkpointId.value);
  },
  { immediate: true }
);


async function handleReorderQuestions({ questions: rows }) {
  if (!Array.isArray(rows) || rows.length === 0) return;
  const accountId = getAccountId();
  if (!accountId || props.responsibilityId == null || checkpointId.value == null) {
    notificationStore.error("Missing checkpoint context.");
    return;
  }

  const apiQuestions = rows.map((row, i) => editorQuizRowToApiQuestion(row, i + 1));
  const hasInvalid = apiQuestions.some(
    (q) => !q.q_text || !Array.isArray(q.q_answers) || !q.q_answers.length
  );
  if (hasInvalid) {
    notificationStore.error("Could not save quiz order: invalid question data.");
    return;
  }

  proxy?.$loader?.setAppLoading?.(true);
  try {
    for (let i = 0; i < apiQuestions.length; i += 1) {
      const targetQNo = i + 1;
      const q = apiQuestions[i];
      await changeCheckpointQuestion(
        accountId,
        props.responsibilityId,
        checkpointId.value,
        targetQNo,
        {
          question: {
            q_text: q.q_text,
            q_answers: q.q_answers,
          },
        }
      );
    }
    emitCheckpointMutationResult();
  } catch (err) {
    console.error("[CheckpointEditFormV2] reorder quiz questions:", err);
    notificationStore.error(
      err?.response?.data?.detail ?? err?.response?.data?.message ?? "Could not save quiz order."
    );
  } finally {
    proxy?.$loader?.setAppLoading?.(false);
  }
}

async function handleAddQuestion(payload) {
  const complete = typeof payload?.complete === "function" ? payload.complete : null;
  const accountId = getAccountId();
  if (!accountId || props.responsibilityId == null || checkpointId.value == null) {
    complete?.(new Error("Missing checkpoint context"));
    return;
  }

  const requestPayload = mapAddQuestionPayload(payload);
  const q = requestPayload.question;
  const answersList = Array.isArray(q?.q_answers) ? q.q_answers : [];
  const hasCorrect = answersList.some((a) => Number(a.isCorrect) === 1);
  if (
    !q?.q_text ||
    answersList.length < MIN_QUIZ_ANSWERS ||
    answersList.length > MAX_QUIZ_ANSWERS ||
    !hasCorrect
  ) {
    notificationStore.error(QUIZ_QUESTION_INVALID_MESSAGE);
    complete?.(new Error("validation"));
    return;
  }

  proxy?.$loader?.setAppLoading?.(true);
  try {
    await addCheckpointQuestion(
      accountId,
      props.responsibilityId,
      checkpointId.value,
      requestPayload
    );
    emitCheckpointMutationResult();
    complete?.(null);
  } catch (err) {
    console.error("[CheckpointEditFormV2] add question:", err);
    notificationStore.error(
      err?.response?.data?.detail ?? err?.response?.data?.message ?? "Could not add the question."
    );
    complete?.(err);
  } finally {
    proxy?.$loader?.setAppLoading?.(false);
  }
}

async function handleUpdateQuestion(payload) {
  const complete = typeof payload?.complete === "function" ? payload.complete : null;
  const accountId = getAccountId();
  if (!accountId || props.responsibilityId == null || checkpointId.value == null) {
    notificationStore.error("Missing checkpoint context.");
    complete?.(new Error("Missing checkpoint context"));
    return;
  }

  const qNo = payload?.q_no;
  if (qNo == null || qNo === "") {
    notificationStore.error("Missing question number. Try refreshing the page.");
    complete?.(new Error("missing q_no"));
    return;
  }

  const requestPayload = mapAddQuestionPayload(payload);
  const q = requestPayload.question;
  const answersList = Array.isArray(q?.q_answers) ? q.q_answers : [];
  const hasCorrect = answersList.some((a) => Number(a.isCorrect) === 1);
  if (
    !q?.q_text ||
    answersList.length < MIN_QUIZ_ANSWERS ||
    answersList.length > MAX_QUIZ_ANSWERS ||
    !hasCorrect
  ) {
    notificationStore.error(QUIZ_QUESTION_INVALID_MESSAGE);
    complete?.(new Error("validation"));
    return;
  }

  proxy?.$loader?.setAppLoading?.(true);
  try {
    await changeCheckpointQuestion(
      accountId,
      props.responsibilityId,
      checkpointId.value,
      qNo,
      requestPayload
    );
    void notifyPeersOfCheckpointUpdate();
    handleRefreshCheckpoint();
    complete?.(null);
  } catch (err) {
    console.error("[CheckpointEditFormV2] update question:", err);
    notificationStore.error(
      err?.response?.data?.detail ??
        err?.response?.data?.message ??
        "Could not update the question."
    );
    complete?.(err);
  } finally {
    proxy?.$loader?.setAppLoading?.(false);
  }
}

async function handleDeleteQuestion(payload) {
  const complete = typeof payload?.complete === "function" ? payload.complete : null;
  const accountId = getAccountId();
  if (!accountId || props.responsibilityId == null || checkpointId.value == null) {
    notificationStore.error("Missing checkpoint context.");
    complete?.(new Error("Missing checkpoint context"));
    return;
  }

  const qNo = payload?.q_no;
  if (qNo == null || qNo === "") {
    notificationStore.error(
      "This question cannot be removed from the server. Try refreshing the page."
    );
    complete?.(new Error("missing q_no"));
    return;
  }

  try {
    await deleteCheckpointQuestion(accountId, props.responsibilityId, checkpointId.value, qNo);
    void notifyPeersOfCheckpointUpdate();
    handleRefreshCheckpoint();
    complete?.(null);
  } catch (err) {
    console.error("[CheckpointEditFormV2] delete question:", err);
    notificationStore.error(
      err?.response?.data?.detail ??
        err?.response?.data?.message ??
        "Could not delete the question."
    );
    complete?.(err);
  }
}

function handleQuizBulkMenuAction(payload) {
  if (payload?.action === "delete-quiz") {
    showDeleteEntireQuizDialog.value = true;
  }
}

async function onConfirmDeleteEntireQuizDialog() {
  if (deleteEntireQuizSubmitting.value) return;
  const accountId = getAccountId();
  if (!accountId || props.responsibilityId == null || checkpointId.value == null) {
    notificationStore.error("Missing checkpoint context.");
    return;
  }

  deleteEntireQuizSubmitting.value = true;
  try {
    await changeCheckpointV2(accountId, props.responsibilityId, checkpointId.value, {
      proposed_quiz: null,
      proposed_quiz_is_autopass: false,
    });
    emitCheckpointMutationResult();
    showDeleteEntireQuizDialog.value = false;
  } catch (err) {
    console.error("[CheckpointEditFormV2] clear proposed quiz:", err);
    notificationStore.error(
      err?.response?.data?.detail ?? err?.response?.data?.message ?? "Could not delete the quiz."
    );
  } finally {
    deleteEntireQuizSubmitting.value = false;
  }
}

const readinessLevel = computed(
  () => LEVEL_BY_NAME[props.checkpoint?.level] || LEVEL_BY_NAME.Apprentice
);

const checkpointStatus = computed(() => props.checkpoint?.status || "draft");

const lockState = computed(() => resolveLockState(props.checkpoint));

/** ER-12337 — surface a store-run's terminal state when it resolves (possibly while unmounted). */
watch(
  () => {
    const run = iqVideoMicrolearningStore.getRun(checkpointId.value);
    return run ? `${run.status}:${run.outcome}` : "";
  },
  (signature) => {
    if (!signature) return;
    const run = iqVideoMicrolearningStore.getRun(checkpointId.value);
    if (!run) return;
    if (run.status === "done") {
      contextValidationMismatchOverride.value = false;
      contextValidationError.value = "";
      videoUploadFlowPhase.value = null;
      emit("refresh-checkpoint");
    } else if (run.status === "failed" && run.outcome === "mismatch") {
      contextValidationError.value = run.error || "";
      contextValidationMismatchOverride.value = true;
      videoUploadFlowPhase.value = "mismatch";
      emit("refresh-checkpoint");
    }
  },
  { immediate: true }
);

/** ER-12337 — losing the edit lock (stale / stolen / released) cancels in-flight runs. */
watch(lockState, (next, prev) => {
  if (prev === "lockedBySelf" && next !== "lockedBySelf") {
    iqVideoMicrolearningStore.abortRun(checkpointId.value);
    // Runs outlive the editor; a lock loss must stop them before they persist against a
    // checkpoint this operator no longer owns.
    iqCheckpointSuggestStore.abortAllForCheckpoint(checkpointId.value);
    iqVideoProcessingStore.abort(checkpointId.value);
  }
});

/**
 * Active published content, RETIRED, and ARCHIVED default views are always read-only.
 * Edit only on draft, or on the Proposed Changes tab while locked by self.
 */
const canEditCheckpointContent = computed(() => {
  if (props.pastVersionView) return false;
  if (isRetiredCheckpoint.value || isArchivedCheckpoint.value) return false;
  if (lockState.value !== "lockedBySelf") return false;
  if (isActiveCheckpoint.value && !isViewingProposedChanges.value) return false;
  return true;
});

/** Edit variant (Add Microlearning) when content is editable; otherwise read-only. */
const noLearningNeededBannerMode = computed(() =>
  canEditCheckpointContent.value ? "edit" : "read-only"
);

const showLockBanner = computed(
  () => !isRetiredCheckpoint.value && !isArchivedCheckpoint.value && lockState.value !== "unlocked"
);

const lockBannerVariant = computed(() => {
  if (lockState.value === "lockedBySelf") return "by-you";
  // Prefer live SSE editLock when it includes is_stale; else checkpoint prop.
  const isStale = Boolean(
    editLock.value != null && "is_stale" in editLock.value
      ? editLock.value.is_stale
      : props.checkpoint?.edit_lock_is_stale
  );
  if (lockState.value === "lockedByOther" && isStale) {
    return "other-stepped-away";
  }
  return "by-other";
});

const lockEditorName = computed(() => formatLockEditorName(getLockHolder(props.checkpoint)));

/** Encoding lock copy on LockCheckpointBanner while video stream is processing. */
const showVideoProcessingLockBanner = computed(
  () => isVideoProcessing.value && lockState.value === "lockedBySelf"
);

/** Editable only for draft / Proposed Changes while locked by self — never Active published. */
const criteriaReadonly = computed(() => !canEditCheckpointContent.value);

/**
 * Short instructions to employee (Optional) — draft.employee_prep_note.
 * Inspector Only (Shape 4) always renders the field; content-bearing shapes only while a note exists.
 */
const employeePrepNote = computed(() => props.checkpoint?.employee_prep_note ?? "");
const hasEmployeePrepNote = computed(() => Boolean(String(employeePrepNote.value).trim()));

/** Read-only surfaces (Active published, historical, RETIRED / ARCHIVED) render the note as prose. */
const isEmployeePrepNoteProseSurface = computed(() => {
  if (props.pastVersionView) return true;
  if (isRetiredCheckpoint.value || isArchivedCheckpoint.value) return true;
  if (isActiveCheckpoint.value && !isViewingProposedChanges.value) return true;
  return false;
});

/** Editable only while the operator holds the edit lock — same as every other draft field. */
const employeePrepNoteReadonly = computed(() => !canEditCheckpointContent.value);

function handleEmployeePrepNoteUpdate(value) {
  emit("update-employee-prep-note", String(value ?? ""));
}

/**
 * Active + proposed_* → Active + Proposed Changes tabs.
 * Active only → Propose Changes bar.
 * Retired → Retired + Reactivate bar (content remains read-only).
 * Archived → Archived + Unarchive bar (content remains read-only).
 * Otherwise draft.
 */
const editBarCheckpointState = computed(() => {
  const status = String(props.checkpoint?.status ?? "draft").toLowerCase();
  if (status === "archived") return "archived";
  if (status === "retired") return "retired";
  if (status === "active") {
    return hasProposedChanges.value ? "active-with-changes" : "active";
  }
  return "draft";
});

/** ER-11249 / ER-11208: keep the edit-bar V{n} pill + history tooltip in sync. */
watch(
  () => [checkpointId.value, editBarCheckpointState.value],
  () => {
    void loadCurrentVersionMeta();
  },
  { immediate: true }
);

/** Article/video asset present on the proposed draft (mirrors editor visibility). */
const hasProposedMicrolearningAssetForPublish = computed(() => {
  const ckpt = displayCheckpoint.value;
  if (!ckpt) return false;
  if (isArticle.value) {
    return (
      hasMeaningfulProposedArticle(ckpt.proposed_article) ||
      hasValue(ckpt.proposed_knowledge_article_html)
    );
  }
  if (isVideo.value) {
    return hasProposedVideoAsset(ckpt);
  }
  return false;
});

/**
 * Whether Publish is disabled for an active change proposal.
 * Inspector-only and task-an-employee can publish immediately.
 * Video/article need a non-empty asset plus quiz/inspection per resolvedQuizMode.
 */
const isProposedPublishDisabled = computed(() => {
  if (!props.checkpoint || !hasProposedChanges.value) return true;
  if (isInspectorOnly.value || isTaskAnEmployee.value) return false;
  if (!isArticle.value && !isVideo.value) return true;
  if (!hasProposedMicrolearningAssetForPublish.value) return true;
  const mode = resolvedQuizMode.value;
  if (mode !== "SKIP_QUIZ" && !hasProposedQuiz.value) return true;
  if (mode !== "SKIP_INSPECTION" && !hasProposedInspection.value) return true;
  return false;
});

/**
 * ER-11860: a pending upload_microlearning_video task exists while `assigned_to` is
 * populated (the same signal driving PendingTaskToUploadVideoBanner). Used to hide the
 * "Lock to Edit" secondary action and to gate the disabled-Activate tooltip.
 */
const hasPendingUploadTask = computed(() => hasPendingUploadTaskForCheckpoint(props.checkpoint));

/**
 * Retired → Reactivate (content is never editable).
 * Archived → Unarchive (content is never editable).
 * Locked by self + dirty criteria/prefix → draft Save Changes / Discard changes.
 * Article locked by self (clean) → Save + Exit Edit.
 * Other types locked by self (clean) → Exit Edit only.
 * Locked by self on Active tab → Exit Edit only (Active is never editable).
 * Locked by other → default bar (no lock/activate actions).
 * Active with proposal, viewing proposed, unlocked → Publish / Edit / Discard.
 * Active with proposal, viewing Active tab → no action buttons.
 * Active (no proposal) → Propose Changes.
 * Unlocked draft → Activate (+ Lock to Edit secondary).
 */
const editBarAction = computed(() => {
  if (editBarCheckpointState.value === "archived") return "unarchive";
  if (editBarCheckpointState.value === "retired") return "reactivate";
  if (lockState.value === "lockedBySelf") {
    // Active published side is view-only; Exit Edit releases the lock.
    if (isActiveCheckpoint.value && !isViewingProposedChanges.value) {
      return "exit-edit";
    }
    if (isCriteriaDirty.value) return "save-changes";
    return isArticle.value ? "save-and-exit-edit" : "exit-edit";
  }
  if (lockState.value === "lockedByOther") return "no-action";
  if (editBarCheckpointState.value === "active-with-changes") {
    if (editBarSelectedTab.value === "primary") return "no-action";
    return isProposedPublishDisabled.value ? "cannot-publish" : "can-publish";
  }
  if (editBarCheckpointState.value === "active") return "propose-changes";
  return isCheckpointActivateDisabled(props.checkpoint) ? "activate-disabled" : "activate";
});

const prefixDisplay = computed(() => formatPrefixDisplay(props.checkpoint?.prefix));

const pendingAssignedToFirstName = computed(
  () =>
    props.checkpoint?.assigned_to?.firstName ||
    splitDisplayName(props.checkpoint?.assigned_to?.name).first ||
    props.checkpoint?.assigned_to?.first_name ||
    ""
);
const pendingAssignedToLastName = computed(
  () =>
    props.checkpoint?.assigned_to?.lastName ||
    splitDisplayName(props.checkpoint?.assigned_to?.name).last ||
    props.checkpoint?.assigned_to?.last_name ||
    ""
);
const pendingAssignedToAvatar = computed(() => props.checkpoint?.assigned_to?.avatar ?? null);

const pendingAssignedByFirstName = computed(
  () =>
    props.checkpoint?.assigned_by?.firstName ||
    splitDisplayName(props.checkpoint?.assigned_by?.name).first ||
    props.checkpoint?.assigned_by?.first_name ||
    ""
);
const pendingAssignedByLastName = computed(
  () =>
    props.checkpoint?.assigned_by?.lastName ||
    splitDisplayName(props.checkpoint?.assigned_by?.name).last ||
    props.checkpoint?.assigned_by?.last_name ||
    ""
);
const pendingAssignedByAvatar = computed(() => props.checkpoint?.assigned_by?.avatar ?? null);

const pendingAssignedDate = computed(
  () => props.checkpoint?.assigned_on ?? props.checkpoint?.assigned_date ?? null
);
const pendingDueDate = computed(() => props.checkpoint?.deadline ?? null);
</script>

<style scoped>
.checkpoint-edit-form-v2 {
  display: flex;
  flex-direction: column;
  padding: 0 17px 24px 17px;
  gap: 16px;
  width: 100%;
}

/* Full-bleed edit bar strip (Figma utility-gray-50) within form horizontal padding. */
.checkpoint-edit-form-v2__edit-bar {
  box-sizing: border-box;
  width: calc(100% + 34px);
  margin-left: -17px;
  margin-right: -17px;
  padding-left: 17px;
  padding-right: 17px;
}

.checkpoint-edit-form-v2__inspections {
  width: 100%;
  min-width: 0;
}

.checkpoint-edit-form-v2__article {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 12px;
}

.checkpoint-edit-form-v2__video-processing {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 160px;
  margin-top: 8px;
  padding: 24px 16px;
  box-sizing: border-box;
  background-color: #f9fafb;
  border: 1px dashed #d0d5dd;
  border-radius: 8px;
}

.checkpoint-edit-form-v2__video-processing-loader-wrap {
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.checkpoint-edit-form-v2__video-processing-loader {
  width: 56px;
  height: 56px;
  display: block;
  flex-shrink: 0;
}

.checkpoint-edit-form-v2__video-processing-text {
  margin: 0;
  font-family: Inter, sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #344054;
  text-align: center;
}

.checkpoint-edit-form-v2__video {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 12px;
}

.checkpoint-edit-form-v2__video-bar {
  width: 100%;
}

.checkpoint-edit-form-v2__video-card {
  width: 100%;
  margin-top: 4px;
}

.checkpoint-edit-form-v2__quiz-mode {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.checkpoint-edit-form-v2__quiz-mode-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.checkpoint-edit-form-v2__quiz-mode-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-left: auto;
  flex-shrink: 0;
}

.checkpoint-edit-form-v2__disabled-btn-tooltip-wrap {
  display: inline-flex;
}

.checkpoint-edit-form-v2__skip-banner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 8px;
  box-sizing: border-box;
}

.checkpoint-edit-form-v2__skip-banner--both {
  background: #f9fafb;
  align-items: center;
}

.checkpoint-edit-form-v2__skip-banner--inspection {
  background: #f4f3ff;
}

.checkpoint-edit-form-v2__skip-banner--quiz {
  background: #f9fafb;
}

.checkpoint-edit-form-v2__skip-banner-icon {
  display: inline-flex;
  flex-shrink: 0;
  margin-top: 2px;
}

.checkpoint-edit-form-v2__skip-banner-copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.checkpoint-edit-form-v2__skip-banner-title {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.checkpoint-edit-form-v2__skip-banner--inspection .checkpoint-edit-form-v2__skip-banner-title {
  color: #5925dc;
}

.checkpoint-edit-form-v2__skip-banner--quiz .checkpoint-edit-form-v2__skip-banner-title {
  color: #344054;
}

.checkpoint-edit-form-v2__skip-banner-text {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.checkpoint-edit-form-v2__skip-banner--both .checkpoint-edit-form-v2__skip-banner-text {
  color: #475467;
}

.checkpoint-edit-form-v2__skip-banner--inspection .checkpoint-edit-form-v2__skip-banner-text {
  color: #6938ef;
}

.checkpoint-edit-form-v2__skip-banner--quiz .checkpoint-edit-form-v2__skip-banner-text {
  color: #667085;
}

.checkpoint-edit-form-v2__article-quiz,
.checkpoint-edit-form-v2__article-inspection {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.checkpoint-edit-form-v2__skipped-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin: 0 auto;
  padding: 32px 8px 24px;
  max-width: 640px;
  text-align: center;
}

.checkpoint-edit-form-v2__skipped-empty-title {
  margin: 0;
  color: #101828;
  font-family: Inter, sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
}

.checkpoint-edit-form-v2__skipped-empty-text {
  margin: 0;
  color: #475467;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.checkpoint-edit-form-v2__iq-thinking-banner {
  width: 100%;
}

/* Short instructions to employee — sits below the Inspector Only / pending-task banner
   (the video/article branches already space it via their column gap). */
.checkpoint-edit-form-v2__prep-note {
  width: 100%;
}

.checkpoint-edit-form-v2__video-view-edit {
  width: 100%;
  margin-top: 8px;
}

.checkpoint-edit-form-v2__hidden-iq-runner {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
  pointer-events: none;
}

.checkpoint-edit-form-v2__video-dialog-card {
  border-radius: 12px;
  overflow: hidden;
}

.checkpoint-edit-form-v2__video-dialog-title {
  background-color: #f9fafb;
  padding: 16px 24px;
  border-bottom: 1px solid #eaecf0;
}

.checkpoint-edit-form-v2__video-dialog-title-text {
  font-family: Inter, sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: #344054;
}

.checkpoint-edit-form-v2__video-dialog-close {
  color: #667085;
}

.checkpoint-edit-form-v2__video-dialog-content {
  padding: 24px;
  background-color: #ffffff;
}

.checkpoint-edit-form-v2__video-dialog-iframe-wrap {
  width: 100%;
  height: 400px;
  border-radius: 8px;
  overflow: hidden;
}

.checkpoint-edit-form-v2__video-dialog-iframe {
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 8px;
}

.checkpoint-edit-form-v2__video-dialog-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  color: #667085;
  font-family: Inter, sans-serif;
  font-size: 16px;
  gap: 16px;
}

.checkpoint-edit-form-v2__video-dialog-empty p {
  margin: 0;
  font-weight: 500;
}
</style>
